
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      // Actions (root level for easy access across all components)
      actions: {
        add: "Add",
        edit: "Edit",
        delete: "Delete",
        remove: "Remove",
        cancel: "Cancel",
        confirm: "Confirm",
        save: "Save",
        saving: "Saving..."
      },
      // Validation (root level for easy access across all components)
      validation: {
        required: "{{field}} is required",
        nameRequired: "Name is required",
        emailRequired: "Email is required",
        email: "Please enter a valid email address",
        invalidEmail: "Please enter a valid email address",
        url: "Please enter a valid URL",
        invalidUrl: "Please enter a valid URL",
        invalidUrlField: "{{field}} must be a valid URL (e.g., https://example.com)",
        phone: "Please enter a valid phone number",
        minLength: "Minimum {min} characters required",
        maxLength: "Maximum {max} characters allowed",
        pleaseFixErrors: "Please fix the errors:",
        correctFollowingErrors: "Please correct the following errors:",
        andMoreErrors: "+{{count}} more error(s)",
        englishOnly: "Please use English characters only",
        englishOnlyField: "{{field}} must be in English only",
        bilingualField: "{{field}} must be in English or Arabic only",
        invalidCharacters: "Contains invalid characters",
        invalidCharsField: "{{field}} contains invalid characters",
        noFutureDate: "{{field}} cannot be in the future",
        futureDateField: "{{field}} cannot be a future date",
        dateRange: "Start date must be before end date",
        startBeforeEnd: "Start date must be before end date",
        linkRequired: "Both link name and URL are required"
      },
      // Navigation
      nav: {
        home: "Home",
        cvBuilder: "CV Builder",
        jobSearch: "Job Search",
        remoteJobs: "Remote Jobs",
        aiAssistant: "AI Assistant",
        services: "Services",
        about: "About",
        contact: "Contact",
        login: "Sign In",
        register: "Get Started",
        logout: "Sign Out",
        profile: "Profile",
        myCV: "My CVs",
        settings: "Settings"
      },
      // Dashboard
      dashboard: {
        welcome: "Welcome back",
        subtitle: "Here's what's happening with your job search today",
        errorLoading: "Failed to load dashboard data",
        loading: "Loading dashboard...",
        loadingJobs: "Loading jobs...",
        totalResumes: "Total Resumes",
        savedJobs: "Saved Jobs",
        applications: "Applications",
        jobsApplied: "Job Applications Submitted",
        jobSearches: "Job Searches",
        activityInsights: "Your Activity & Insights",
        activityInsightsDesc: "Track your progress and performance",
        momentumTitle: "Weekly momentum",
        momentumDesc: "Combined score of your recent activity",
        focusTitle: "Next best actions",
        focusDesc: "Quick wins to keep your search moving",
        timelineTitle: "Recent timeline",
        timelineEmpty: "No activity yet — start exploring tools to see insights here",
        resumeUpdated: "CV updated",
        jobSaved: "Job saved",
        searchTriggered: "Job search completed",
        aiReady: "AI enhancement ready",
        createFirstInsight: "Create your first resume to unlock insights",
        focusEnhance: "Enhance your best CV with AI",
        focusApply: "Submit two targeted applications",
        focusSearch: "Refresh your job searches",
        momentumHigh: "Great pace! Keep the streak alive.",
        thisMonth: "This Month",
        newApplications: "new applications",
        recentActivity: "Recent Activity",
        last7Days: "Last 7 days",
        profileStrength: "Profile Strength",
        complete: "complete",
        quickActions: "Quick Actions",
        createResume: "Create Resume",
        searchJobs: "Search Jobs",
        aiEnhancement: "AI Enhancement",
        remoteJobs: "Remote Jobs",
        recentResumes: "Recent Resumes",
        savedJobsList: "Saved Jobs",
        remoteJobsTitle: "Explore Remote Opportunities",
        remoteJobsDesc: "Work from anywhere in the world",
        exploreRemote: "Explore All Remote Jobs",
        viewAll: "View All",
        noResumes: "No resumes yet",
        createFirst: "Create your first resume",
        noJobs: "No saved jobs yet",
        noRemoteJobs: "No remote jobs available",
        startSearching: "Start searching for jobs",
        applicationProgress: "Application Progress",
        totalSaved: "Total Saved",
        applied: "Applied",
        interviewing: "Interviewing",
        offers: "Offers",
        successStories: "Success Stories",
        joinThousands: "Join thousands",
        testimonial1: "Found my dream job in 2 weeks!",
        users: "Users",
        jobsLanded: "Jobs",
        rating: "Rating",
        usersHired: "Users Got Hired This Month",
        avgTime: "Average Time to Interview",
        days: "days",
        satisfactionRate: "User Satisfaction",
        upgradeTitle: "Unlock Your Career Potential",
        upgradeDesc: "Upgrade to Pro and get unlimited resumes, AI enhancements, and priority support",
        upgradeCTA: "Upgrade to Pro",
        upgradePro: "Upgrade to Pro",
        unlockFeatures: "Unlock all premium features",
        unlimitedCV: "Unlimited CVs",
        advancedAI: "Advanced AI",
        prioritySupport: "Priority Support",
        upgradeNow: "Upgrade Now",
        proFeature1: "Unlimited CV generations",
        proFeature2: "Advanced AI optimization",
        proFeature3: "Priority support 24/7",
        proFeature4: "Premium templates",
        selectCV: "Select a CV for AI Enhancement",
        selectCVDesc: "Choose which CV you want to enhance with AI",
        noResumesForAI: "No resumes available",
        createCVFirst: "Create a CV first to use AI enhancement",
        aiMatching: "AI Job Matching",
        aiMatchingDesc: "Smart recommendations",
        tryNow: "Try Now",
        explore: "Explore",
        aiCoverLetter: "AI Cover Letters",
        aiCoverLetterDesc: "Professional in seconds",
        generate: "Generate",
        trackProgress: "Track your progress",
        active: "Active",
        trackJourney: "Track your job application journey",
        loadingJobs: "Loading jobs...",
        loadingRemoteJobs: "Loading remote jobs...",
        moreRemoteOpportunities: "more remote opportunities",
        moreSavedJobs: "more saved jobs",
        jobSearch: "Job Search",
        advancedSearch: "Advanced Search",
        searchWorldwide: "Search for jobs worldwide",
        advancedSearchDesc: "Filter with advanced options"
      },
      // Welcome Banner
      welcomeBanner: {
        heading: "Your Career Workspace",
        description: "Build your resume, find matching jobs, and generate tailored cover letters with AI.",
        createResume: "Create Your First Resume",
        searchJobs: "Search Jobs",
        exploreAI: "Explore AI Features"
      },
      // Onboarding Checklist
      onboarding: {
        title: "Getting Started Checklist",
        stepsRemaining_one: "{{count}} step remaining",
        stepsRemaining_other: "{{count}} steps remaining",
        allDone: "All done! 🎉",
        step_resume: "Create your first resume",
        step_resume_desc: "Build an ATS-ready CV using our smart builder",
        step_resume_cta: "Create Resume",
        step_search: "Search for a job",
        step_search_desc: "Find relevant jobs and save the ones you like",
        step_search_cta: "Search Jobs",
        step_match: "Run an AI job match",
        step_match_desc: "Let AI compare your CV against a job description",
        step_match_cta: "Match Now",
        step_cover: "Generate a cover letter",
        step_cover_desc: "Create a tailored cover letter in one click",
        step_cover_cta: "Generate"
      },
      // Auth
      auth: {
        welcomeBack: "Welcome Back",
        signInDesc: "Sign in to your account to continue",
        emailAddress: "Email Address",
        enterEmail: "Enter your email",
        password: "Password",
        enterPassword: "Enter your password",
        signIn: "Sign In",
        signingIn: "Signing In...",
        orContinueWith: "Or continue with",
        googleSignInFailed: "Google sign-in failed",
        forgotPassword: "Forgot Password?",
        createAccount: "Create Account",
        signUp: "Sign Up",
        signingUp: "Signing Up...",
        alreadyHaveAccount: "Already have an account?",
        dontHaveAccount: "Don't have an account?",
        fullName: "Full Name",
        enterFullName: "Enter your full name",
        firstName: "First Name",
        firstNamePlaceholder: "First name",
        lastName: "Last Name",
        lastNamePlaceholder: "Last name",
        confirmPassword: "Confirm Password",
        enterConfirmPassword: "Confirm your password",
        passwordsDontMatch: "Passwords don't match",
        accountCreated: "Account created successfully!",
        resetEmailSent: "Password reset email sent! Check your inbox.",
        resetEmailSentTitle: "Check Your Email",
        resetEmailSentDesc: "We've sent a password reset link to your email address. Please check your inbox and follow the instructions to reset your password.",
        didntReceiveEmail: "Didn't receive the email?",
        resendEmail: "Resend Email",
        backToLogin: "Back to Login",
        email: "Email Address",
        emailPlaceholder: "Enter your email",
        sending: "Sending...",
        sendResetLink: "Send Reset Link",
        resetInstructions: "Enter your email address and we'll send you a link to reset your password.",
        // Email verification
        verifyEmailTitle: "Verify Your Email",
        verifyEmailDesc: "We've sent a verification link to your email address. Please check your inbox and click the link to verify your account.",
        verificationEmailSent: "Verification email sent! Check your inbox.",
        emailVerified: "Email verified successfully!",
        emailVerifiedTitle: "Email Verified!",
        emailVerifiedDesc: "Your email has been verified. Redirecting you to the dashboard...",
        emailNotVerifiedYet: "Email not verified yet. Please check your inbox.",
        iVerifiedMyEmail: "I've Verified My Email",
        resendVerificationEmail: "Send Verification Email",
        checkSpamFolder: "Can't find the email? Check your spam folder.",
        checking: "Checking...",
        redirecting: "Redirecting...",
        autoCheckingVerification: "Automatically checking verification status..."
      },
      // Hero Section
      hero: {
        title: "Create Your Professional Resume",
        subtitle: "Stand out from the crowd with a professionally crafted resume and cover letter",
        getStarted: "Get Started Now",
        aiPowered: "AI-Powered CV Builder",
        landDreamJob: "Land Your Dream Job",
        landDreamJobDesc: "Create a professional CV that gets you noticed by recruiters and hiring managers. Our AI technology optimizes your resume for maximum impact and ATS compatibility.",
        startBuilding: "Start Building Now",
        seeStories: "See Success Stories",
        instantUpgrade: "Instant CV Upgrade",
        transformCV: "Transform Your Existing CV",
        transformCVDesc: "Upload your current resume and watch our AI instantly enhance it with professional formatting, keyword optimization, and industry-specific improvements.",
        uploadUpgrade: "Upload & Upgrade",
        previewTransforms: "Preview Transformations",
        smartCreation: "Smart CV Creation",
        buildPerfect: "Build Your Perfect Resume",
        buildPerfectDesc: "Our intelligent questionnaire guides you through creating a compelling CV that showcases your unique value proposition to employers.",
        startSmart: "Start Smart Builder",
        tryDemo: "Try Interactive Demo"
      },
      // Features
      features: {
        title: "Why Choose Our CV Builder?",
        subtitle: "Everything you need to create a professional resume",
        templates: "Professional Templates",
        customize: "Easy to Customize", 
        download: "Instant Download",
        templatesDesc: "Choose from dozens of ATS-friendly templates designed by recruitment experts",
        customizeDesc: "Easily customize your resume with our intuitive drag-and-drop editor",
        downloadDesc: "Download your resume in PDF or DOCX format instantly",
        advancedBuilder: "Advanced CV Builder",
        advancedBuilderDesc: "Create professional CVs with modern templates and full customization",
        aiChat: "AI-Powered Chat",
        aiChatDesc: "Get personalized CV improvement tips from our AI assistant",
        multiExport: "Multiple Export Formats",
        multiExportDesc: "Save your CV as PDF, Word, or HTML format",
        autoEnhancement: "Auto Enhancement",
        autoEnhancementDesc: "Automatic content enhancement and SEO optimization",
        millionsUsers: "Millions of Users",
        millionsUsersDesc: "Join millions of professionals who trust our services",
        securityPrivacy: "Security & Privacy",
        securityPrivacyDesc: "Your data is protected with the latest security and encryption",
        optimization: "Smart Optimization",
        optimizationDesc: "AI-powered content suggestions and keyword optimization for better visibility",
        atsOptimized: "ATS-Friendly",
        atsOptimizedDesc: "Our templates are optimized for Applicant Tracking Systems used by major companies",
        bilingualSupport: "Bilingual Support",
        bilingualSupportDesc: "Create CVs in both English and Arabic with proper RTL support and cultural adaptations",
        aiSuggestions: "AI-Powered Suggestions",
        aiSuggestionsDesc: "Get intelligent recommendations for content, formatting, and keywords to make your CV stand out"
      },
      // Journey Steps
      journey: {
        title: "How It Works",
        subtitle: "Follow our simple 4-step process to create your perfect CV",
        step1: "Choose Your Method",
        step1Desc: "Upload existing CV or start from scratch with our step-by-step builder",
        step2: "Smart Form or AI Extraction",
        step2Desc: "Answer smart questions or let AI extract data from your current resume",
        step3: "Customize & Preview",
        step3Desc: "Edit content, choose templates, and preview your professional CV",
        step4: "Download & Share",
        step4Desc: "Export in multiple formats and start applying to your dream jobs",
        getStarted: "Get Started",
        tryAI: "Try AI Chat",
        previewDemo: "Preview Demo",
        exportOptions: "Export Options"
      },
      // CV Builder
      cvBuilder: {
        title: "CV Builder",
        chooseLanguage: "Choose Language",
        uploadExisting: "Upload Existing CV",
        startFromScratch: "Start from Scratch",
        uploadDescription: "Upload your existing CV (PDF or Word) and we'll extract the data for you",
        scratchDescription: "Answer simple questions and we'll build your CV step by step",
        continue: "Continue",
        back: "Back",
        next: "Next",
        skip: "Skip",
        done: "Done",
        preview: "Preview",
        download: "Download",
        edit: "Edit",
        view: "View",
        chooseTemplate: {
          title: "CV Builder - Choose Template | CvLogs",
          description: "Choose from professional CV templates and start building your resume",
          heading: "Choose Your CV Template",
          subtitle: "Select a professional template that best represents your style",
          templates: "Professional Templates",
          recent: "Recent CVs",
          upload: "Upload Existing CV",
          uploadDesc: "Import your existing CV and enhance it with our tools",
          uploadButton: "Upload CV",
          startFrom: "Start from scratch",
          startFromDesc: "Begin with a blank template and build your CV step by step",
          startBuilding: "Start Building",
          preview: "Preview",
          select: "Select Template",
          popular: "Popular",
          noRecent: "No recent CVs yet",
          createFirst: "Create your first CV to see it here",
          createFirstCV: "Create Your First CV",
          lastUpdated: "Last updated",
          continueEditing: "Continue Editing"
        },
        templates: {
          modern: {
            name: "Modern Professional",
            description: "Clean and contemporary design perfect for tech and creative roles"
          },
          classic: {
            name: "Classic Executive", 
            description: "Traditional layout ideal for corporate and executive positions"
          },
          creative: {
            name: "Creative Designer",
            description: "Bold and artistic design for creative professionals"
          },
          minimalist: {
            name: "Minimalist Clean",
            description: "Simple and elegant design that focuses on content"
          }
        }
      },
      // Steps
      steps: {
        personalInfo: "Personal Information",
        education: "Education",
        experience: "Work Experience", 
        projects: "Projects",
        courses: "Training & Certifications",
        skills: "Skills",
        review: "Review & Download"
      },
      // Auth Pages
      auth: {
        // Login
        welcomeBack: "Welcome Back",
        signInDesc: "Sign in to your account to continue building amazing CVs",
        emailAddress: "Email Address",
        password: "Password",
        enterEmail: "Enter your email",
        enterPassword: "Enter your password",
        forgotPassword: "Forgot your password?",
        signIn: "Sign In",
        signingIn: "Signing In...",
        orContinueWith: "Or continue with",
        signInGoogle: "Sign in with Google",
        connectingGoogle: "Connecting to Google...",
        noAccount: "Don't have an account?",
        signUpNow: "Sign up now",
        
        // Register
        getStarted: "Get Started",
        createAccount: "Create your account and start building professional CVs",
        firstName: "First Name",
        firstNamePlaceholder: "First name",
        lastName: "Last Name",
        lastNamePlaceholder: "Last name",
        fullName: "Full Name",
        enterFullName: "Enter your full name",
        confirmPassword: "Confirm Password",
        confirmPasswordPlaceholder: "Confirm your password",
        createAccountBtn: "Create Account",
        creatingAccount: "Creating Account...",
        signUpGoogle: "Sign up with Google",
        hasAccount: "Already have an account?",
        signInNow: "Sign in now",
        
        // Common
        email: "Email",
        name: "Name",
        // Forgot Password & Reset
        forgotPasswordTitle: "Forgot Password?",
        resetInstructions: "Enter your email address and we'll send you a link to reset your password.",
        emailPlaceholder: "Enter your email",
        sending: "Sending...",
        sendResetLink: "Send Reset Link",
        backToLogin: "Back to Login",
        resetEmailSent: "Password reset email sent! Check your inbox.",
        resetEmailSentTitle: "Check Your Email",
        resetEmailSentDesc: "We've sent a password reset link to your email address. Please check your inbox and follow the instructions to reset your password.",
        didntReceiveEmail: "Didn't receive the email?",
        resendEmail: "Resend Email",
        // Email Verification
        verifyEmailTitle: "Verify Your Email",
        verifyEmailDesc: "We've sent a verification link to your email address. Please check your inbox and click the link to verify your account.",
        verificationEmailSent: "Verification email sent! Check your inbox.",
        emailVerified: "Email verified successfully!",
        emailVerifiedTitle: "Email Verified!",
        emailVerifiedDesc: "Your email has been verified. Redirecting you to the dashboard...",
        emailNotVerifiedYet: "Email not verified yet. Please check your inbox.",
        iVerifiedMyEmail: "I've verified my email",
        resendVerificationEmail: "Resend verification email"
      },
      // Error Messages
      errors: {
        fillAllFields: "Please fill in all fields",
        passwordsDontMatch: "Passwords don't match",
        passwordTooShort: "Password must be at least 6 characters",
        loginFailed: "Login failed. Please try again.",
        registrationFailed: "Registration failed. Please try again.",
        resetFailed: "Failed to send reset email",
        userNotFound: "No account found with this email",
        wrongPassword: "Invalid password",
        invalidEmail: "Invalid email address",
        tooManyRequests: "Too many failed attempts. Please try again later.",
        userDisabled: "This account has been disabled",
        emailInUse: "An account with this email already exists",
        weakPassword: "Password is too weak",
        popupBlocked: "Popup was blocked. Please allow popups and try again.",
        popupClosed: "Sign-in was cancelled",
        accountExistsDifferent: "An account already exists with this email using a different sign-in method",
        googleSignInFailed: "Google sign-in failed. Please try again.",
        embeddedBrowser: "Google Sign-In is not supported in this browser. Please open in Safari or Chrome.",
        networkError: "Network error. Please check your connection.",
        emailNotVerified: "Please verify your email address before logging in.",
        emailSendFailed: "Failed to send verification email.",
        checkVerificationFailed: "Failed to check verification status.",
        fillRequired: "Please fill in all required fields",
        addFailed: "Failed to add entry",
        updateFailed: "Failed to update entry",
        deleteFailed: "Failed to delete entry",
        saveFailed: "Failed to save changes"
      },
      // Embedded Browser Modal
      embeddedBrowser: {
        title: "Open in Browser Required",
        description: "For security reasons, Google Sign-In is not available in this browser. Please open this page in Safari or Chrome.",
        howTo: "How to continue:",
        step1: "1. Tap the menu icon (⋯) in the top right",
        step2: "2. Select 'Open in Safari' or 'Open in Browser'",
        step3: "3. Or copy the link below and paste in your browser",
        copyLink: "Copy Link",
        copied: "Copied!",
        tryOpenBrowser: "Try Open in Browser",
        useEmail: "Continue with Email instead",
        googleNotAvailable: "Google Sign-In (Open in Browser)",
        detectedIn: "Detected:",
        inAppBrowser: "in-app browser"
      },
      // Success Messages
      success: {
        welcomeBack: "Welcome back!",
        accountCreated: "Account created! Please check your email to verify your account.",
        passwordReset: "Password reset email sent!",
        googleSignIn: "Successfully signed in with Google!"
      },
      // Common
      common: {
        loading: "Loading...",
        error: "Error",
        success: "Success",
        save: "Save",
        cancel: "Cancel",
        delete: "Delete",
        add: "Add",
        required: "Required",
        optional: "Optional",
        close: "Close",
        open: "Open",
        yes: "Yes",
        no: "No",
        createCV: "Create CV"
      },
      // Pricing
      pricing: {
        title: "Choose Your Plan",
        subtitle: "Start for free, upgrade when you need more features",
        free: "Free",
        pro: "Pro",
        month: "/month",
        features: "Features",
        getStarted: "Get Started",
        upgrade: "Upgrade Now",
        popular: "Most Popular"
      },
      // Home page content
      home: {
        metaTitle: "CvLogs - Professional CV Builder",
        metaDescription: "Create stunning professional CVs with AI-powered suggestions",
        heroTitle: "Build Your Perfect CV in Minutes",
        heroSubtitle: "Create professional, ATS-friendly resumes with AI-powered suggestions and beautiful templates",
        heroFeatures: "Trusted by 10,000+ professionals worldwide",
        continueBuilding: "Continue Building",
        startBuilding: "Start Building Your CV",
        benefits: {
          title: "Why Choose CvLogs?",
          professionalTemplates: "Professional templates designed by experts",
          realtimePreview: "Real-time preview as you build",
          industrySpecific: "Industry-specific suggestions",
          mobileResponsive: "Mobile-responsive design",
          secureStorage: "Secure cloud storage",
          multipleFormats: "Export in multiple formats"
        },
        benefitsList: [
          "Professional templates designed by experts",
          "Real-time preview as you build",
          "Industry-specific suggestions",
          "Mobile-responsive design", 
          "Secure cloud storage",
          "Export in multiple formats"
        ],
        testimonials: {
          title: "What Our Users Say",
          user1: "CvLogs helped me land my dream job! The AI suggestions were incredibly helpful.",
          user2: "The templates are professional and the interface is so easy to use.",
          user3: "I created a stunning CV in just 15 minutes. Highly recommended!"
        },
        testimonial: {
          text: "CvLogs helped me land my dream job! The AI suggestions were spot-on and the templates look incredibly professional.",
          author: "Sarah Johnson",
          role: "Software Engineer at Google"
        },
        cta: {
          title: "Ready to Build Your Perfect CV?",
          subtitle: "Join thousands of professionals who have successfully landed their dream jobs",
          button: "Start Building Now",
          free: "It's completely free to get started"
        }
      },
      // Resume Chat
      resumeChat: {
        title: "AI Resume Assistant | CvLogs",
        description: "Get personalized CV advice and build your resume with AI assistance",
        heading: "AI Resume Assistant",
        subtitle: "Ask me anything about your resume or let me help you build one step by step",
        placeholder: "Type your message here...",
        send: "Send",
        quickQuestions: "Quick Questions",
        exampleQuestions: {
          improve: "How can I improve my resume?",
          format: "What's the best resume format?",
          keywords: "What keywords should I include?",
          experience: "How do I describe my experience?"
        },
        aiTyping: "AI is typing...",
        welcome: "Welcome! I'm your AI resume assistant. How can I help you today?",
        buildCV: "Build CV with AI",
        startBuilding: "Start Building"
      },
      
      // CV Builder Form
      form: {
        title: "Build Your CV",
        editTitle: "Edit Your CV",
        description: "Follow the steps below to create your professional CV",
        metaDescription: "Build your professional CV step by step with our modern form builder",
        progress: "Progress",
        progressDescription: "Track your CV completion progress",
        step: "Step",
        of: "of",
        complete: "Complete",
        next: "Next",
        previous: "Previous", 
        skip: "Skip",
        save: "Save",
        saveCV: "Save CV",
        updateCV: "Update CV",
        saving: "Saving...",
        updating: "Updating...",
        finish: "Finish",
        required: "Required field",
        optional: "Optional",
        languageChanged: "Language changed to",
        readyToSave: "Ready to save!",
        
        // Loading states
        loading: {
          title: "Loading CV Data",
          message: "Please wait while we load your CV for editing..."
        },
        preparing: {
          title: "Preparing Form",
          message: "Please wait while we prepare the CV builder..."
        },
        
        // Steps
        steps: {
          personalInfo: {
            title: "Personal Information",
            subtitle: "Let's start with your basic information",
            description: "This information will appear at the top of your CV"
          },
          education: {
            title: "Education",
            subtitle: "Add your educational background",
            description: "Include your degrees, certifications, and academic achievements"
          },
          track: {
            title: "Career Track",
            subtitle: "Select your professional field",
            description: "Choose the industry or field you work in"
          },
          experience: {
            title: "Work Experience", 
            subtitle: "Tell us about your work history",
            description: "List your professional experience in reverse chronological order"
          },
          projects: {
            title: "Projects",
            subtitle: "Showcase your notable projects",
            description: "Include personal, academic, or professional projects"
          },
          certifications: {
            title: "Certifications",
            subtitle: "Add your professional certifications",
            description: "Include certificates, licenses, and professional credentials"
          },
          skills: {
            title: "Skills & Languages",
            subtitle: "What are you good at?",
            description: "List your technical skills, soft skills, and languages"
          },
          review: {
            title: "Review & Finish",
            subtitle: "Review your information",
            description: "Check everything looks good before generating your CV"
          }
        },
        
        // Career Tracks
        tracks: {
          technology: "Technology",
          healthcare: "Healthcare",
          finance: "Finance",
          education: "Education",
          marketing: "Marketing",
          sales: "Sales",
          design: "Design",
          engineering: "Engineering",
          management: "Management",
          consulting: "Consulting",
          legal: "Legal",
          media: "Media",
          research: "Research",
          operations: "Operations",
          hr: "Human Resources",
          other: "Other"
        },

        // Track
        track: {
          customTrack: "Specify your field",
          customTrackPlaceholder: "Enter your professional field"
        },

        // Personal Information
        personalInfo: {
          fullName: {
            label: "Full Name",
            placeholder: "Enter your full name"
          },
          email: {
            label: "Email Address",
            placeholder: "your.email@example.com"
          },
          phone: {
            label: "Phone Number",
            placeholder: "+1 (555) 123-4567"
          },
          location: {
            label: "Location",
            placeholder: "City, Country"
          },
          website: {
            label: "Website/Portfolio",
            placeholder: "https://yourwebsite.com"
          },
          linkedin: {
            label: "LinkedIn Profile",
            placeholder: "https://linkedin.com/in/yourprofile"
          },
          github: {
            label: "GitHub Profile",
            placeholder: "https://github.com/yourusername"
          },
          summary: {
            label: "Professional Summary",
            placeholder: "A brief summary of your professional background and career objectives..."
          }
        },
        
        // Education
        education: {
          entry: "Education",
          degree: "Degree",
          degreePlaceholder: "Bachelor of Science in Computer Science",
          institution: "Institution",
          institutionPlaceholder: "University of Technology",
          field: "Field of Study",
          fieldPlaceholder: "Computer Science",
          graduationDate: "Graduation Date",
          location: "Location",
          locationPlaceholder: "City, Country",
          addEducation: "Add Education"
        },
        
        // Experience
        experience: {
          entry: "Experience",
          position: "Position",
          positionPlaceholder: "Software Engineer",
          company: "Company",
          companyPlaceholder: "Tech Company Inc.",
          startDate: "Start Date",
          endDate: "End Date",
          currentJob: "I currently work here",
          location: "Location",
          locationPlaceholder: "City, Country",
          description: "Job Description",
          descriptionPlaceholder: "Describe your key responsibilities and achievements...",
          addExperience: "Add Experience"
        },
        
        // Projects
        projects: {
          entry: "Project",
          name: "Project Name",
          namePlaceholder: "E-commerce Website",
          url: "Project URL",
          urlPlaceholder: "https://github.com/user/project",
          startDate: "Start Date",
          endDate: "End Date",
          technologies: "Technologies",
          technologiesPlaceholder: "React, Node.js, MongoDB",
          technologiesHint: "Separate technologies with commas",
          description: "Description",
          descriptionPlaceholder: "Brief description of the project...",
          addProject: "Add Project"
          },

        // Certifications
        certifications: {
          entry: "Certification",
          name: "Certification Name",
          namePlaceholder: "AWS Certified Solutions Architect",
          issuer: "Issuing Organization",
          issuerPlaceholder: "Amazon Web Services",
          date: "Date Obtained",
          credentialId: "Credential ID",
          credentialIdPlaceholder: "ABC123XYZ",
          url: "Verification URL",
          urlPlaceholder: "https://verify.certification.com",
          addCertification: "Add Certification"
        },
        
        // Volunteer
        volunteer: {
          title: "Volunteer Experience",
          entry: "Volunteer Experience",
          role: "Role",
          organization: "Organization",
          cause: "Cause",
          startDate: "Start Date",
          endDate: "End Date"
        },
        // Awards
        awards: {
          title: "Awards & Recognition",
          entry: "Award",
          name: "Award Title",
          organization: "Issuing Organization",
          date: "Date Received"
        },
        
        // Skills
        skills: {
          technical: "Technical Skills",
          technicalPlaceholder: "Add a technical skill and press Enter",
          languages: "Languages",
          languagesPlaceholder: "Add a language (e.g., English - Native) and press Enter",
          soft: "Soft Skills",
          softPlaceholder: "Add a soft skill and press Enter",
          pressEnter: "Press Enter to add"
        },

        // Review
        review: {
          congratulations: "Congratulations!",
          completedMessage: "You've completed your CV. You can now preview and download it.",
          previewCV: "Preview CV",
          editMore: "Edit More",
          sections: "Completed Sections",
          stats: "CV Statistics",
          education: "Education Entries",
          experience: "Work Experience",
          projects: "Projects",
          certifications: "Certifications"
        },
        

        

        

        

        

        
        // Common
        actions: {
          add: "Add",
          edit: "Edit",
          delete: "Delete",
          remove: "Remove",
          cancel: "Cancel",
          confirm: "Confirm"
        },
        
        validation: {
          required: "{{field}} is required",
          nameRequired: "Name is required",
          emailRequired: "Email is required",
          email: "Please enter a valid email address",
          invalidEmail: "Please enter a valid email address",
          url: "Please enter a valid URL",
          invalidUrl: "Please enter a valid URL",
          invalidUrlField: "{{field}} must be a valid URL (e.g., https://example.com)",
          phone: "Please enter a valid phone number",
          minLength: "Minimum {min} characters required",
          maxLength: "Maximum {max} characters allowed",
          pleaseFixErrors: "Please fix the errors:",
          correctFollowingErrors: "Please correct the following errors:",
          andMoreErrors: "+{{count}} more error(s)",
          englishOnly: "Please use English characters only",
          englishOnlyField: "{{field}} must be in English only",
          bilingualField: "{{field}} must be in English or Arabic only",
          invalidCharacters: "Contains invalid characters",
          invalidCharsField: "{{field}} contains invalid characters",
          noFutureDate: "{{field}} cannot be in the future",
          futureDateField: "{{field}} cannot be a future date",
          dateRange: "Start date must be before end date",
          startBeforeEnd: "Start date must be before end date",
          linkRequired: "Both link name and URL are required"
        }
      },
      
      // CV Generation
      cvGeneration: {
        title: "AI CV Generation",
        subtitle: "Generate your professional CV with AI",
        targetJob: "Target Job (Optional)",
        jobTitle: "Job Title",
        jobTitlePlaceholder: "e.g., Senior Frontend Developer",
        jobDescription: "Job Description",
        jobDescriptionPlaceholder: "Paste the job description here to get a tailored CV...",
        generateAICV: "Generate AI CV",
        generating: "Generating...",
        templateStyle: "Template Style",
        atsFriendly: "ATS-Friendly",
        atsFriendlyDesc: "Black & white, simple format",
        modernColorful: "Modern Colorful",
        modernColorfulDesc: "Vibrant design with colors",
        exportOptions: "Export Options",
        downloadAsPDF: "Download as PDF",
        downloadAsWord: "Download as Word",
        viewHistory: "View History",
        noCVGenerated: "No CV Generated Yet",
        saveDraft:"ٍSave Draft",
        noCVGeneratedDesc: "Generate your first AI-enhanced CV to see the preview here",
        generateFirstCV: "Generate Your First CV",
        loadingCV: "Loading your CV...",
        cvLoaded: "CV loaded successfully",
        enhancingSection: "Enhancing section...",
        enhancementSuccess: "CV section enhanced successfully!",
        enhancementError: "Failed to enhance CV section",
        enhancementNotAvailable: "Enhancement handler not available",
        aiEnhancement: "AI Enhancement",
        enhanceSection: "Enhance Section",
        enhanceButtonTooltip: "Click to enhance this section with AI",
        enhancementPrompt: "Enhancement Prompt",
        enhancementPromptPlaceholder: "Describe how you'd like to improve this section...",
        enhancementPromptHint: "Provide specific instructions for how AI should enhance this section",
        quickSuggestions: "Quick suggestions:",
        cancel: "Cancel",
        enhancing: "Enhancing...",
        enhancementModalTitle: "Enhance with AI",
        backToBuilder: "Back to Builder",
        enterPromptError: "Please enter a prompt or select a suggestion",
        aiContentHistory: "Enhanced Sections History",
        aiContent: "AI Content",
        history: "History",
        aiContentHistoryDesc: "View all AI-enhanced sections with their prompts and timestamps",
        loadingAIContent: "Loading AI content...",
        noAIContent: "No AI-enhanced content found",
        enhancedContent: "Enhanced Content",
        enhancedItems: "Enhanced Items",
        technicalSkills: "Technical Skills",
        softSkills: "Soft Skills",
        professionalSummary: "Professional Summary",
        experienceEnhancements: "Experience Enhancements",
        projectEnhancements: "Project Enhancements",
        skillsEnhancement: "Skills Enhancement",
        volunteerEnhancements: "Volunteer Enhancements",
        awardsEnhancements: "Awards Enhancements",
        experience: "Experience",
        project: "Project",
        volunteer: "Volunteer",
        award: "Award",
        item: "Item",
        copy: "Copy",
        copied: "Copied to clipboard",
        copyFailed: "Failed to copy",
        aiContentError: "Failed to load AI content",
        close: "Close",
        // Enhancement Section Menu
        enhancementSectionMenu: "AI Enhancement",
        enhancementSectionMenuDesc: "Select a section to enhance with AI",
        selectSectionToEnhance: "Select a section to enhance with AI",
        professionalSummarySection: "Professional Summary",
        professionalSummaryDesc: "Enhance your professional summary",
        skillsSection: "Skills",
        skillsDesc: "Improve your skills section",
        workExperienceSection: "Work Experience",
        workExperienceDesc: "Enhance individual experiences",
        projectsSection: "Projects",
        projectsDesc: "Enhance individual projects",
        volunteerWorkSection: "Volunteer Work",
        volunteerWorkDesc: "Enhance volunteer experiences",
        awardsSection: "Awards",
        awardsDesc: "Enhance your awards",
        noDataAvailable: "No data available",
        itemsCount: "items",
        oneItem: "item",
        howEnhancementWorks: "How AI Enhancement Works",
        enhancementWorkflowDesc: "Select a section above, provide instructions on how you want to improve it, and our AI will generate an enhanced version. You can review and compare before applying changes.",
        enhanceCVSections: "Enhance CV Sections",
        enhancementAvailable: "AI Enhancement Available!",
        enhancementHintDesc: "Use two ways to enhance your CV with AI:",
        enhancementQuickWay: "Quick: Click \"Enhance CV Sections\" button ←",
        enhancementDirectWay: "Direct: Hover over any section below and click the ✨ button",
        viewThisTemplate: "View This Template",
        makeItProfessional: "Make it more professional",
        addMoreImpact: "Add more impact",
        focusOnAchievements: "Focus on achievements",
        makeItConcise: "Make it concise",
        highlightTechnicalSkills: "Highlight technical skills",
        emphasizeLeadership: "Emphasize leadership",
        improveClarity: "Improve clarity",
        addQuantifiableResults: "Add quantifiable results",
        // Additional CVGeneration page translations
        title: "AI CV Generation",
        description: "Generate your professional CV with AI",
        backToBuilder: "Back to Builder",
        generatedCV: "Generated CV",
        enhance: "Enhance",
        aiContent: "AI Content",
        template: "Template",
        history: "History",
        regenerate: "Regenerate",
        exportPDF: "Export PDF",
        export: "Export",
        regenerateCV: "Regenerate CV",
        targetJobOptional: "Target a specific job (optional)",
        generateNow: "Generate",
        leaveBlankGeneral: "Leave blank for a general CV",
        aiEnhancedCV: "AI-Enhanced CV",
        quickActions: "Quick Actions",
        generateAICV: "Generate New AI CV",
        saveDraft: "Save Draft & View Old CV",
        saving: "Saving...",
        // ComparisonModal translations
        reviewChanges: "Review changes",
        original: "Original",
        aiEnhanced: "AI Enhanced",
        editing: "Editing",
        copyOriginal: "Copy original content",
        copyEnhanced: "Copy enhanced content",
        editManually: "Edit manually",
        stopEditing: "Stop editing",
        editEnhanced: "Edit the enhanced content...",
        noOriginalContent: "No original content",
        noEnhancedContent: "No enhanced content",
        processing: "Processing...",
        editBeforeApplying: "Edit before applying",
        acceptOrKeepOriginal: "Accept AI version or keep original",
        decline: "Decline",
        accept: "Accept",
        acceptAndApply: "Accept & Apply",
        changesApplied: "Changes applied successfully!",
        changesDeclined: "Changes declined",
        originalCopied: "Original copied to clipboard!",
        enhancedCopied: "Enhanced copied to clipboard!",
        // Template & Export translations
        templateAndExport: "Template & Export",
        currentTemplate: "Current Template",
        change: "Change",
        downloadPDF: "Download PDF",
        downloadWord: "Download Word",
        chooseTemplate: "Choose Your CV Template",
        templatePreviewDesc: "Preview and select the perfect template for your CV",
        // Target Job translations
        targetJob: "Target Job (Optional)",
        leaveBlankGeneral: "Leave blank for general CV",
        jobTitle: "Job Title",
        jobTitlePlaceholder: "e.g., Senior Frontend Developer",
        jobDescription: "Job Description",
        jobDescriptionPlaceholder: "Paste the job description here...",
        generating: "Generating...",
        generateWithTargetJob: "Generate with Target Job",
        generatingPreview: "Generating preview...",
        generatingPDF: "Generating PDF...",
        templateNotFound: "CV template not found. Please wait for the template to load.",
        pdfDownloaded: "PDF downloaded successfully!",
        exportFailed: "Failed to export PDF",
        refreshPreview: "Refresh Preview",
        refreshing: "Refreshing...",
        retryPreview: "Retry Preview",
        // Generation History
        generationHistory: "Generation History",
        generationHistoryDesc: "View and restore previous CV generations",
        noGenerationHistory: "No generation history yet",
        generationLabel: "Generation",
        current: "Current",
        skillsGenerated: "Skills Generated",
        totalGenerations: "Total Generations",
        sectionsAvailable: "sections available for enhancement"
      },
      // CV Selector Modal
      cvSelector: {
        title: "Select Your CV",
        noCVs: "No CVs found. Create one first!",
        selectInstruction: "Select the CV you want to use for job matching",
        lastUpdated: "Last updated",
        continue: "Continue with Selected CV"
      },
      // Template Selector
      templateSelector: {
        title: "Choose Your CV Template",
        description: "Select a template and preview it before downloading. ATS templates are optimized for applicant tracking systems.",
        atsFriendly: "ATS-Friendly Templates",
        recommended: "Recommended",
        viewTemplate: "View This Template",
        coloredDesign: "Colored & Design Templates",
        colorfulHeader: "Colorful header design",
        selected: "Selected",
        generating: "Generating...",
        downloadPDF: "Download PDF"
      },
      // Job Search
      jobSearch: {
        title: "Job Search",
        subtitle: "Search for jobs on LinkedIn, Indeed, and more",
        searchFilters: "Search Filters",
        viewHistory: "View History",
        searchHistory: "Search History",
        noHistory: "No search history",
        provider: "Provider",
        searchTerm: "Search Term",
        searchTermPlaceholder: "Search jobs by title, company...",
        location: "Location",
        locationPlaceholder: "e.g., Cairo, Egypt",
        country: "Country",
        resultsWanted: "Results",
        daysOld: "Max Days Old",
        hoursOld: "Hours Old (max)",
        fetchDescription: "Fetch Job Descriptions",
        search: "Search",
        searching: "Searching for jobs...",
        enterSearchTerm: "Please enter a search term",
        searchSuccess: "Found {count} jobs",
        noResults: "No jobs found",
        searchError: "Failed to search jobs",
        results: "results",
        refresh: "Refresh",
        refreshSuccess: "Refreshed search found {count} jobs",
        refreshError: "Failed to refresh search",
        noSearchToRefresh: "No search to refresh",
        loadedFromHistory: "Loaded search from history",
        loadError: "Failed to load search",
        noJobs: "No jobs found",
        noJobsDesc: "Try adjusting your search criteria",
        remote: "Remote",
        viewJob: "View Job",
        showDescription: "Show Description",
        hideDescription: "Hide Description",
        unnamedSearch: "Unnamed Search",
        history: "History",
        applyNow: "Apply Now",
        proMatch: "Pro Match",
        generateCoverLetter: "Generate Cover Letter",
        matchWith: "Match with:",
        analyzing: "Analyzing...",
        viewMatchAnalysis: "View Match Analysis",
        saveToLibrary: "Save to library",
        savedToLibrary: "Saved to library",
        createCV: "Create CV"
      },
      // CV Builder specific
      cvBuilder: {
        noResumes: "No resumes yet",
        getStarted: "Get started by creating your first professional resume",
        createNew: "Create New Resume",
        createFirst: "Create Your First Resume",
        loading: "Loading your resumes...",
        untitled: "Untitled Resume",
        edit: "Edit",
        createModalTitle: "Create New Resume",
        createModalDesc: "Choose a name for your CV document (e.g., \"Frontend Developer Resume 2024\")",
        resumeName: "CV Document Name",
        resumeNamePlaceholder: "e.g., Software Developer Resume",
        resumeNameHint: "This is your document's title, not your personal name. You'll add your personal details in the next step.",
        createButton: "Create Resume",
        creating: "Creating...",
        cancel: "Cancel"
      },
      // Settings
      settings: {
        title: "Settings",
        account: "Account",
        profile: "Profile",
        preferences: "Preferences",
        language: "Language",
        theme: "Theme",
        notifications: "Notifications",
        privacy: "Privacy",
        security: "Security",
        billing: "Billing",
        subscription: "Subscription",
        helpSupport: "Help & Support"
      },
      // Subscription
      subscription: {
        title: "Subscription Plan",
        currentPlan: "Current Plan",
        free: "Free",
        pro: "Pro",
        upgrade: "Upgrade",
        downgrade: "Downgrade",
        cancel: "Cancel",
        manage: "Manage Subscription",
        features: "Features",
        billing: "Billing",
        nextBillingDate: "Next Billing Date",
        paymentMethod: "Payment Method"
      },
      // Overview/Dashboard extended
      overview: {
        title: "Overview",
        welcomeBack: "Welcome back",
        summary: "Summary",
        recentActivity: "Recent Activity",
        stats: "Statistics",
        quickLinks: "Quick Links"
      },
      // Edit Resume
      editResume: {
        sections: "Sections",
        progress: "Progress",
        previous: "Previous",
        next: "Next",
        finish: "Finish & Generate",
        saveDraft: "Save Draft",
        saveMagic: "Save & See CVLogs Magic",
        saveMagicDesc: "Let CVLogs intelligence tailor it",
        saveAndGenerate: "Save & Generate",
        previewCV: "Preview CV"
      },
      // Tabs
      tabs: {
        personal: "Personal Info",
        education: "Education",
        experience: "Experience",
        skills: "Skills",
        languages: "Languages",
        projects: "Projects",
        licenses: "Licenses",
        training: "Training",
        awards: "Awards",
        volunteer: "Volunteer",
        recommendations: "Recommendations",
        publications: "Publications",
        interests: "Interests"
      },
      // Job Library
      jobLibrary: {
        title: "My Job Library",
        myJobLibrary: "My Job Library",
        subtitle: "Track and manage your saved job opportunities",
        loading: "Loading Your Job Library...",
        totalSaved: "Total Saved",
        avgMatch: "Avg Match",
        searchPlaceholder: "Search saved jobs by title, company, location...",
        filters: "Filters",
        refresh: "Refresh",
        applicationStatus: "Application Status",
        allStatuses: "All Statuses",
        jobSource: "Job Source",
        allSources: "All Sources",
        jobSearchLinkedIn: "Job Search (LinkedIn/Indeed)",
        remoteJobsSource: "Remote Jobs",
        advancedSearchSource: "Advanced Search",
        reset: "Reset",
        applyFilters: "Apply Filters",
        noSavedJobs: "No Saved Jobs",
        noSavedJobsDesc: "Start saving jobs from Job Search or Remote Jobs pages",
        noJobsMatch: "No jobs match your filters. Try adjusting your search.",
        searchJobsBtn: "Search Jobs",
        showing: "Showing",
        matchWith: "Match with:",
        fullTime: "Full-time",
        remote: "Remote",
        jobSearch: "Job Search",
        advanced: "Advanced",
        saved: "Saved",
        viewDetails: "View Details",
        removeFromLibrary: "Remove from Library",
        personalNotes: "Personal Notes",
        addNotes: "Add Notes",
        editNotes: "Edit Notes",
        saveNotes: "Save Notes",
        cancel: "Cancel",
        jobDescription: "Job Description",
        type: "Type",
        location: "Location",
        posted: "Posted",
        requiredSkills: "Required Skills",
        notApplied: "Not Applied",
        applied: "Applied",
        interviewing: "Interviewing",
        rejected: "Rejected",
        accepted: "Accepted",
        avgMatch: "Avg Match",
        searchPlaceholder: "Search jobs by title, company, or location...",
        filterStatus: "Filter by Status",
        filterSource: "Filter by Source",
        allStatuses: "All Statuses",
        allSources: "All Sources",
        showFilters: "Show Filters",
        hideFilters: "Hide Filters",
        noJobs: "No Jobs Found",
        noJobsDesc: "You haven't saved any jobs yet. Start by searching for jobs!",
        startSearching: "Start Job Search",
        viewDetails: "View Details",
        generateCoverLetter: "Generate Cover Letter",
        analyzeMatch: "Analyze Match",
        proMatch: "Pro Match",
        updateStatus: "Update Status",
        addNotes: "Add Notes",
        editNotes: "Edit Notes",
        saveNotes: "Save Notes",
        notesPlaceholder: "Add your notes about this job...",
        deleteJob: "Delete Job",
        confirmDelete: "Confirm Delete",
        deleteMessage: "Are you sure you want to remove this job from your library?",
        matchScore: "Match Score",
        calculating: "Calculating...",
        analyzing: "Analyzing...",
        selectCV: "Select a CV to calculate match score",
        loadingJobs: "Loading Your Job Library...",
        createCV: "Create CV"
      },
      // Job Search
      jobSearchPage: {
        title: "Job Search",
        subtitle: "Find your next opportunity",
        searchPlaceholder: "Job title, keywords, or company",
        locationPlaceholder: "City, state, or zip code",
        searchButton: "Search Jobs",
        filters: "Filters",
        jobType: "Job Type",
        experienceLevel: "Experience Level",
        salary: "Salary Range",
        datePosted: "Date Posted",
        remote: "Remote",
        fullTime: "Full Time",
        partTime: "Part Time",
        contract: "Contract",
        internship: "Internship",
        entryLevel: "Entry Level",
        midLevel: "Mid Level",
        seniorLevel: "Senior Level",
        anyTime: "Any Time",
        past24h: "Past 24 Hours",
        pastWeek: "Past Week",
        pastMonth: "Past Month",
        applyFilters: "Apply Filters",
        clearFilters: "Clear Filters",
        resultsFound: "jobs found",
        noResults: "No Jobs Found",
        noResultsDesc: "Try adjusting your search criteria",
        saveJob: "Save Job",
        viewJob: "View Job",
        applyNow: "Apply Now",
        loading: "Loading jobs...",
        searching: "Searching..."
      },
      // Cover Letter
      coverLetterModal: {
        generateTitle: "Generate Cover Letter",
        viewTitle: "Cover Letter",
        selectResume: "Select Resume",
        chooseResume: "Choose a resume...",
        companyName: "Company Name",
        companyPlaceholder: "e.g., Google, Microsoft, Amazon",
        jobTitle: "Job Title",
        jobTitlePlaceholder: "e.g., Senior Software Engineer, Product Manager",
        jobDescription: "Job Description",
        jobDescPlaceholder: "Paste the full job description here...",
        customInstructions: "Custom Instructions (Prompt)",
        optional: "Optional",
        customPlaceholder: "e.g., Emphasize my leadership experience, Highlight Python skills, Use a formal tone",
        generateBtn: "Generate Cover Letter with AI",
        generatingBtn: "Generating Cover Letter...",
        exportPDF: "Export PDF",
        preparing: "Preparing...",
        edit: "Edit",
        save: "Save",
        regenerate: "Regenerate",
        required: "Please select a resume",
        requiredCompany: "Please enter company name",
        requiredTitle: "Please enter job title",
        requiredDesc: "Please enter job description",
        successGenerate: "Cover letter generated",
        successRegenerate: "Cover letter regenerated successfully",
        successUpdate: "Cover letter updated successfully",
        errorGenerate: "Failed to generate cover letter",
        errorRegenerate: "Failed to regenerate cover letter",
        errorUpdate: "Failed to update cover letter",
        errorResumes: "Failed to load resumes"
      },
      coverLetter: {
        title: "Cover Letter Generator",
        history: "Cover Letter History",
        historyTitle: "Cover Letter History",
        historySubtitle: "View and manage your generated cover letters",
        generate: "Generate Cover Letter",
        generating: "Generating...",
        selectJob: "Select a Job",
        selectCV: "Select Your CV",
        jobTitle: "Job Title",
        company: "Company",
        jobDescription: "Job Description",
        customInstructions: "Custom Instructions (Optional)",
        customPlaceholder: "Add any specific points you want to highlight...",
        generateButton: "Generate Cover Letter",
        download: "Download",
        copy: "Copy to Clipboard",
        edit: "Edit",
        delete: "Delete",
        preview: "Preview",
        createdOn: "Created on",
        noHistory: "No Cover Letters Yet",
        noHistoryDesc: "Generate your first cover letter to see it here",
        createCustom: "Create Custom Cover Letter",
        letter: "Letter",
        letters: "Letters",
        searchPlaceholder: "Search by company or job title...",
        allResumes: "All Resumes",
        untitledResume: "Untitled Resume",
        noLettersFound: "No cover letters found",
        generateFirst: "Generate your first cover letter from the job search or library pages",
        adjustFilters: "Try adjusting your filters",
        goToSearchBtn: "Go to Job Search",
        edited: "Edited",
        view: "View",
        editLetter: "Edit Cover Letter",
        deleteTitle: "Delete Cover Letter",
        deleteMessage: "Are you sure you want to delete this cover letter? This action cannot be undone.",
        deleteConfirm: "Delete",
        cancel: "Cancel",
        startGenerating: "Generate Cover Letter",
        copied: "Copied to clipboard!",
        downloadSuccess: "Cover letter downloaded successfully!",
        deleteConfirm: "Are you sure you want to delete this cover letter?",
        loading: "Loading cover letters..."
      },
      // Job Match
      jobMatch: {
        title: "AI Job Matcher",
        subtitle: "Analyze how well your resume matches any job description",
        analyzeProfile: "Analyze My Profile",
        selectCV: "Select Your CV",
        selectResume: "Select Your Resume",
        selectJob: "Select a Job to Analyze",
        untitledResume: "Untitled Resume",
        jobDescription: "Job Description",
        jobDescriptionPlaceholder: "Paste the complete job description here...",
        analyzeMatch: "Analyze Match with AI",
        matchScore: "Match Score",
        strengths: "Strengths",
        weaknesses: "Areas for Improvement",
        gaps: "Skill Gaps",
        recommendations: "AI Recommendations",
        analyzing: "Analyzing Match...",
        noMatch: "No Match Data",
        noMatchDesc: "Select a CV and job to see match analysis",
        readyToAnalyze: "Ready to Analyze",
        readyToAnalyzeDesc: "Select your resume and paste a job description to get AI-powered insights on how well you match the position.",
        strongMatch: "Strong Match",
        goodMatch: "Good Match",
        fairMatch: "Fair Match",
        weakMatch: "Weak Match",
        excellentMatch: "Excellent Match",
        poorMatch: "Poor Match",
        viewDetails: "View Details",
        improveMatch: "How to Improve",
        keySkills: "Key Skills",
        missingSkills: "Missing Skills",
        matchedSkills: "Matched Skills",
        missingRequiredSkills: "Missing Required Skills",
        additionalSkills: "Additional Skills",
        experience: "Experience",
        experienceMatch: "Experience Match",
        education: "Education",
        educationMatch: "Education Match",
        overall: "Overall Match",
        skillsAnalysis: "Skills Analysis",
        aiSummary: "AI Summary",
        analyzedIn: "Analyzed in",
        analysisTime: "Analyzed in {{time}}ms",
        jobTitle: "Job Title",
        noResumes: "No resumes found. Create one first!",
        resumesLoadError: "Failed to load resumes",
        pleaseSelectResume: "Please select a resume",
        pleaseEnterJobDescription: "Please enter a job description",
        analysisComplete: "Analysis complete!",
        analysisFailed: "Failed to analyze job match",
        premiumFeature: "Premium Feature",
        premiumDesc: "AI Job Matcher with advanced analysis and recommendations is available for Basic, Premium, and Pro members.",
        unlockFeatures: "Unlock AI Job Matcher:",
        feature1: "AI-powered job matching analysis",
        feature2: "Detailed skill match breakdown",
        feature3: "Personalized recommendations",
        feature4: "Strengths and weaknesses analysis",
        feature5: "Experience and education scoring",
        upgradeNow: "Upgrade Now",
        backToDashboard: "Back to Dashboard",
        loading: "Loading match analysis...",
        totalYears: "years total",
        highestDegree: "highest degree"
      },
      paymentSuccess: {
        fetchError: "Unable to fetch subscription details",
        loadingMessage: "Processing your subscription...",
        title: "Payment Successful!",
        subtitle: "Welcome to your upgraded experience",
        errorDescription: "Your payment was successful, but we're having trouble loading your subscription details. Don't worry, your subscription is active!",
        subscriptionDetails: "Your Subscription Details",
        planLabel: "Plan",
        trialTitle: "Trial Period Active",
        trialRemaining: "You have {{days}} days remaining in your free trial",
        nextBillingDate: "Next billing date",
        cvGenerations: "CV Generations",
        perMonth: "per month",
        jobApplications: "Job Applications",
        confirmationTitle: "🎉 Confirmation email sent!",
        confirmationSubtitle: "Check your inbox for the receipt and subscription details.",
        thankYouTitle: "Thank you for your payment!",
        thankYouSubtitle: "Your subscription has been activated successfully.",
        continueButton: "Continue to Dashboard",
        sessionId: "Session ID",
        supportPrompt: "Need help? Contact our support team at",
        supportEmail: "support@cvlogs.com"
      },
      paymentFailed: {
        title: "Payment Cancelled",
        subtitle: "Your subscription was not activated",
        noChargeMessage: "Don't worry! No charges were made to your account.",
        cancellationReason: "Your payment was cancelled or interrupted. This can happen for various reasons:",
        reasonWindowClosed: "Payment window was closed before completion",
        reasonDeclined: "Payment method declined or insufficient funds",
        reasonNetwork: "Network connection interrupted",
        reasonChangedMind: "Changed your mind (that's okay!)",
        tryAgain: "Try Again",
        goHome: "Go Home",
        support: "Support",
        upgradeTitle: "Still interested in upgrading?",
        upgradeDescription: "Our premium plans unlock powerful features to accelerate your career journey. Choose a plan that fits your needs.",
        viewPlans: "View all plans",
        sessionId: "Session ID",
        needHelp: "Need help with payment?",
        contactSupport: "Contact Support: support@cvlogs.com",
        freePlanReminder: "You can still use CVLogs with our",
        freePlanLink: "free plan"
      },
      // Remote Jobs
      remoteJobs: {
        title: "Remote Jobs",
        subtitle: "remote positions from companies worldwide",
        loading: "Loading Remote Jobs...",
        searchPlaceholder: "Search jobs by title, company, or keywords...",
        filters: "Filters",
        search: "Search",
        searching: "Searching...",
        resetFilters: "Reset Filters",
        viewAllJobs: "View All Jobs",
        showing: "Showing",
        of: "of",
        jobs: "jobs",
        noJobsFound: "No Jobs Found",
        tryAdjusting: "Try adjusting your search criteria",
        loadMore: "Load More Jobs",
        remaining: "remaining",
        loading: "Loading...",
        fullTime: "Full-time",
        type: "Type",
        location: "Location",
        posted: "Posted",
        salary: "Salary",
        requiredSkills: "Required Skills",
        applyOnCompanySite: "Apply on Company Site",
        generateCoverLetterAI: "Generate Cover Letter with AI",
        saveToLibrary: "Save to Library",
        savedToLibrary: "Saved to Library",
        noDescription: "No detailed job description available. Click \"Apply\" to view more details on the company's website.",
        category: "Category",
        allCategories: "All Categories",
        jobType: "Job Type",
        allTypes: "All Types",
        location: "Location",
        locationPlaceholder: "e.g., USA, Europe, Worldwide",
        filters: "Filters",
        search: "Search",
        searching: "Searching...",
        resetFilters: "Reset Filters",
        noJobs: "No Jobs Found",
        noJobsDesc: "Try adjusting your search criteria",
        viewAllJobs: "View All Jobs",
        showing: "Showing",
        of: "of",
        jobs: "jobs",
        loadMore: "Load More Jobs",
        remaining: "remaining",
        loading: "Loading...",
        loadingJobs: "Loading Remote Jobs...",
        jobDescription: "Job Description",
        requiredSkills: "Required Skills",
        type: "Type",
        posted: "Posted",
        salary: "Salary",
        applyOnCompanySite: "Apply on Company Site",
        generateCoverLetterAI: "Generate Cover Letter with AI",
        saveToLibrary: "Save to Library",
        savedToLibrary: "Saved to Library",
        noDescriptionAvailable: "No detailed job description available. Click 'Apply' to view more details on the company's website.",
        proMatch: "Pro Match",
        analyzing: "Analyzing...",
        today: "Today",
        yesterday: "Yesterday",
        daysAgo: "days ago",
        weeksAgo: "weeks ago",
        createCV: "Create CV",
        selectResumeFirst: "Please create or select a resume to create CV",
        createCVTooltip: "Create CV for this job"
      },
      // Advanced Search
      advancedSearch: {
        title: "Advanced Job Search",
        subtitle: "Search for jobs with advanced filters and ranking",
        searchPlaceholder: "Search jobs by title, keywords, location...",
        history: "History",
        search: "Search",
        searching: "Searching...",
        enterSearchTerm: "Please enter a search query",
        location: "Location",
        locationPlaceholder: "e.g., Cairo, Egypt",
        filters: "Filters",
        search: "Search",
        searching: "Searching...",
        resetFilters: "Reset Filters",
        provider: "Provider",
        searchTerm: "Search Term",
        resultsWanted: "Results",
        daysOld: "Max Days Old",
        country: "Country",
        datePosted: "Date Posted",
        pages: "Pages",
        remoteJobsOnly: "Remote Jobs Only",
        fetchDescription: "Fetch Job Descriptions",
        history: "History",
        searchHistory: "Search History",
        noHistory: "No search history",
        loadedFromHistory: "Loaded search from history",
        refresh: "Refresh",
        noSearchToRefresh: "No search to refresh",
        unnamedSearch: "Unnamed Search",
        results: "results",
        showing: "Showing",
        noJobs: "No jobs found",
        noJobsDesc: "Try adjusting your search criteria",
        remote: "Remote",
        applyNow: "Apply Now",
        proMatch: "Pro Match",
        fullMatch: "Full Match",
        generateCoverLetter: "Generate Cover Letter",
        matchWith: "Match with:",
        analyzing: "Analyzing...",
        viewMatchAnalysis: "View Match Analysis",
        saveToLibrary: "Save to library",
        savedToLibrary: "Saved to library",
        saveJobSuccess: "Job saved to your library!",
        saveJobError: "Failed to save job",
        jobAlreadySaved: "This job is already in your library",
        searchSuccess: "Found {{count}} jobs",
        refreshSuccess: "Refreshed search found {{count}} jobs",
        loadError: "Failed to load search",
        searchError: "Failed to search jobs",
        refreshError: "Failed to refresh search",
        deleteConfirm: "Are you sure you want to delete this search?",
        searchDeleted: "Search deleted",
        deleteError: "Failed to delete search",
        delete: "Delete",
        selectResume: "Please select a resume first",
        noJobDescription: "No job description available for analysis",
        fullMatchSuccess: "Full Match analysis completed!",
        fullMatchError: "Failed to analyze job match",
        fullMatchUpgrade: "Full Match is available for Basic, Premium, and Pro users",
        createResumeFullMatch: "💡 Create a resume to use Full Match feature",
        premiumFeatureTitle: "Premium Feature",
        premiumFeatureDescription: "Advanced Job Search with powerful filters and AI-powered ranking is available for Basic and Premium members.",
        unlockHeading: "Unlock Advanced Search:",
        unlockItem1: "Search across 50+ countries",
        unlockItem2: "Advanced filters (date, location, remote)",
        unlockItem3: "AI-powered job ranking",
        unlockItem4: "Save searches and history",
        upgradeNow: "Upgrade Now",
        useBasicSearch: "Use Basic Search",
        createCV: "Create CV"
      },
      // Personal Info Form
      personalInfo: {
        title: "Personal Information",
        subtitle: "Tell us about yourself and how to contact you",
        form: {
          name: "Your Full Name",
          namePlaceholder: "e.g., John Doe",
          nameHelper: "English characters only",
          email: "Email",
          emailPlaceholder: "e.g., john.doe@example.com",
          phone: "Phone",
          phonePlaceholder: "e.g., +1 234 567 890",
          phoneHelper: "English characters only",
          city: "City",
          cityPlaceholder: "e.g., New York",
          cityHelper: "English characters only",
          country: "Country",
          countryPlaceholder: "e.g., United States",
          countryHelper: "Select your country",
          address: "Address",
          addressPlaceholder: "e.g., 123 Main St",
          addressHelper: "English characters only",
          professionalLinks: "Professional Links",
          links: "Professional Links",
          linksDescription: "Add links to your LinkedIn, GitHub, portfolio, or other professional profiles",
          linkedinUrl: "LinkedIn URL",
          linkedinPlaceholder: "https://www.linkedin.com/in/your-profile",
          githubUrl: "GitHub URL",
          githubPlaceholder: "https://github.com/your-username",
          portfolioUrl: "Portfolio/Website URL",
          portfolioPlaceholder: "https://your-portfolio.com",
          customLink: "Other Link (Label)",
          customLinkLabel: "Label (e.g., Behance, Twitter)",
          customLinkUrl: "URL",
          currentLinks: "Current Links",
          summary: "Professional Summary",
          summaryPlaceholder: "A brief professional summary about yourself...",
          summaryHelper: "Can be written in English or Arabic"
        }
      },
      // Education Form
      education: {
        title: "Education",
        subtitle: "Add your educational background",
        add: "Add Education",
        addNew: "Add New Education",
        edit: "Edit Education",
        form: {
          school: "School/University",
          degree: "Degree",
          major: "Field of Study",
          city: "City",
          country: "Country",
          startDate: "Start Date",
          endDate: "End Date"
        },
        organizationName: "Institution/University Name",
        organizationPlaceholder: "e.g., Harvard University",
        degree: "Degree",
        degreePlaceholder: "e.g., Bachelor of Science",
        major: "Field of Study/Major",
        majorPlaceholder: "e.g., Computer Science",
        city: "City",
        cityPlaceholder: "e.g., Cambridge",
        country: "Country",
        countryPlaceholder: "Select country",
        startDate: "Start Date",
        startDatePlaceholder: "Select start date",
        endDate: "End Date",
        endDatePlaceholder: "Select end date",
        save: "Save",
        cancel: "Cancel",
        delete: "Delete",
        noEntries: "No education entries yet",
        empty: "No education entries added yet",
        emptySubtitle: "Add your first education entry to get started",
        addFirst: "Add Your First Education"
      },
      // Experience Form
      experience: {
        title: "Work Experience",
        subtitle: "Add your work experience",
        add: "Add Experience",
        organization: "Company/Organization Name",
        organizationPlaceholder: "e.g., Google Inc.",
        jobTitle: "Job Title",
        jobTitlePlaceholder: "e.g., Software Engineer",
        city: "City",
        cityPlaceholder: "e.g., Mountain View",
        country: "Country",
        countryPlaceholder: "e.g., United States",
        workType: "Work Type",
        fulltime: "Full-time",
        parttime: "Part-time",
        intern: "Internship",
        freelance: "Freelance",
        remote: "Remote",
        other: "Other",
        startDate: "Start Date",
        endDate: "End Date",
        description: "Job Description",
        descriptionPlaceholder: "Describe your responsibilities and achievements...",
        present: "Present",
        save: "Save",
        cancel: "Cancel",
        edit: "Edit",
        delete: "Delete",
        noEntries: "No experience entries yet",
        empty: "No experience added yet",
        emptySubtitle: "Add your first work experience to get started",
        addFirst: "Add Your First Experience"
      },
      // Skills Form
      skills: {
        title: "Skills",
        subtitle: "Add your skills and competencies",
        add: "Add Skill",
        addNew: "Add New Skill",
        edit: "Edit Skill",
        name: "Skill Name",
        namePlaceholder: "e.g., JavaScript, Project Management",
        type: "Skill Type",
        technical: "Technical",
        soft: "Soft",
        other: "Other",
        save: "Save",
        cancel: "Cancel",
        delete: "Delete",
        noEntries: "No skills added yet",
        empty: "No skills added yet",
        emptySubtitle: "Add your first skill to get started",
        addFirst: "Add Your First Skill"
      },
      // Languages Form
      languages: {
        title: "Languages",
        subtitle: "Add languages you speak",
        add: "Add Language",
        addNew: "Add New Language",
        edit: "Edit Language",
        languageName: "Language",
        languagePlaceholder: "e.g., English, Spanish, Arabic",
        proficiencyLevel: "Proficiency Level",
        levels: {
          native: "Native",
          fluent: "Fluent",
          advanced: "Advanced",
          intermediate: "Intermediate",
          beginner: "Beginner"
        },
        save: "Save",
        cancel: "Cancel",
        delete: "Delete",
        noEntries: "No languages added yet",
        empty: "No languages added yet",
        emptySubtitle: "Add your first language to get started",
        addFirst: "Add Your First Language"
      },
      // Projects Form
      projects: {
        title: "Projects",
        subtitle: "Showcase your best work",
        add: "Add Project",
        addNew: "Add New Project",
        edit: "Edit Project",
        projectTitle: "Project Title",
        titlePlaceholder: "e.g., Online Store Platform",
        technologies: "Technologies Used",
        technologiesPlaceholder: "e.g., Web Development, Database Management",
        description: "Description",
        descriptionPlaceholder: "Describe your project, its purpose, and your role...",
        links: "Links",
        linkName: "Link Name",
        linkNamePlaceholder: "e.g., Website",
        linkUrl: "Link URL",
        linkUrlPlaceholder: "https://...",
        link: "Project Link",
        linkPlaceholder: "https://...",
        save: "Save",
        cancel: "Cancel",
        delete: "Delete",
        noEntries: "No projects added yet",
        empty: "No projects added yet",
        emptySubtitle: "Add your first project to get started",
        addFirst: "Add Your First Project"
      },
      // Training Form
      training: {
        title: "Training & Courses",
        subtitle: "Add your training courses and certifications",
        add: "Add Training",
        addNew: "Add New Training Course",
        edit: "Edit Training Course",
        courseTitle: "Course Title",
        titlePlaceholder: "e.g., Advanced Web Development",
        organization: "Training Organization",
        organizationPlaceholder: "e.g., Udemy, Coursera",
        date: "Completion Date",
        datePlaceholder: "Select completion date",
        certificateLink: "Certificate Link",
        linkPlaceholder: "https://...",
        save: "Save",
        cancel: "Cancel",
        delete: "Delete",
        noEntries: "No training added yet",
        empty: "No training added yet",
        emptySubtitle: "Add your first training course to get started",
        addFirst: "Add Your First Training Course"
      },
      // Publications Form
      publications: {
        title: "Publications",
        subtitle: "List your published works",
        add: "Add Publication",
        addNew: "Add New Publication",
        edit: "Edit Publication",
        title_field: "Publication Title",
        titlePlaceholder: "e.g., Research Paper on AI",
        publisher: "Publisher",
        publisherPlaceholder: "e.g., IEEE",
        publicationDate: "Publication Date",
        publicationDatePlaceholder: "Select publication date",
        link: "Link",
        linkPlaceholder: "https://...",
        save: "Save",
        cancel: "Cancel",
        delete: "Delete",
        noEntries: "No publications added yet",
        empty: "No publications added yet",
        emptySubtitle: "Add your first publication to get started",
        addFirst: "Add Your First Publication"
      },
      // Licenses Form
      licenses: {
        title: "Licenses & Certifications",
        subtitle: "Add your professional certifications",
        add: "Add License",
        addNew: "Add New License",
        edit: "Edit License",
        form: {
          title: "License/Certification Title",
          titlePlaceholder: "e.g., AWS Certified Solutions Architect",
          name: "License/Certification Name",
          namePlaceholder: "e.g., AWS Certified Solutions Architect",
          issuer: "Issuing Organization",
          issuerPlaceholder: "e.g., Amazon Web Services",
          issueDate: "Issue Date",
          issueDatePlaceholder: "Select issue date",
          expiryDate: "Expiry Date",
          expiryDatePlaceholder: "Select expiry date",
          credentialId: "Credential ID",
          credentialIdPlaceholder: "e.g., ABC123XYZ",
          credentialUrl: "Credential URL",
          credentialUrlPlaceholder: "https://..."
        },
        save: "Save",
        cancel: "Cancel",
        delete: "Delete",
        noEntries: "No licenses added yet",
        empty: "No licenses added yet",
        emptySubtitle: "Add your first license or certification to get started",
        addFirst: "Add Your First License"
      },
      // Awards Form
      awards: {
        title: "Awards & Achievements",
        subtitle: "Highlight your accomplishments",
        add: "Add Award",
        addNew: "Add New Award",
        edit: "Edit Award",
        form: {
          title: "Award Title",
          titlePlaceholder: "e.g., Employee of the Year",
          organization: "Issuing Organization",
          organizationPlaceholder: "e.g., ABC Company",
          dateReceived: "Date Received",
          dateReceivedPlaceholder: "Select date received",
          link: "Award URL",
          linkPlaceholder: "https://...",
          description: "Description",
          descriptionPlaceholder: "Describe the achievement..."
        },
        save: "Save",
        cancel: "Cancel",
        delete: "Delete",
        noEntries: "No awards added yet",
        empty: "No awards added yet",
        emptySubtitle: "Add your first award to get started",
        addFirst: "Add Your First Award"
      },
      // Volunteer Form
      volunteer: {
        title: "Volunteer Experience",
        subtitle: "Share your volunteer work",
        add: "Add Volunteer Experience",
        addNew: "Add New Volunteer Experience",
        edit: "Edit Volunteer Experience",
        organization: "Organization",
        organizationPlaceholder: "e.g., Red Cross",
        role: "Role",
        rolePlaceholder: "e.g., Community Organizer",
        link: "Organization Link",
        linkPlaceholder: "https://...",
        startDate: "Start Date",
        startDatePlaceholder: "Select start date",
        endDate: "End Date",
        endDatePlaceholder: "Select end date",
        description: "Description",
        descriptionPlaceholder: "Describe your volunteer work...",
        save: "Save",
        cancel: "Cancel",
        delete: "Delete",
        noEntries: "No volunteer experience added yet",
        empty: "No volunteer experience added yet",
        emptySubtitle: "Add your first volunteer experience to get started",
        addFirst: "Add Your First Volunteer Experience"
      },
      // Recommendations Form
      recommendations: {
        title: "Recommendations",
        subtitle: "Add professional recommendations",
        add: "Add Recommendation",
        addNew: "Add New Recommendation",
        edit: "Edit Recommendation",
        name: "Recommender Name",
        namePlaceholder: "e.g., John Smith",
        position: "Position",
        positionPlaceholder: "e.g., Senior Manager",
        company: "Company",
        companyPlaceholder: "e.g., ABC Corp",
        relationship: "Relationship",
        relationshipPlaceholder: "e.g., Direct Manager",
        text: "Recommendation Text",
        textPlaceholder: "Enter the recommendation...",
        save: "Save",
        cancel: "Cancel",
        delete: "Delete",
        noEntries: "No recommendations added yet",
        empty: "No recommendations added yet",
        emptySubtitle: "Add your first recommendation to get started",
        addFirst: "Add Your First Recommendation"
      },
      // Interests Form
      interests: {
        title: "Interests",
        subtitle: "Share your hobbies and interests",
        add: "Add Interest",
        addNew: "Add New Interest",
        edit: "Edit Interest",
        name: "Interest",
        interestName: "Interest/Hobby",
        namePlaceholder: "e.g., Photography, Reading, Hiking",
        save: "Save",
        cancel: "Cancel",
        delete: "Delete",
        noEntries: "No interests added yet",
        empty: "No interests added yet",
        emptySubtitle: "Add your first interest to get started",
        addFirst: "Add Your First Interest"
      },
      // Common Form Actions
      formActions: {
        save: "Save",
        cancel: "Cancel",
        edit: "Edit",
        delete: "Delete",
        add: "Add",
        update: "Update",
        close: "Close",
        submit: "Submit"
      },
      // Success Messages
      success: {
        saved: "Saved successfully",
        updated: "Updated successfully",
        deleted: "Deleted successfully",
        added: "Added successfully",
        educationAdded: "Education added successfully",
        educationUpdated: "Education updated successfully",
        educationDeleted: "Education deleted successfully",
        experienceAdded: "Experience added successfully",
        experienceUpdated: "Experience updated successfully",
        experienceDeleted: "Experience deleted successfully",
        skillAdded: "Skill added successfully",
        skillUpdated: "Skill updated successfully",
        skillDeleted: "Skill deleted successfully"
      },
      // Confirmation Messages
      confirm: {
        delete: "Are you sure you want to delete this entry?",
        deleteEducation: "Are you sure you want to delete this education entry?",
        deleteExperience: "Are you sure you want to delete this experience entry?",
        deleteSkill: "Are you sure you want to delete this skill?"
      },
      // Usage
      usage: {
        resumes: "Resumes",
        aiGenerations: "AI Enhancements",
        coverLetters: "Cover Letters",
        chatMessages: "Chat Messages (Today)",
        noDataAvailable: "Usage data not available",
        loading: "Loading plans..."
      },
      // Chatbot
      chatbot: {
        title: "AI Career Assistant",
        subtitle: "Ask me anything about your career",
        welcomeTitle: "Hello! How can I help you today?",
        welcomeMessage: "I'm here to assist you with your resume, cover letters, job search, and career advice. Ask me anything!",
        newChat: "New Chat",
        sessions: "Chat History",
        session: "Chat",
        messages: "Messages",
        noSessions: "No chat history yet",
        inputPlaceholder: "Type your message...",
        inputHint: "Press Enter to send, Shift + Enter for new line",
        suggestion1: "How can I improve my resume?",
        suggestion2: "Help me write a cover letter",
        suggestion3: "What skills should I add?",
        suggestion4: "Tips for job interviews",
        errorLoadingSessions: "Failed to load chat history",
        errorLoadingHistory: "Failed to load conversation",
        errorCreatingSession: "Failed to create new chat",
        errorDeletingSession: "Failed to delete chat",
        errorSendingMessage: "Failed to send message",
        newSessionCreated: "New chat created successfully",
        sessionDeleted: "Chat deleted successfully",
        featureResume: "Resume Review",
        featureInterview: "Interview Prep",
        featureCoverLetter: "Cover Letter",
        featureCareer: "Career Advice",
        tryAsking: "Try asking",
        prompt1: "Review my resume",
        prompt2: "Help me prepare for an interview",
        prompt3: "Write a cover letter for me",
        prompt4: "What skills should I learn?"
      }
    }
  },
  ar: {
    translation: {
      // Actions (root level for easy access across all components)
      actions: {
        add: "إضافة",
        edit: "تعديل",
        delete: "حذف",
        remove: "إزالة",
        cancel: "إلغاء",
        confirm: "تأكيد",
        save: "حفظ",
        saving: "جاري الحفظ..."
      },
      // Validation (root level for easy access across all components)
      validation: {
        required: "{{field}} مطلوب",
        nameRequired: "الاسم مطلوب",
        emailRequired: "البريد الإلكتروني مطلوب",
        email: "يرجى إدخال عنوان بريد إلكتروني صحيح",
        invalidEmail: "يرجى إدخال عنوان بريد إلكتروني صحيح",
        url: "يرجى إدخال رابط صحيح",
        invalidUrl: "يرجى إدخال رابط صحيح",
        invalidUrlField: "{{field}} يجب أن يكون رابط صالح (مثال: https://example.com)",
        phone: "يرجى إدخال رقم هاتف صحيح",
        minLength: "مطلوب {min} أحرف كحد أدنى",
        maxLength: "مسموح {max} أحرف كحد أقصى",
        pleaseFixErrors: "يرجى إصلاح الأخطاء:",
        correctFollowingErrors: "يرجى تصحيح الأخطاء التالية:",
        andMoreErrors: "+{{count}} خطأ إضافي",
        englishOnly: "يرجى استخدام أحرف إنجليزية فقط",
        englishOnlyField: "{{field}} يجب أن يكون بالإنجليزية فقط",
        bilingualField: "{{field}} يجب أن يكون بالإنجليزية أو العربية فقط",
        invalidCharacters: "يحتوي على أحرف غير صالحة",
        invalidCharsField: "{{field}} يحتوي على أحرف غير صالحة",
        noFutureDate: "{{field}} لا يمكن أن يكون في المستقبل",
        futureDateField: "{{field}} لا يمكن أن يكون تاريخ مستقبلي",
        dateRange: "تاريخ البدء يجب أن يكون قبل تاريخ الانتهاء",
        startBeforeEnd: "تاريخ البدء يجب أن يكون قبل تاريخ الانتهاء",
        linkRequired: "اسم الرابط و URL مطلوبان معًا"
      },
      // Navigation
      nav: {
        home: "الرئيسية",
        cvBuilder: "بناء السيرة الذاتية",
        jobSearch: "البحث عن وظائف",
        remoteJobs: "وظائف عن بعد",
        aiAssistant: "المساعد الذكي",
        services: "خدماتنا",
        about: "من نحن",
        contact: "اتصل بنا",
        login: "تسجيل الدخول",
        register: "ابدأ الآن",
        logout: "تسجيل الخروج",
        profile: "الملف الشخصي",
        myCV: "سيرتي الذاتية",
        settings: "الإعدادات"
      },
      // Dashboard
      dashboard: {
        welcome: "مرحباً بعودتك",
        subtitle: "إليك ما يحدث مع بحثك عن وظيفة اليوم",
        errorLoading: "فشل تحميل بيانات لوحة التحكم",
        loading: "جاري تحميل لوحة التحكم...",
        loadingJobs: "جاري تحميل الوظائف...",
        totalResumes: "إجمالي السير الذاتية",
        savedJobs: "الوظائف المحفوظة",
        applications: "التطبيقات",
        jobsApplied: "طلبات الوظائف المقدمة",
        jobSearches: "عمليات البحث",
        activityInsights: "نشاطك ورؤىك",
        activityInsightsDesc: "تتبع تقدمك وأدائك",
        momentumTitle: "الزخم الأسبوعي",
        momentumDesc: "مؤشر يجمع نشاطك الأخير",
        focusTitle: "أفضل الخطوات التالية",
        focusDesc: "تحركات سريعة للحفاظ على تقدمك",
        timelineTitle: "الخط الزمني الحديث",
        timelineEmpty: "لا يوجد نشاط بعد — ابدأ باستخدام الأدوات لرؤية الرؤى هنا",
        resumeUpdated: "تم تحديث السيرة الذاتية",
        jobSaved: "تم حفظ وظيفة",
        searchTriggered: "تم إجراء بحث عن وظيفة",
        aiReady: "جاهز للتحسين بالذكاء الاصطناعي",
        createFirstInsight: "أنشئ سيرتك الأولى لفتح الرؤى",
        focusEnhance: "حسّن أفضل سيرة ذاتية لديك بالذكاء الاصطناعي",
        focusApply: "قدّم على وظيفتين مستهدفتين",
        focusSearch: "حدّث عمليات البحث عن الوظائف",
        momentumHigh: "وتيرة رائعة! استمر في السلسلة.",
        thisMonth: "هذا الشهر",
        newApplications: "طلبات جديدة",
        recentActivity: "النشاط الأخير",
        last7Days: "آخر 7 أيام",
        profileStrength: "قوة الملف",
        complete: "مكتمل",
        quickActions: "إجراءات سريعة",
        createResume: "إنشاء سيرة ذاتية",
        searchJobs: "البحث عن وظائف",
        aiEnhancement: "التحسين بالذكاء الاصطناعي",
        remoteJobs: "وظائف عن بعد",
        recentResumes: "السير الذاتية الأخيرة",
        savedJobsList: "الوظائف المحفوظة",
        remoteJobsTitle: "استكشف فرص العمل عن بعد",
        remoteJobsDesc: "اعمل من أي مكان في العالم",
        exploreRemote: "استكشف جميع الوظائف عن بعد",
        viewAll: "عرض الكل",
        noResumes: "لا توجد سير ذاتية بعد",
        createFirst: "أنشئ سيرتك الذاتية الأولى",
        noJobs: "لا توجد وظائف محفوظة بعد",
        noRemoteJobs: "لا توجد وظائف عن بعد متاحة",
        startSearching: "ابدأ البحث عن وظائف",
        applicationProgress: "تقدم التطبيقات",
        totalSaved: "إجمالي المحفوظة",
        applied: "تم التقديم",
        interviewing: "مقابلات",
        offers: "عروض",
        successStories: "قصص النجاح",
        joinThousands: "انضم للآلاف",
        testimonial1: "وجدت وظيفة أحلامي في أسبوعين!",
        users: "المستخدمون",
        jobsLanded: "الوظائف",
        rating: "التقييم",
        usersHired: "مستخدم حصل على وظيفة هذا الشهر",
        avgTime: "متوسط الوقت للمقابلة",
        days: "أيام",
        satisfactionRate: "رضا المستخدمين",
        upgradeTitle: "اطلق إمكانياتك المهنية",
        upgradeDesc: "ترقية إلى Pro واحصل على سير ذاتية غير محدودة وتحسينات AI ودعم أولوية",
        upgradeCTA: "الترقية إلى Pro",
        upgradePro: "الترقية إلى برو",
        unlockFeatures: "فتح جميع الميزات المميزة",
        unlimitedCV: "سير ذاتية غير محدودة",
        advancedAI: "ذكاء اصطناعي متقدم",
        prioritySupport: "دعم أولوية",
        upgradeNow: "ترقية الآن",
        proFeature1: "سير ذاتية غير محدودة",
        proFeature2: "تحسين متقدم بالذكاء الاصطناعي",
        proFeature3: "دعم أولوية على مدار الساعة",
        proFeature4: "قوالب مميزة",
        selectCV: "اختر سيرة ذاتية للتحسين بالذكاء الاصطناعي",
        selectCVDesc: "اختر السيرة الذاتية التي تريد تحسينها بالذكاء الاصطناعي",
        noResumesForAI: "لا توجد سير ذاتية متاحة",
        createCVFirst: "أنشئ سيرة ذاتية أولاً لاستخدام التحسين بالذكاء الاصطناعي",
        aiMatching: "مطابقة الوظائف بالذكاء الاصطناعي",
        aiMatchingDesc: "توصيات ذكية",
        tryNow: "جرب الآن",
        explore: "استكشف",
        aiCoverLetter: "رسائل تغطية بالذكاء الاصطناعي",
        aiCoverLetterDesc: "احترافية في ثوانٍ",
        generate: "إنشاء",
        trackProgress: "تتبع تقدمك",
        active: "نشط",
        trackJourney: "تتبع رحلة طلبات الوظيفة",
        loadingJobs: "جارٍ تحميل الوظائف...",
        loadingRemoteJobs: "جارٍ تحميل الوظائف عن بعد...",
        moreRemoteOpportunities: "فرص عمل عن بعد إضافية",
        moreSavedJobs: "وظائف محفوظة إضافية",
        jobSearch: "البحث عن وظائف",
        advancedSearch: "بحث متقدم",
        searchWorldwide: "البحث عن وظائف في جميع أنحاء العالم",
        advancedSearchDesc: "تصفية بالخيارات المتقدمة"
      },
      // Welcome Banner
      welcomeBanner: {
        heading: "مساحة عملك المهنية",
        description: "أنشئ سيرتك الذاتية، اعثر على وظائف مناسبة، وولّد رسائل تغطية مخصصة بالذكاء الاصطناعي.",
        createResume: "أنشئ سيرتك الأولى",
        searchJobs: "ابحث عن وظيفة",
        exploreAI: "استكشف مميزات الذكاء الاصطناعي"
      },
      // Onboarding Checklist
      onboarding: {
        title: "قائمة البدء",
        stepsRemaining_one: "{{count}} خطوة متبقية",
        stepsRemaining_other: "{{count}} خطوات متبقية",
        allDone: "أحسنت! تم الإنجاز 🎉",
        step_resume: "أنشئ سيرتك الذاتية الأولى",
        step_resume_desc: "ابنِ سيرة ذاتية جاهزة لـ ATS باستخدام أداة البناء الذكية",
        step_resume_cta: "إنشاء سيرة ذاتية",
        step_search: "ابحث عن وظيفة",
        step_search_desc: "اعثر على الوظائف المناسبة واحفظ التي تهمك",
        step_search_cta: "بحث عن وظائف",
        step_match: "شغّل مطابقة الوظيفة بالذكاء الاصطناعي",
        step_match_desc: "دع الذكاء الاصطناعي يقارن سيرتك الذاتية بوصف الوظيفة",
        step_match_cta: "مطابقة الآن",
        step_cover: "أنشئ رسالة تغطية",
        step_cover_desc: "أنشئ رسالة تغطية مخصصة بنقرة واحدة",
        step_cover_cta: "إنشاء"
      },
      // Auth
      auth: {
        welcomeBack: "مرحباً بعودتك",
        signInDesc: "سجل الدخول إلى حسابك للمتابعة",
        emailAddress: "البريد الإلكتروني",
        enterEmail: "أدخل بريدك الإلكتروني",
        password: "كلمة المرور",
        enterPassword: "أدخل كلمة المرور",
        signIn: "تسجيل الدخول",
        signingIn: "جاري تسجيل الدخول...",
        orContinueWith: "أو المتابعة باستخدام",
        googleSignInFailed: "فشل تسجيل الدخول باستخدام جوجل",
        forgotPassword: "نسيت كلمة المرور؟",
        createAccount: "إنشاء حساب",
        signUp: "إنشاء حساب",
        signingUp: "جاري إنشاء الحساب...",
        alreadyHaveAccount: "لديك حساب بالفعل؟",
        dontHaveAccount: "ليس لديك حساب؟",
        firstName: "الاسم الأول",
        firstNamePlaceholder: "الاسم الأول",
        lastName: "اسم العائلة",
        lastNamePlaceholder: "اسم العائلة",
        confirmPassword: "تأكيد كلمة المرور",
        enterConfirmPassword: "أعد إدخال كلمة المرور",
        passwordsDontMatch: "كلمات المرور غير متطابقة",
        accountCreated: "تم إنشاء الحساب بنجاح!",
        resetEmailSent: "تم إرسال رسالة إعادة تعيين كلمة المرور! تحقق من بريدك الإلكتروني.",
        resetEmailSentTitle: "تحقق من بريدك الإلكتروني",
        resetEmailSentDesc: "لقد أرسلنا رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني. يرجى التحقق من بريدك الوارد واتباع التعليمات لإعادة تعيين كلمة المرور.",
        didntReceiveEmail: "لم تستلم الرسالة؟",
        resendEmail: "إعادة إرسال الرسالة",
        backToLogin: "العودة إلى تسجيل الدخول",
        email: "البريد الإلكتروني",
        emailPlaceholder: "أدخل بريدك الإلكتروني",
        sending: "جاري الإرسال...",
        sendResetLink: "إرسال رابط إعادة التعيين",
        resetInstructions: "أدخل بريدك الإلكتروني وسنرسل لك رابط لإعادة تعيين كلمة المرور.",
        // Email verification
        verifyEmailTitle: "تأكيد بريدك الإلكتروني",
        verifyEmailDesc: "لقد أرسلنا رابط تأكيد إلى بريدك الإلكتروني. يرجى التحقق من بريدك الوارد والنقر على الرابط لتأكيد حسابك.",
        verificationEmailSent: "تم إرسال رسالة التأكيد! تحقق من بريدك الوارد.",
        emailVerified: "تم تأكيد البريد الإلكتروني بنجاح!",
        emailVerifiedTitle: "تم تأكيد البريد الإلكتروني!",
        emailVerifiedDesc: "تم تأكيد بريدك الإلكتروني. جاري تحويلك إلى لوحة التحكم...",
        emailNotVerifiedYet: "لم يتم تأكيد البريد الإلكتروني بعد. يرجى التحقق من بريدك الوارد.",
        iVerifiedMyEmail: "لقد أكدت بريدي الإلكتروني",
        resendVerificationEmail: "إرسال رسالة التأكيد",
        checkSpamFolder: "لا تجد الرسالة؟ تحقق من مجلد الرسائل غير المرغوب فيها.",
        checking: "جاري التحقق...",
        redirecting: "جاري التحويل...",
        autoCheckingVerification: "جاري التحقق من حالة التأكيد تلقائياً..."
      },
      // Hero Section
      hero: {
        title: "أنشئ سيرتك الذاتية الاحترافية",
        subtitle: "تميز عن الآخرين بسيرة ذاتية ورسالة تغطية مصممة بشكل احترافي",
        getStarted: "ابدأ الآن",
        aiPowered: "منشئ السيرة الذاتية بالذكاء الاصطناعي",
        landDreamJob: "احصل على وظيفة أحلامك",
        landDreamJobDesc: "أنشئ سيرة ذاتية مهنية تلفت انتباه مسؤولي التوظيف ومديري الموارد البشرية. تقنيتنا الذكية تحسن سيرتك للحصول على أقصى تأثير وتوافق مع أنظمة ATS.",
        startBuilding: "ابدأ الإنشاء الآن",
        seeStories: "قصص النجاح",
        instantUpgrade: "تطوير فوري للسيرة الذاتية",
        transformCV: "حول سيرتك الذاتية الحالية",
        transformCVDesc: "ارفع سيرتك الذاتية الحالية وشاهد الذكاء الاصطناعي يطورها فوراً بتنسيق مهني وتحسين الكلمات المفتاحية والتطويرات الخاصة بالمجال.",
        uploadUpgrade: "ارفع وطور",
        previewTransforms: "معاينة التحولات",
        smartCreation: "إنشاء سيرة ذاتية ذكية",
        buildPerfect: "ابن سيرتك الذاتية المثالية",
        buildPerfectDesc: "استبياننا الذكي يرشدك خلال إنشاء سيرة ذاتية مقنعة تُظهر قيمتك الفريدة لأصحاب العمل.",
        startSmart: "ابدأ المنشئ الذكي",
        tryDemo: "جرب العرض التفاعلي"
      },
      // Features
      features: {
        title: "لماذا تختار منشئ السيرة الذاتية الخاص بنا؟",
        subtitle: "كل ما تحتاجه لإنشاء سيرة ذاتية احترافية",
        templates: "قوالب احترافية",
        customize: "سهولة التخصيص",
        download: "تحميل فوري",
        templatesDesc: "اختر من عشرات القوالب المتوافقة مع أنظمة التوظيف والمصممة من قبل خبراء التوظيف",
        customizeDesc: "خصص سيرتك الذاتية بسهولة باستخدام محررنا البديهي",
        downloadDesc: "حمل سيرتك الذاتية بصيغة PDF أو DOCX فوراً",
        advancedBuilder: "منشئ السيرة الذاتية المتقدم",
        advancedBuilderDesc: "أنشئ سيرة ذاتية احترافية بقوالب حديثة وتخصيص كامل",
        aiChat: "المحادثة الذكية",
        aiChatDesc: "احصل على نصائح مخصصة لتحسين سيرتك الذاتية من الذكاء الاصطناعي",
        multiExport: "تصدير متعدد الأشكال",
        multiExportDesc: "احفظ سيرتك الذاتية بصيغة PDF أو Word أو HTML",
        autoEnhancement: "تحسين تلقائي",
        autoEnhancementDesc: "تحسين المحتوى تلقائياً وتحسين التسويق البحثي",
        millionsUsers: "ملايين المستخدمين",
        millionsUsersDesc: "انضم إلى ملايين المهنيين الذين يؤمنون بخدماتنا",
        securityPrivacy: "أمان وخصوصية",
        securityPrivacyDesc: "بياناتك محمية بأحدث أمان وتشفير",
        optimization: "تحسين ذكي",
        optimizationDesc: "اقتراحات محتوى مدعومة بالذكاء الاصطناعي وتحسين الكلمات المفتاحية لرؤية أفضل",
        atsOptimized: "متوافق مع أنظمة ATS",
        atsOptimizedDesc: "قوالبنا مصممة لأنظمة تتبع المتقدمين المستخدمة في الشركات الرئيسية",
        bilingualSupport: "الدعم العربي والإنجليزي",
        bilingualSupportDesc: "أنشئ سيرات ذاتية في اللغتين الإنجليزية والعربية مع دعم RTL صحيح وتكيفات ثقافية",
        aiSuggestions: "توصيات بالذكاء الاصطناعي",
        aiSuggestionsDesc: "احصل على توصيات ذكية للمحتوى، والتنسيق، والكلمات المفتاحية لجعل سيرتك ذاتية عالية الجودة"
      },
      // Journey Steps
      journey: {
        title: "كيف يعمل",
        subtitle: "اتبع عمليتنا البسيطة المكونة من 4 خطوات لإنشاء سيرتك الذاتية المثالية",
        step1: "اختر طريقتك",
        step1Desc: "ارفع سيرتك الحالية أو ابدأ من الصفر مع منشئنا خطوة بخطوة",
        step2: "نموذج ذكي أو استخراج بالذكاء الاصطناعي",
        step2Desc: "أجب على أسئلة ذكية أو دع الذكاء الاصطناعي يستخرج البيانات من سيرتك",
        step3: "خصص واستعرض",
        step3Desc: "عدّل المحتوى، اختر القوالب، واستعرض سيرتك الذاتية الاحترافية",
        step4: "حمّل وشارك",
        step4Desc: "صدّر بتنسيقات متعددة وابدأ في التقديم لوظائف أحلامك",
        getStarted: "ابدأ الآن",
        tryAI: "جرب المحادثة الذكية",
        previewDemo: "معاينة تجريبية",
        exportOptions: "خيارات التصدير"
      },
      // CV Builder
      cvBuilder: {
        title: "بناء السيرة الذاتية",
        chooseLanguage: "اختر اللغة",
        uploadExisting: "رفع سيرة ذاتية موجودة",
        startFromScratch: "البدء من الصفر",
        uploadDescription: "ارفع سيرتك الذاتية الموجودة (PDF أو Word) وسنستخرج البيانات لك",
        scratchDescription: "أجب على أسئلة بسيطة وسنبني سيرتك الذاتية خطوة بخطوة",
        continue: "متابعة",
        back: "السابق", 
        next: "التالي",
        skip: "تخطي",
        done: "تم",
        preview: "معاينة",
        download: "تحميل",
        edit: "تعديل",
        chooseTemplate: {
          title: "بناء السيرة الذاتية - اختر القالب | CvLogs",
          description: "اختر من قوالب السيرة الذاتية الاحترافية وابدأ في بناء سيرتك الذاتية",
          heading: "اختر قالب سيرتك الذاتية",
          subtitle: "اختر قالب احترافي يمثل أفضل نمطك",
          templates: "قوالب احترافية",
          recent: "سيرتي حديثة",
          upload: "رفع سيرة ذاتية موجودة",
          uploadDesc: "استيراد سيرتك الذاتية الموجودة وتحسينها بأدواتنا",
          uploadButton: "رفع سيرة ذاتية",
          startFrom: "بدء من الصفر",
          startFromDesc: "ابدء بقالب فارغ وبني سيرتك الذاتية خطوة بخطوة",
          startBuilding: "ابدأ البناء",
          preview: "معاينة",
          select: "اختر القالب",
          popular: "شائع",
          noRecent: "لا توجد سيرتي حديثة بعد",
          createFirst: "أنشئ سيرتك الأولى لرؤيتها هنا",
          createFirstCV: "أنشئ سيرتك الأولى",
          lastUpdated: "آخر تحديث",
          continueEditing: "استمر في التعديل"
        },
        templates: {
          modern: {
            name: "عصري احترافي",
            description: "تصميم نظيف وعصري مثالي للأدوار التقنية والإبداعية"
          },
          classic: {
            name: "تنفيذي كلاسيكي", 
            description: "تخطيط تقليدي مثالي للمناصب الشركات والتنفيذية"
          },
          creative: {
            name: "مصمم إبداعي",
            description: "تصميم جريء وفني للمحترفين المبدعين"
          },
          minimalist: {
            name: "بسيط ونظيف",
            description: "تصميم بسيط وأنيق يركز على المحتوى"
          }
        }
      },
      // Steps
      steps: {
        personalInfo: "المعلومات الشخصية",
        education: "التعليم",
        experience: "الخبرة العملية",
        projects: "المشاريع",
        courses: "التدريب والشهادات",
        skills: "المهارات",
        review: "المراجعة والتحميل"
      },
      // Auth Pages
      auth: {
        // Login
        welcomeBack: "مرحباً بعودتك",
        signInDesc: "سجل الدخول إلى حسابك لمتابعة بناء سير ذاتية مذهلة",
        emailAddress: "عنوان البريد الإلكتروني",
        password: "كلمة المرور",
        enterEmail: "أدخل بريدك الإلكتروني",
        enterPassword: "أدخل كلمة المرور",
        forgotPassword: "نسيت كلمة المرور؟",
        signIn: "تسجيل الدخول",
        signingIn: "جاري تسجيل الدخول...",
        orContinueWith: "أو تابع مع",
        signInGoogle: "تسجيل الدخول بجوجل",
        connectingGoogle: "جاري الاتصال بجوجل...",
        noAccount: "ليس لديك حساب؟",
        signUpNow: "سجل الآن",
        
        // Register
        getStarted: "ابدأ الآن",
        createAccount: "أنشئ حسابك وابدأ في بناء سير ذاتية احترافية",
        firstName: "الاسم الأول",
        firstNamePlaceholder: "الاسم الأول",
        lastName: "اسم العائلة",
        lastNamePlaceholder: "اسم العائلة",
        fullName: "الاسم الكامل",
        enterFullName: "أدخل اسمك الكامل",
        confirmPassword: "تأكيد كلمة المرور",
        confirmPasswordPlaceholder: "أكد كلمة المرور",
        createAccountBtn: "إنشاء حساب",
        creatingAccount: "جاري إنشاء الحساب...",
        signUpGoogle: "التسجيل بجوجل",
        hasAccount: "لديك حساب بالفعل؟",
        signInNow: "سجل دخول الآن",
        
        // Common
        email: "البريد الإلكتروني",
        name: "الاسم",
        // Forgot Password & Reset
        forgotPasswordTitle: "نسيت كلمة المرور؟",
        resetInstructions: "أدخل بريدك الإلكتروني وسنرسل لك رابط لإعادة تعيين كلمة المرور.",
        emailPlaceholder: "أدخل بريدك الإلكتروني",
        sending: "جاري الإرسال...",
        sendResetLink: "إرسال رابط إعادة التعيين",
        backToLogin: "العودة إلى تسجيل الدخول",
        resetEmailSent: "تم إرسال رسالة إعادة تعيين كلمة المرور! تحقق من بريدك الإلكتروني.",
        resetEmailSentTitle: "تحقق من بريدك الإلكتروني",
        resetEmailSentDesc: "لقد أرسلنا رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني. يرجى التحقق من بريدك الوارد واتباع التعليمات لإعادة تعيين كلمة المرور.",
        didntReceiveEmail: "لم تستلم الرسالة؟",
        resendEmail: "إعادة إرسال الرسالة",
        // Email Verification
        verifyEmailTitle: "تأكيد بريدك الإلكتروني",
        verifyEmailDesc: "لقد أرسلنا رابط تأكيد إلى بريدك الإلكتروني. يرجى التحقق من بريدك الوارد والنقر على الرابط لتأكيد حسابك.",
        verificationEmailSent: "تم إرسال رسالة التأكيد! تحقق من بريدك الوارد.",
        emailVerified: "تم تأكيد البريد الإلكتروني بنجاح!",
        emailVerifiedTitle: "تم تأكيد البريد الإلكتروني!",
        emailVerifiedDesc: "تم تأكيد بريدك الإلكتروني. جاري تحويلك إلى لوحة التحكم...",
        emailNotVerifiedYet: "لم يتم تأكيد البريد الإلكتروني بعد. يرجى التحقق من بريدك الوارد.",
        iVerifiedMyEmail: "لقد أكدت بريدي الإلكتروني",
        resendVerificationEmail: "إعادة إرسال رسالة التأكيد"
      },
      // Error Messages
      errors: {
        fillAllFields: "يرجى ملء جميع الحقول",
        passwordsDontMatch: "كلمات المرور غير متطابقة",
        passwordTooShort: "يجب أن تكون كلمة المرور على الأقل 6 أحرف",
        loginFailed: "فشل تسجيل الدخول. يرجى المحاولة مرة أخرى.",
        registrationFailed: "فشل التسجيل. يرجى المحاولة مرة أخرى.",
        userNotFound: "لا يوجد حساب بهذا البريد الإلكتروني",
        wrongPassword: "كلمة المرور غير صحيحة",
        invalidEmail: "عنوان البريد الإلكتروني غير صحيح",
        tooManyRequests: "محاولات كثيرة فاشلة. يرجى المحاولة لاحقاً.",
        userDisabled: "تم تعطيل هذا الحساب",
        emailInUse: "يوجد حساب بهذا البريد الإلكتروني بالفعل",
        weakPassword: "كلمة المرور ضعيفة جداً",
        popupBlocked: "تم حظر النافذة المنبثقة. يرجى السماح بالنوافذ المنبثقة والمحاولة مرة أخرى.",
        popupClosed: "تم إلغاء تسجيل الدخول",
        accountExistsDifferent: "يوجد حساب بهذا البريد الإلكتروني باستخدام طريقة تسجيل دخول مختلفة",
        googleSignInFailed: "فشل تسجيل الدخول بجوجل. يرجى المحاولة مرة أخرى.",
        embeddedBrowser: "تسجيل الدخول بجوجل غير مدعوم في هذا المتصفح. يرجى الفتح في Safari أو Chrome.",
        networkError: "خطأ في الشبكة. يرجى التحقق من اتصالك.",
        emailNotVerified: "يرجى تأكيد بريدك الإلكتروني قبل تسجيل الدخول.",
        emailSendFailed: "فشل إرسال رسالة التأكيد.",
        checkVerificationFailed: "فشل التحقق من حالة التأكيد.",
        fillRequired: "يرجى ملء جميع الحقول المطلوبة",
        addFailed: "فشل في إضافة الإدخال",
        updateFailed: "فشل في تحديث الإدخال",
        deleteFailed: "فشل في حذف الإدخال",
        saveFailed: "فشل في حفظ التغييرات"
      },
      // Embedded Browser Modal
      embeddedBrowser: {
        title: "يجب الفتح في المتصفح",
        description: "لأسباب أمنية، تسجيل الدخول بجوجل غير متاح في هذا المتصفح. يرجى فتح هذه الصفحة في Safari أو Chrome.",
        howTo: "كيفية المتابعة:",
        step1: "1. اضغط على أيقونة القائمة (⋯) في الأعلى",
        step2: "2. اختر 'فتح في Safari' أو 'فتح في المتصفح'",
        step3: "3. أو انسخ الرابط أدناه والصقه في متصفحك",
        copyLink: "نسخ الرابط",
        copied: "تم النسخ!",
        tryOpenBrowser: "محاولة الفتح في المتصفح",
        useEmail: "المتابعة بالبريد الإلكتروني بدلاً من ذلك",
        googleNotAvailable: "تسجيل الدخول بجوجل (افتح في المتصفح)",
        detectedIn: "تم الاكتشاف في:",
        inAppBrowser: "متصفح داخل التطبيق"
      },
      // Success Messages
      success: {
        welcomeBack: "أهلاً بعودتك!",
        accountCreated: "تم إنشاء الحساب! يرجى التحقق من بريدك الإلكتروني لتأكيد حسابك.",
        passwordReset: "تم إرسال رابط إعادة تعيين كلمة المرور!",
        googleSignIn: "تم تسجيل الدخول بجوجل بنجاح!"
      },
      // Common
      common: {
        loading: "جاري التحميل...",
        error: "خطأ",
        success: "نجح",
        save: "حفظ",
        cancel: "إلغاء",
        delete: "حذف",
        add: "إضافة",
        required: "مطلوب",
        optional: "اختياري",
        close: "إغلاق",
        open: "فتح",
        yes: "نعم",
        no: "لا",
        createCV: "إنشاء سيرة ذاتية"
      },
      // Pricing
      pricing: {
        title: "اختر خطتك",
        subtitle: "ابدأ مجاناً، وقم بالترقية عندما تحتاج لمزيد من المميزات",
        free: "مجاني",
        pro: "احترافي",
        month: "/شهر",
        features: "المميزات",
        getStarted: "ابدأ الآن",
        upgrade: "ترقية الآن",
        popular: "الأكثر شعبية"
      },
      // Home page content
      home: {
        metaTitle: "CvLogs - بناء السيرة الذاتية بالذكاء الاصطناعي",
        metaDescription: "أنشئ سيرات ذاتية احترافية عصرية مع توصيات بالذكاء الاصطناعي",
        heroTitle: "أنشئ سيرتك الذاتية المثالية في دقائق",
        heroSubtitle: "أنشئ سيرات ذاتية احترافية، متوافقة مع أنظمة ATS، مع توصيات بالذكاء الاصطناعي وقوالب جميلة",
        heroFeatures: "موثوق به من قبل أكثر من 10,000 مهني عالمياً",
        continueBuilding: "استمر في بناء",
        startBuilding: "ابدأ بناء سيرتك الذاتية",
        benefits: {
          title: "لماذا تختار CvLogs؟",
          professionalTemplates: "قوالب احترافية مصممة بخبراء",
          realtimePreview: "عرض حي في الوقت الفعلي أثناء البناء",
          industrySpecific: "توصيات صناعية محددة",
          mobileResponsive: "تصميم متجاوب مع الأجهزة",
          secureStorage: "تخزين سري في السحابة",
          multipleFormats: "تصدير بتنسيقات متعددة"
        },
        benefitsList: [
          "قوالب احترافية مصممة بواسطة خبراء",
          "معاينة فورية أثناء البناء",
          "توصيات خاصة بالصناعة",
          "تصميم متجاوب مع الأجهزة المحمولة",
          "تخزين آمن في السحابة",
          "تصدير بتنسيقات متعددة"
        ],
        testimonials: {
          title: "ما يقول المستخدمون عنا",
          user1: "كانت CvLogs تساعدني في الحصول على وظيفة أحلامي! كانت توصيات الذكاء الاصطناعي مفيدة جداً.",
          user2: "القوالب حديثة والواجهة سهلة الاستخدام.",
          user3: "أنشأت سيرة ذاتية مذهلة في أقل من 15 دقيقة. موصى بها بشدة!"
        },
        testimonial: {
          text: "ساعدني CvLogs في الحصول على وظيفة أحلامي! كانت توصيات الذكاء الاصطناعي دقيقة والقوالب تبدو احترافية للغاية.",
          author: "سارة أحمد",
          role: "مهندسة برمجيات في جوجل"
        },
        cta: {
          title: "جاهز لبناء سيرتك الذاتية المثالية؟",
          subtitle: "انضم إلى آلاف المهنيين الذين نجحوا في الحصول على وظائف أحلامهم",
          button: "ابدأ الآن",
          free: "متاح تماماً مجاناً للبدء"
        }
      },
      // Resume Chat
      resumeChat: {
        title: "مساعد السيرة الذاتية الذكي | CvLogs",
        description: "احصل على نصائح شخصية للسيرة الذاتية وابن سيرتك بمساعدة الذكاء الاصطناعي",
        heading: "مساعد السيرة الذاتية الذكي",
        subtitle: "اسألني أي شيء عن سيرتك الذاتية أو دعني أساعدك في بنائها خطوة بخطوة",
        placeholder: "اكتب رسالتك هنا...",
        send: "إرسال",
        quickQuestions: "أسئلة سريعة",
        exampleQuestions: {
          improve: "كيف يمكنني تحسين سيرتي الذاتية؟",
          format: "ما هو أفضل تنسيق للسيرة الذاتية؟",
          keywords: "ما هي الكلمات المفتاحية التي يجب أن أشملها؟",
          experience: "كيف أصف خبرتي؟"
        },
        aiTyping: "الذكاء الاصطناعي يكتب...",
        welcome: "مرحباً! أنا مساعدك الذكي للسيرة الذاتية. كيف يمكنني مساعدتك اليوم؟",
        buildCV: "ابن السيرة الذاتية بالذكاء الاصطناعي",
        startBuilding: "ابدأ البناء"
      },
      
      // CV Builder Form
      form: {
        title: "ابن سيرتك الذاتية",
        editTitle: "حرر سيرتك الذاتية",
        description: "اتبع الخطوات أدناه لإنشاء سيرتك الذاتية الاحترافية",
        metaDescription: "قم ببناء سيرتك الذاتية المهنية خطوة بخطوة باستخدام منشئ النماذج الحديث",
        progress: "التقدم",
        progressDescription: "تتبع تقدمك في إنجاز السيرة الذاتية",
        step: "خطوة",
        of: "من",
        complete: "مكتمل",
        next: "التالي",
        previous: "السابق",
        skip: "تخطي",
        save: "حفظ",
        saveCV: "حفظ السيرة الذاتية",
        updateCV: "تحديث السيرة الذاتية",
        saving: "جاري الحفظ...",
        updating: "جاري التحديث...",
        finish: "إنهاء",
        required: "حقل مطلوب",
        languageChanged: "تم تغيير اللغة إلى",
        readyToSave: "جاهز للحفظ!",
        
        // Loading states
        loading: {
          title: "تحميل بيانات السيرة الذاتية",
          message: "يرجى الانتظار بينما نقوم بتحميل سيرتك الذاتية للتحرير..."
        },
        preparing: {
          title: "تحضير النموذج",
          message: "يرجى الانتظار بينما نقوم بتحضير منشئ السيرة الذاتية..."
        },
        optional: "اختياري",
        
        // Steps
        steps: {
          personalInfo: {
            title: "المعلومات الشخصية",
            subtitle: "لنبدأ بمعلوماتك الأساسية",
            description: "ستظهر هذه المعلومات في أعلى سيرتك الذاتية"
          },
          education: {
            title: "التعليم",
            subtitle: "أضف خلفيتك التعليمية",
            description: "تشمل شهاداتك وإنجازاتك الأكاديمية"
          },
          track: {
            title: "المسار المهني",
            subtitle: "اختر مجالك المهني",
            description: "اختر الصناعة أو المجال الذي تعمل فيه"
          },
          experience: {
            title: "الخبرة العملية",
            subtitle: "أخبرنا عن تاريخك المهني",
            description: "اذكر خبرتك المهنية بترتيب زمني عكسي"
          },
          projects: {
            title: "المشاريع",
            subtitle: "اعرض مشاريعك المميزة",
            description: "تشمل المشاريع الشخصية والأكاديمية والمهنية"
          },
          certifications: {
            title: "الشهادات المهنية",
            subtitle: "أضف شهاداتك المهنية",
            description: "تشمل الشهادات والتراخيص والاعتمادات المهنية"
          },
          skills: {
            title: "المهارات واللغات",
            subtitle: "ما الذي تجيده؟",
            description: "اذكر مهاراتك التقنية والشخصية واللغات"
          },
          review: {
            title: "مراجعة وإنهاء",
            subtitle: "راجع معلوماتك",
            description: "تأكد من أن كل شيء يبدو جيداً قبل إنشاء سيرتك الذاتية"
          }
        },
        
        // المسارات المهنية
        tracks: {
          technology: "التكنولوجيا",
          healthcare: "الرعاية الصحية",
          finance: "المالية",
          education: "التعليم",
          marketing: "التسويق",
          sales: "المبيعات",
          design: "التصميم",
          engineering: "الهندسة",
          management: "الإدارة",
          consulting: "الاستشارات",
          legal: "القانونية",
          media: "الإعلام",
          research: "البحث",
          operations: "العمليات",
          hr: "الموارد البشرية",
          other: "أخرى"
        },

        // المسار
        track: {
          customTrack: "حدد مجالك",
          customTrackPlaceholder: "أدخل مجالك المهني"
        },

        // المعلومات الشخصية
        personalInfo: {
          fullName: {
            label: "الاسم الكامل",
            placeholder: "أدخل اسمك الكامل"
          },
          email: {
            label: "عنوان البريد الإلكتروني",
            placeholder: "your.email@example.com"
          },
          phone: {
            label: "رقم الهاتف",
            placeholder: "+966 50 123 4567"
          },
          location: {
            label: "الموقع",
            placeholder: "المدينة، البلد"
          },
          website: {
            label: "موقع ويب/معرض أعمال",
            placeholder: "https://yourwebsite.com"
          },
          linkedin: {
            label: "ملف LinkedIn الشخصي",
            placeholder: "https://linkedin.com/in/yourprofile"
          },
          github: {
            label: "ملف GitHub الشخصي",
            placeholder: "https://github.com/yourusername"
          },
          summary: {
            label: "الملخص المهني",
            placeholder: "ملخص موجز عن خلفيتك المهنية وأهدافك المهنية..."
          }
        },
        
        // التعليم
        education: {
          entry: "تعليم",
          degree: "الدرجة العلمية",
          degreePlaceholder: "بكالوريوس علوم الحاسوب",
          institution: "المؤسسة التعليمية",
          institutionPlaceholder: "جامعة التكنولوجيا",
          field: "مجال الدراسة",
          fieldPlaceholder: "علوم الحاسوب",
          graduationDate: "تاريخ التخرج",
          location: "الموقع",
          locationPlaceholder: "المدينة، البلد",
          addEducation: "إضافة تعليم"
        },
        
        // الخبرة
        experience: {
          entry: "خبرة",
          position: "المنصب",
          positionPlaceholder: "مهندس برمجيات",
          company: "الشركة",
          companyPlaceholder: "شركة التكنولوجيا المحدودة",
          startDate: "تاريخ البداية",
          endDate: "تاريخ النهاية",
          currentJob: "أعمل هنا حالياً",
          location: "الموقع",
          locationPlaceholder: "المدينة، البلد",
          description: "وصف الوظيفة",
          descriptionPlaceholder: "اوصف مسؤولياتك الرئيسية وإنجازاتك...",
          addExperience: "إضافة خبرة"
        },
        
        // المشاريع
        projects: {
          entry: "مشروع",
          name: "اسم المشروع",
          namePlaceholder: "موقع تجارة إلكترونية",
          url: "رابط المشروع",
          urlPlaceholder: "https://github.com/user/project",
          startDate: "تاريخ البداية",
          endDate: "تاريخ النهاية",
          technologies: "التقنيات",
          technologiesPlaceholder: "React, Node.js, MongoDB",
          technologiesHint: "افصل التقنيات بفاصلة",
          description: "الوصف",
          descriptionPlaceholder: "وصف مختصر للمشروع...",
          addProject: "إضافة مشروع"
          },

        // الشهادات
        certifications: {
          entry: "شهادة",
          name: "اسم الشهادة",
          namePlaceholder: "AWS Certified Solutions Architect",
          issuer: "الجهة المانحة",
          issuerPlaceholder: "Amazon Web Services",
          date: "تاريخ الحصول",
          credentialId: "رقم الاعتماد",
          credentialIdPlaceholder: "ABC123XYZ",
          url: "رابط التحقق",
          urlPlaceholder: "https://verify.certification.com",
          addCertification: "إضافة شهادة"
        },
        
        // العمل التطوعي
        volunteer: {
          title: "العمل التطوعي",
          entry: "العمل التطوعي",
          role: "الدور",
          organization: "المؤسسة",
          cause: "القضية",
          startDate: "تاريخ البدء",
          endDate: "تاريخ الانتهاء"
        },
        // الجوائز
        awards: {
          title: "الجوائز والتقدير",
          entry: "جائزة",
          name: "عنوان الجائزة",
          organization: "الجهة المانحة",
          date: "تاريخ الحصول عليها"
        },
        
        // المهارات
        skills: {
          technical: "المهارات التقنية",
          technicalPlaceholder: "أضف مهارة تقنية واضغط Enter",
          languages: "اللغات",
          languagesPlaceholder: "أضف لغة (مثل: الإنجليزية - متقدم) واضغط Enter",
          soft: "المهارات الشخصية",
          softPlaceholder: "أضف مهارة شخصية واضغط Enter",
          pressEnter: "اضغط Enter للإضافة"
        },

        // المراجعة
        review: {
          congratulations: "تهانينا!",
          completedMessage: "لقد أكملت سيرتك الذاتية. يمكنك الآن معاينتها وتحميلها.",
          previewCV: "معاينة السيرة الذاتية",
          editMore: "تعديل المزيد",
          sections: "الأقسام المكتملة",
          stats: "إحصائيات السيرة الذاتية",
          education: "مدخلات التعليم",
          experience: "الخبرة العملية",
          projects: "المشاريع",
          certifications: "الشهادات"
        },
        

        

        

        

        
        // Common
        actions: {
          add: "إضافة",
          edit: "تعديل",
          delete: "حذف",
          remove: "إزالة",
          cancel: "إلغاء",
          confirm: "تأكيد"
        },
        
        validation: {
          required: "{{field}} مطلوب",
          nameRequired: "الاسم مطلوب",
          emailRequired: "البريد الإلكتروني مطلوب",
          email: "يرجى إدخال عنوان بريد إلكتروني صحيح",
          invalidEmail: "يرجى إدخال عنوان بريد إلكتروني صحيح",
          url: "يرجى إدخال رابط صحيح",
          invalidUrl: "يرجى إدخال رابط صحيح",
          invalidUrlField: "{{field}} يجب أن يكون رابط صالح (مثال: https://example.com)",
          phone: "يرجى إدخال رقم هاتف صحيح",
          minLength: "مطلوب {min} أحرف كحد أدنى",
          maxLength: "مسموح {max} أحرف كحد أقصى",
          pleaseFixErrors: "يرجى إصلاح الأخطاء:",
          correctFollowingErrors: "يرجى تصحيح الأخطاء التالية:",
          andMoreErrors: "+{{count}} خطأ إضافي",
          englishOnly: "يرجى استخدام أحرف إنجليزية فقط",
          englishOnlyField: "{{field}} يجب أن يكون بالإنجليزية فقط",
          bilingualField: "{{field}} يجب أن يكون بالإنجليزية أو العربية فقط",
          invalidCharacters: "يحتوي على أحرف غير صالحة",
          invalidCharsField: "{{field}} يحتوي على أحرف غير صالحة",
          noFutureDate: "{{field}} لا يمكن أن يكون في المستقبل",
          futureDateField: "{{field}} لا يمكن أن يكون تاريخ مستقبلي",
          dateRange: "تاريخ البدء يجب أن يكون قبل تاريخ الانتهاء",
          startBeforeEnd: "تاريخ البدء يجب أن يكون قبل تاريخ الانتهاء",
          linkRequired: "اسم الرابط و URL مطلوبان معًا"
        }
      },
      
      // CV Generation
      cvGeneration: {
        title: "إنشاء السيرة الذاتية بالذكاء الاصطناعي",
        subtitle: "أنشئ سيرتك الذاتية الاحترافية بالذكاء الاصطناعي",
        targetJob: "الوظيفة المستهدفة (اختياري)",
        jobTitle: "عنوان الوظيفة",
        jobTitlePlaceholder: "مثل: مطور واجهة أمامية أول",
        jobDescription: "وصف الوظيفة",
        jobDescriptionPlaceholder: "الصق وصف الوظيفة هنا للحصول على سيرة ذاتية مخصصة...",
        generateAICV: "إنشاء السيرة الذاتية بالذكاء الاصطناعي",
        generating: "جاري الإنشاء...",
        templateStyle: "نمط القالب",
        atsFriendly: "متوافق مع أنظمة ATS",
        saveDraft: "حفظ الملف",
        atsFriendlyDesc: "أبيض وأسود، تنسيق بسيط",
        modernColorful: "عصري ملون",
        modernColorfulDesc: "تصميم حيوي بالألوان",
        exportOptions: "خيارات التصدير",
        downloadAsPDF: "تحميل كـ PDF",
        downloadAsWord: "تحميل كـ Word",
        viewHistory: "عرض التاريخ",
        noCVGenerated: "لم يتم إنشاء سيرة ذاتية بعد",
        noCVGeneratedDesc: "أنشئ سيرتك الذاتية الأولى المعززة بالذكاء الاصطناعي لرؤية المعاينة هنا",
        generateFirstCV: "أنشئ سيرتك الأولى",
        loadingCV: "جاري تحميل سيرتك الذاتية...",
        cvLoaded: "تم تحميل السيرة الذاتية بنجاح",
        enhancingSection: "جاري تحسين القسم...",
        enhancementSuccess: "تم تحسين قسم السيرة الذاتية بنجاح!",
        enhancementError: "فشل تحسين قسم السيرة الذاتية",
        enhancementNotAvailable: "معالج التحسين غير متاح",
        aiEnhancement: "التحسين بالذكاء الاصطناعي",
        enhanceSection: "تحسين القسم",
        enhanceButtonTooltip: "انقر لتحسين هذا القسم بالذكاء الاصطناعي",
        enhancementPrompt: "موجه التحسين",
        enhancementPromptPlaceholder: "اوصف كيف تريد تحسين هذا القسم...",
        enhancementPromptHint: "قدم تعليمات محددة لكيفية تحسين الذكاء الاصطناعي لهذا القسم",
        quickSuggestions: "اقتراحات سريعة:",
        cancel: "إلغاء",
        enhancing: "جاري التحسين...",
        enhancementModalTitle: "التحسين بالذكاء الاصطناعي",
        backToBuilder: "العودة إلى المنشئ",
        enterPromptError: "يرجى إدخال موجه أو اختيار اقتراح",
        aiContentHistory: "سجل المحتوى بالذكاء الاصطناعي",
        aiContent: "المحتوى",
        history: "التاريخ",
        aiContentHistoryDesc: "عرض جميع الأقسام المعززة بالذكاء الاصطناعي مع موجهاتها وطوابعها الزمنية",
        loadingAIContent: "جاري تحميل المحتوى بالذكاء الاصطناعي...",
        noAIContent: "لم يتم العثور على محتوى معزز بالذكاء الاصطناعي",
        enhancedContent: "المحتوى المحسن",
        enhancedItems: "العناصر المحسنة",
        technicalSkills: "المهارات التقنية",
        softSkills: "المهارات الشخصية",
        professionalSummary: "الملخص المهني",
        experienceEnhancements: "تحسينات الخبرة",
        projectEnhancements: "تحسينات المشاريع",
        skillsEnhancement: "تحسين المهارات",
        volunteerEnhancements: "تحسينات العمل التطوعي",
        awardsEnhancements: "تحسينات الجوائز",
        experience: "الخبرة",
        project: "المشروع",
        volunteer: "العمل التطوعي",
        award: "الجائزة",
        item: "العنصر",
        copy: "نسخ",
        copied: "تم النسخ إلى الحافظة",
        copyFailed: "فشل النسخ",
        aiContentError: "فشل تحميل المحتوى بالذكاء الاصطناعي",
        close: "إغلاق",
        // Enhancement Section Menu
        enhancementSectionMenu: "التحسين بالذكاء الاصطناعي",
        enhancementSectionMenuDesc: "اختر قسماً لتحسينه بالذكاء الاصطناعي",
        selectSectionToEnhance: "اختر قسماً لتحسينه بالذكاء الاصطناعي",
        professionalSummarySection: "الملخص المهني",
        professionalSummaryDesc: "حسن ملخصك المهني",
        skillsSection: "المهارات",
        skillsDesc: "حسن قسم المهارات",
        workExperienceSection: "الخبرة العملية",
        workExperienceDesc: "حسن الخبرات الفردية",
        projectsSection: "المشاريع",
        projectsDesc: "حسن المشاريع الفردية",
        volunteerWorkSection: "العمل التطوعي",
        volunteerWorkDesc: "حسن تجارب العمل التطوعي",
        awardsSection: "الجوائز",
        awardsDesc: "حسن جوائزك",
        noDataAvailable: "لا توجد بيانات متاحة",
        itemsCount: "عناصر",
        oneItem: "عنصر",
        howEnhancementWorks: "كيف يعمل التحسين بالذكاء الاصطناعي",
        enhancementWorkflowDesc: "اختر قسماً أعلاه، قدم تعليمات حول كيفية تحسينه، وسيقوم الذكاء الاصطناعي بإنشاء نسخة محسنة. يمكنك المراجعة والمقارنة قبل تطبيق التغييرات.",
        enhanceCVSections: "تحسين أقسام السيرة الذاتية",
        enhancementAvailable: "التحسين بالذكاء الاصطناعي متاح!",
        enhancementHintDesc: "استخدم طريقتين لتحسين سيرتك الذاتية بالذكاء الاصطناعي:",
        enhancementQuickWay: "سريع: انقر على زر \"تحسين أقسام السيرة الذاتية\" ←",
        enhancementDirectWay: "مباشر: مرر فوق أي قسم أدناه وانقر على زر ✨",
        viewThisTemplate: "عرض هذا القالب",
        makeItProfessional: "اجعله أكثر احترافية",
        addMoreImpact: "أضف المزيد من التأثير",
        focusOnAchievements: "ركز على الإنجازات",
        makeItConcise: "اجعله مختصراً",
        highlightTechnicalSkills: "أبرز المهارات التقنية",
        emphasizeLeadership: "أكد على القيادة",
        improveClarity: "حسن الوضوح",
        addQuantifiableResults: "أضف نتائج قابلة للقياس",
        // Additional CVGeneration page translations
        title: "إنشاء السيرة الذاتية بالذكاء الاصطناعي",
        description: "أنشئ سيرتك الذاتية المهنية بالذكاء الاصطناعي",
        backToBuilder: "العودة إلى المنشئ",
        generatedCV: "سيرة ذاتية تم إنشاؤها",
        enhance: "تحسين",
        aiContent: "محتوى AI",
        template: "نموذج",
        history: "السجل",
        regenerate: "إعادة إنشاء",
        exportPDF: "تصدير PDF",
        export: "تصدير",
        regenerateCV: "إعادة إنشاء السيرة الذاتية",
        targetJobOptional: "استهداف وظيفة محددة (اختياري)",
        generateNow: "إنشاء",
        aiEnhancedCV: "سيرة ذاتية معززة بالذكاء الاصطناعي",
        quickActions: "الإجراءات السريعة",
        generateAICV: "إنشاء سيرة ذاتية جديدة بالذكاء الاصطناعي",
        saveDraft: "حفظ المسودة وعرض السيرة الذاتية القديمة",
        saving: "جاري الحفظ...",
        // ComparisonModal translations
        reviewChanges: "مراجعة التغييرات",
        original: "الأصلي",
        aiEnhanced: "محسن بالذكاء الاصطناعي",
        editing: "جاري التحرير",
        copyOriginal: "نسخ المحتوى الأصلي",
        copyEnhanced: "نسخ المحتوى المحسن",
        editManually: "تحرير يدوي",
        stopEditing: "إيقاف التحرير",
        editEnhanced: "تحرير المحتوى المحسن...",
        noOriginalContent: "لا يوجد محتوى أصلي",
        noEnhancedContent: "لا يوجد محتوى محسن",
        processing: "جاري المعالجة...",
        editBeforeApplying: "حرر قبل التطبيق",
        acceptOrKeepOriginal: "اقبل النسخة المحسنة أو احتفظ بالأصلية",
        decline: "رفض",
        accept: "قبول",
        acceptAndApply: "قبول وتطبيق",
        changesApplied: "تم تطبيق التغييرات بنجاح!",
        changesDeclined: "تم رفض التغييرات",
        originalCopied: "تم نسخ المحتوى الأصلي إلى الحافظة!",
        enhancedCopied: "تم نسخ المحتوى المحسن إلى الحافظة!",
        // Template & Export translations
        templateAndExport: "القالب والتصدير",
        currentTemplate: "القالب الحالي",
        change: "تغيير",
        downloadPDF: "تحميل PDF",
        downloadWord: "تحميل Word",
        chooseTemplate: "اختر قالب سيرتك الذاتية",
        templatePreviewDesc: "معاينة واختيار القالب المثالي لسيرتك الذاتية",
        // Target Job translations
        targetJob: "الوظيفة المستهدفة (اختياري)",
        leaveBlankGeneral: "اتركه فارغاً للسيرة الذاتية العامة",
        jobTitle: "مسمى الوظيفة",
        jobTitlePlaceholder: "مثل: مطور واجهات أمامية أول",
        jobDescription: "وصف الوظيفة",
        jobDescriptionPlaceholder: "الصق وصف الوظيفة هنا...",
        generating: "جاري الإنشاء...",
        generateWithTargetJob: "إنشاء مع الوظيفة المستهدفة",
        generatingPreview: "جاري إنشاء المعاينة...",
        generatingPDF: "جاري إنشاء PDF...",
        templateNotFound: "لم يتم العثور على قالب السيرة الذاتية. يرجى الانتظار حتى يتم تحميل القالب.",
        pdfDownloaded: "تم تحميل PDF بنجاح!",
        exportFailed: "فشل تصدير PDF",
        refreshPreview: "تحديث المعاينة",
        refreshing: "جاري التحديث...",
        retryPreview: "إعادة المحاولة",
        // Generation History
        generationHistory: "سجل الإنشاء",
        generationHistoryDesc: "عرض واستعادة الإصدارات السابقة من السيرة الذاتية",
        noGenerationHistory: "لا يوجد سجل إنشاء بعد",
        generationLabel: "الإصدار",
        current: "الحالي",
        skillsGenerated: "المهارات المُنشأة",
        totalGenerations: "إجمالي الإصدارات",
        sectionsAvailable: "أقسام متاحة للتحسين"
      },
      // CV Selector Modal
      cvSelector: {
        title: "اختر قالب سيرتك الذاتية",
        noCVs: "لم يتم العثور على سير ذاتية. قم بإنشاء واحدة أولاً!",
        selectInstruction: "اختر السيرة الذاتية التي تريد استخدامها لمطابقة الوظائف",
        lastUpdated: "آخر تحديث",
        continue: "متابعة بالسيرة الذاتية المحددة"
      },
      // Template Selector
      templateSelector: {
        title: "اختر قالب سيرتك الذاتية",
        description: "اختر قالبًا وعاينه قبل التنزيل. قوالب ATS محسّنة لأنظمة تتبع المتقدمين.",
        atsFriendly: "قوالب متوافقة مع ATS",
        recommended: "موصى به",
        viewTemplate: "معاينة هذا القالب",
        coloredDesign: "قوالب ملونة ومصممة",
        colorfulHeader: "تصميم رأس ملون",
        selected: "المحدد",
        generating: "جاري الإنشاء...",
        downloadPDF: "تحميل PDF"
      },
      // Job Search
      jobSearch: {
        title: "البحث عن وظائف",
        subtitle: "ابحث عن الوظائف على LinkedIn وIndeed والمزيد",
        searchFilters: "فلاتر البحث",
        viewHistory: "عرض التاريخ",
        searchHistory: "سجل البحث",
        noHistory: "لا يوجد سجل بحث",
        provider: "الموفر",
        searchTerm: "مصطلح البحث",
        searchTermPlaceholder: "ابحث عن وظائف حسب المسمى أو الشركة...",
        location: "الموقع",
        locationPlaceholder: "مثل: القاهرة، مصر",
        country: "البلد",
        resultsWanted: "النتائج",
        daysOld: "الحد الأقصى للأيام",
        hoursOld: "العمر بالساعات (حد أقصى)",
        fetchDescription: "جلب أوصاف الوظائف",
        search: "بحث",
        searching: "جاري البحث عن وظائف...",
        enterSearchTerm: "يرجى إدخال مصطلح البحث",
        searchSuccess: "تم العثور على {count} وظيفة",
        noResults: "لم يتم العثور على وظائف",
        searchError: "فشل البحث عن الوظائف",
        results: "نتائج",
        refresh: "تحديث",
        refreshSuccess: "تم تحديث البحث ووجد {count} وظيفة",
        refreshError: "فشل تحديث البحث",
        noSearchToRefresh: "لا يوجد بحث للتحديث",
        loadedFromHistory: "تم تحميل البحث من السجل",
        loadError: "فشل تحميل البحث",
        noJobs: "لم يتم العثور على وظائف",
        noJobsDesc: "حاول تعديل معايير البحث الخاصة بك",
        remote: "عن بُعد",
        viewJob: "عرض الوظيفة",
        showDescription: "عرض الوصف",
        hideDescription: "إخفاء الوصف",
        unnamedSearch: "بحث غير مسمى",
        history: "السجل",
        applyNow: "قدم الآن",
        proMatch: "تطابق احترافي",
        generateCoverLetter: "إنشاء خطاب تغطية",
        matchWith: "تطابق مع:",
        analyzing: "جاري التحليل...",
        viewMatchAnalysis: "عرض تحليل التطابق",
        saveToLibrary: "حفظ في المكتبة",
        savedToLibrary: "محفوظ في المكتبة",
        createCV: "إنشاء سيرة ذاتية"
      },
      // CV Builder specific
      cvBuilder: {
        noResumes: "لا توجد سير ذاتية بعد",
        getStarted: "ابدأ بإنشاء سيرتك الذاتية الاحترافية الأولى",
        createNew: "إنشاء سيرة ذاتية جديدة",
        createFirst: "أنشئ سيرتك الأولى",
        loading: "جاري تحميل سيرك الذاتية...",
        untitled: "سيرة ذاتية بدون عنوان",
        edit: "تعديل",
        view: "عرض",
        createModalTitle: "إنشاء سيرة ذاتية جديدة",
        createModalDesc: "اختر اسماً لمستند سيرتك الذاتية (مثل: \"سيرة ذاتية مطور واجهة أمامية 2024\")",
        resumeName: "اسم مستند السيرة الذاتية",
        resumeNamePlaceholder: "مثال: سيرة ذاتية مطور برمجيات",
        resumeNameHint: "هذا عنوان المستند وليس اسمك الشخصي. ستضيف تفاصيلك الشخصية في الخطوة التالية.",
        createButton: "إنشاء السيرة الذاتية",
        creating: "جاري الإنشاء...",
        cancel: "إلغاء"
      },
      // Settings
      settings: {
        title: "الإعدادات",
        account: "الحساب",
        profile: "الملف الشخصي",
        preferences: "التفضيلات",
        language: "اللغة",
        theme: "المظهر",
        notifications: "الإشعارات",
        privacy: "الخصوصية",
        security: "الأمان",
        billing: "الفواتير",
        subscription: "الاشتراك",
        helpSupport: "المساعدة والدعم"
      },
      // Subscription
      subscription: {
        title: "خطة الاشتراك",
        currentPlan: "الخطة الحالية",
        free: "مجاني",
        pro: "احترافي",
        upgrade: "ترقية",
        downgrade: "تخفيض",
        cancel: "إلغاء",
        manage: "إدارة الاشتراك",
        features: "الميزات",
        billing: "الفواتير",
        nextBillingDate: "تاريخ الفوترة التالي",
        paymentMethod: "طريقة الدفع"
      },
      // Overview/Dashboard extended
      overview: {
        title: "نظرة عامة",
        welcomeBack: "مرحباً بعودتك",
        summary: "ملخص",
        recentActivity: "النشاط الأخير",
        stats: "الإحصائيات",
        quickLinks: "روابط سريعة"
      },
      // Edit Resume
      editResume: {
        sections: "الأقسام",
        progress: "التقدم",
        previous: "السابق",
        next: "التالي",
        finish: "إنهاء وإنشاء",
        saveDraft: "حفظ المسودة",
        saveMagic: "حفظ ورؤية سحر CVLogs",
        saveMagicDesc: "دع ذكاء CVLogs يخصصه",
        saveAndGenerate: "حفظ وإنشاء",
        previewCV: "معاينة السيرة الذاتية"
      },
      // Tabs
      tabs: {
        personal: "المعلومات الشخصية",
        education: "التعليم",
        experience: "الخبرة",
        skills: "المهارات",
        languages: "اللغات",
        projects: "المشاريع",
        licenses: "التراخيص",
        training: "التدريب",
        awards: "الجوائز",
        volunteer: "العمل التطوعي",
        recommendations: "التوصيات",
        publications: "المنشورات",
        interests: "الاهتمامات"
      },
      // Job Library
      jobLibrary: {
        title: "مكتبة الوظائف",
        myJobLibrary: "مكتبة الوظائف",
        subtitle: "تتبع وإدارة فرص العمل المحفوظة",
        loading: "جاري تحميل مكتبة الوظائف...",
        totalSaved: "إجمالي المحفوظة",
        avgMatch: "متوسط التطابق",
        searchPlaceholder: "ابحث عن الوظائف المحفوظة حسب المسمى أو الشركة أو الموقع...",
        filters: "الفلاتر",
        refresh: "تحديث",
        applicationStatus: "حالة التقديم",
        allStatuses: "كل الحالات",
        jobSource: "مصدر الوظيفة",
        allSources: "كل المصادر",
        jobSearchLinkedIn: "البحث عن وظائف (LinkedIn/Indeed)",
        remoteJobsSource: "وظائف عن بُعد",
        advancedSearchSource: "بحث متقدم",
        reset: "إعادة تعيين",
        applyFilters: "تطبيق الفلاتر",
        noSavedJobs: "لا توجد وظائف محفوظة",
        noSavedJobsDesc: "ابدأ بحفظ الوظائف من صفحات البحث عن وظائف أو الوظائف عن بُعد",
        noJobsMatch: "لا توجد وظائف تطابق الفلاتر. حاول تعديل معايير البحث.",
        searchJobsBtn: "البحث عن وظائف",
        showing: "عرض",
        matchWith: "تطابق مع:",
        fullTime: "دوام كامل",
        remote: "عن بُعد",
        jobSearch: "البحث عن وظائف",
        advanced: "متقدم",
        saved: "محفوظ",
        viewDetails: "عرض التفاصيل",
        removeFromLibrary: "حذف من المكتبة",
        personalNotes: "ملاحظات شخصية",
        addNotes: "إضافة ملاحظات",
        editNotes: "تعديل الملاحظات",
        saveNotes: "حفظ الملاحظات",
        cancel: "إلغاء",
        jobDescription: "وصف الوظيفة",
        type: "النوع",
        location: "الموقع",
        posted: "تم النشر",
        requiredSkills: "المهارات المطلوبة",
        notApplied: "لم يتم التقديم",
        applied: "تم التقديم",
        interviewing: "مقابلات",
        rejected: "مرفوض",
        accepted: "مقبول",
        fullMatch:"تطابق احترافي",
        avgMatch: "متوسط التطابق",
        searchPlaceholder: "ابحث عن الوظائف حسب المسمى أو الشركة أو الموقع...",
        filterStatus: "تصفية حسب الحالة",
        filterSource: "تصفية حسب المصدر",
        allStatuses: "كل الحالات",
        allSources: "كل المصادر",
        showFilters: "إظهار الفلاتر",
        hideFilters: "إخفاء الفلاتر",
        noJobs: "لم يتم العثور على وظائف",
        noJobsDesc: "لم تحفظ أي وظائف بعد. ابدأ بالبحث عن الوظائف!",
        startSearching: "ابدأ البحث عن وظائف",
        viewDetails: "عرض التفاصيل",
        generateCoverLetter: "إنشاء خطاب تغطية",
        analyzeMatch: "تحليل التطابق",
        proMatch: "تطابق احترافي",
        updateStatus: "تحديث الحالة",
        addNotes: "إضافة ملاحظات",
        editNotes: "تعديل الملاحظات",
        saveNotes: "حفظ الملاحظات",
        notesPlaceholder: "أضف ملاحظاتك حول هذه الوظيفة...",
        deleteJob: "حذف الوظيفة",
        confirmDelete: "تأكيد الحذف",
        deleteMessage: "هل أنت متأكد من إزالة هذه الوظيفة من مكتبتك؟",
        matchScore: "نقاط التطابق",
        calculating: "جاري الحساب...",
        analyzing: "جاري التحليل...",
        selectCV: "اختر سيرة ذاتية لحساب نقاط التطابق",
        loadingJobs: "جاري تحميل مكتبة الوظائف...",
        createCV: "إنشاء سيرة ذاتية"
      },
      // Job Search
      jobSearchPage: {
        title: "البحث عن وظائف",
        subtitle: "ابحث عن فرصتك القادمة",
        searchPlaceholder: "المسمى الوظيفي أو الكلمات المفتاحية أو الشركة",
        locationPlaceholder: "المدينة أو المنطقة أو الرمز البريدي",
        searchButton: "البحث عن وظائف",
        filters: "الفلاتر",
        jobType: "نوع الوظيفة",
        experienceLevel: "مستوى الخبرة",
        salary: "نطاق الراتب",
        datePosted: "تاريخ النشر",
        remote: "عن بُعد",
        fullTime: "دوام كامل",
        partTime: "دوام جزئي",
        contract: "عقد",
        internship: "تدريب",
        entryLevel: "مبتدئ",
        midLevel: "متوسط",
        seniorLevel: "كبير",
        anyTime: "في أي وقت",
        past24h: "آخر 24 ساعة",
        pastWeek: "الأسبوع الماضي",
        pastMonth: "الشهر الماضي",
        applyFilters: "تطبيق الفلاتر",
        clearFilters: "مسح الفلاتر",
        resultsFound: "وظيفة تم العثور عليها",
        noResults: "لم يتم العثور على وظائف",
        noResultsDesc: "حاول تعديل معايير البحث",
        saveJob: "حفظ الوظيفة",
        viewJob: "عرض الوظيفة",
        applyNow: "قدم الآن",
        loading: "جاري تحميل الوظائف...",
        searching: "جاري البحث..."
      },
      // Cover Letter
      coverLetterModal: {
        generateTitle: "إنشاء خطاب تغطية",
        viewTitle: "خطاب التغطية",
        selectResume: "اختر السيرة الذاتية",
        chooseResume: "اختر سيرة ذاتية...",
        companyName: "اسم الشركة",
        companyPlaceholder: "مثال: جوجل، مايكروسوفت، أمازون",
        jobTitle: "المسمى الوظيفي",
        jobTitlePlaceholder: "مثال: مهندس برمجيات أول، مدير منتج",
        jobDescription: "وصف الوظيفة",
        jobDescPlaceholder: "الصق وصف الوظيفة الكامل هنا...",
        customInstructions: "تعليمات مخصصة (موجّه)",
        optional: "اختياري",
        customPlaceholder: "مثال: ركّز على خبرتي القيادية، أبرز مهارات بايثون، استخدم أسلوبًا رسميًا",
        generateBtn: "إنشاء خطاب التغطية بالذكاء الاصطناعي",
        generatingBtn: "جاري إنشاء خطاب التغطية...",
        exportPDF: "تصدير PDF",
        preparing: "جاري التحضير...",
        edit: "تعديل",
        save: "حفظ",
        regenerate: "إعادة الإنشاء",
        required: "يرجى اختيار سيرة ذاتية",
        requiredCompany: "يرجى إدخال اسم الشركة",
        requiredTitle: "يرجى إدخال المسمى الوظيفي",
        requiredDesc: "يرجى إدخال وصف الوظيفة",
        successGenerate: "تم إنشاء خطاب التغطية",
        successRegenerate: "تمت إعادة إنشاء خطاب التغطية بنجاح",
        successUpdate: "تم تحديث خطاب التغطية بنجاح",
        errorGenerate: "فشل في إنشاء خطاب التغطية",
        errorRegenerate: "فشل في إعادة إنشاء خطاب التغطية",
        errorUpdate: "فشل في تحديث خطاب التغطية",
        errorResumes: "فشل في تحميل السير الذاتية"
      },
      coverLetter: {
        title: "منشئ خطاب التغطية",
        history: "سجل خطابات التغطية",
        historyTitle: "سجل خطابات التغطية",
        historySubtitle: "عرض وإدارة خطابات التغطية المُنشأة",
        generate: "إنشاء خطاب تغطية",
        generating: "جاري الإنشاء...",
        selectJob: "اختر وظيفة",
        selectCV: "اختر سيرتك الذاتية",
        jobTitle: "المسمى الوظيفي",
        company: "الشركة",
        jobDescription: "وصف الوظيفة",
        customInstructions: "تعليمات مخصصة (اختياري)",
        customPlaceholder: "أضف أي نقاط محددة تريد تسليط الضوء عليها...",
        generateButton: "إنشاء خطاب التغطية",
        download: "تحميل",
        copy: "نسخ إلى الحافظة",
        edit: "تعديل",
        delete: "حذف",
        preview: "معاينة",
        createdOn: "تم الإنشاء في",
        noHistory: "لا توجد خطابات تغطية بعد",
        noHistoryDesc: "أنشئ خطاب تغطيتك الأول لرؤيته هنا",
        createCustom: "إنشاء خطاب تغطية مخصص",
        letter: "خطاب",
        letters: "خطابات",
        searchPlaceholder: "ابحث حسب الشركة أو المسمى الوظيفي...",
        allResumes: "كل السير الذاتية",
        untitledResume: "سيرة ذاتية بدون عنوان",
        noLettersFound: "لم يتم العثور على خطابات تغطية",
        generateFirst: "أنشئ خطاب التغطية الأول من صفحات البحث عن وظائف أو المكتبة",
        adjustFilters: "حاول تعديل الفلاتر",
        goToSearchBtn: "الذهاب إلى البحث عن وظائف",
        edited: "معدّل",
        view: "عرض",
        editLetter: "تعديل خطاب التغطية",
        deleteTitle: "حذف خطاب التغطية",
        deleteMessage: "هل أنت متأكد من حذف خطاب التغطية هذا؟ لا يمكن التراجع عن هذا الإجراء.",
        deleteConfirm: "حذف",
        cancel: "إلغاء",
        startGenerating: "إنشاء خطاب تغطية",
        copied: "تم النسخ إلى الحافظة!",
        downloadSuccess: "تم تحميل خطاب التغطية بنجاح!",
        deleteConfirm: "هل أنت متأكد من حذف خطاب التغطية هذا؟",
        loading: "جاري تحميل خطابات التغطية..."
      },
      // Job Match
      jobMatch: {
        title: "مطابق الوظائف بالذكاء الاصطناعي",
        subtitle: "حلل مدى تطابق سيرتك الذاتية مع أي وصف وظيفي",
        analyzeProfile: "تحليل ملفي الشخصي",
        selectCV: "اختر سيرتك الذاتية",
        selectResume: "اختر سيرتك الذاتية",
        selectJob: "اختر وظيفة للتحليل",
        untitledResume: "سيرة ذاتية بدون عنوان",
        jobDescription: "وصف الوظيفة",
        jobDescriptionPlaceholder: "الصق وصف الوظيفة الكامل هنا...",
        analyzeMatch: "تحليل التطابق بالذكاء الاصطناعي",
        matchScore: "نقاط التطابق",
        strengths: "نقاط القوة",
        weaknesses: "مجالات التحسين",
        gaps: "فجوات المهارات",
        recommendations: "توصيات الذكاء الاصطناعي",
        analyzing: "جاري تحليل التطابق...",
        noMatch: "لا توجد بيانات تطابق",
        noMatchDesc: "اختر سيرة ذاتية ووظيفة لرؤية تحليل التطابق",
        readyToAnalyze: "جاهز للتحليل",
        readyToAnalyzeDesc: "اختر سيرتك الذاتية والصق وصف الوظيفة للحصول على رؤى مدعومة بالذكاء الاصطناعي حول مدى تطابقك مع الوظيفة.",
        strongMatch: "تطابق قوي",
        goodMatch: "تطابق جيد",
        fairMatch: "تطابق عادل",
        weakMatch: "تطابق ضعيف",
        excellentMatch: "تطابق ممتاز",
        poorMatch: "تطابق ضعيف",
        viewDetails: "عرض التفاصيل",
        improveMatch: "كيفية التحسين",
        keySkills: "المهارات الأساسية",
        missingSkills: "المهارات المفقودة",
        matchedSkills: "المهارات المتطابقة",
        missingRequiredSkills: "المهارات المطلوبة المفقودة",
        additionalSkills: "مهارات إضافية",
        experience: "الخبرة",
        experienceMatch: "تطابق الخبرة",
        education: "التعليم",
        educationMatch: "تطابق التعليم",
        overall: "التطابق الإجمالي",
        skillsAnalysis: "تحليل المهارات",
        aiSummary: "ملخص الذكاء الاصطناعي",
        analyzedIn: "تم التحليل في",
        analysisTime: "تم التحليل في {{time}} مللي ثانية",
        jobTitle: "المسمى الوظيفي",
        noResumes: "لم يتم العثور على سير ذاتية. أنشئ واحدة أولاً!",
        resumesLoadError: "فشل تحميل السير الذاتية",
        pleaseSelectResume: "يرجى اختيار سيرة ذاتية",
        pleaseEnterJobDescription: "يرجى إدخال وصف الوظيفة",
        analysisComplete: "اكتمل التحليل!",
        analysisFailed: "فشل تحليل تطابق الوظيفة",
        premiumFeature: "ميزة بريميوم",
        premiumDesc: "مطابق الوظائف بالذكاء الاصطناعي مع التحليل المتقدم والتوصيات متاح لأعضاء Basic وPremium وPro.",
        unlockFeatures: "افتح مطابق الوظائف بالذكاء الاصطناعي:",
        feature1: "تحليل مطابقة الوظائف بالذكاء الاصطناعي",
        feature2: "تفصيل دقيق لتطابق المهارات",
        feature3: "توصيات شخصية",
        feature4: "تحليل نقاط القوة والضعف",
        feature5: "تقييم الخبرة والتعليم",
        upgradeNow: "الترقية الآن",
        backToDashboard: "العودة إلى لوحة التحكم",
        loading: "جاري تحميل تحليل التطابق...",
        totalYears: "سنوات إجمالي",
        highestDegree: "أعلى درجة"
      },
      paymentSuccess: {
        fetchError: "تعذر جلب تفاصيل الاشتراك",
        loadingMessage: "جاري معالجة اشتراكك...",
        title: "تم الدفع بنجاح!",
        subtitle: "مرحباً بتجربتك المطورة",
        errorDescription: "تم الدفع بنجاح، لكن واجهنا مشكلة في تحميل تفاصيل الاشتراك. لا تقلق، اشتراكك مفعل!",
        subscriptionDetails: "تفاصيل اشتراكك",
        planLabel: "الخطة",
        trialTitle: "الفترة التجريبية مفعلة",
        trialRemaining: "لديك {{days}} يوم متبقٍ في نسختك التجريبية",
        nextBillingDate: "تاريخ الفاتورة القادم",
        cvGenerations: "توليد السير الذاتية",
        perMonth: "شهرياً",
        jobApplications: "طلبات التوظيف",
        confirmationTitle: "🎉 تم إرسال رسالة التأكيد!",
        confirmationSubtitle: "تحقق من بريدك للحصول على الإيصال وتفاصيل الاشتراك.",
        thankYouTitle: "شكراً لدفعتك!",
        thankYouSubtitle: "تم تفعيل اشتراكك بنجاح.",
        continueButton: "تابع إلى لوحة التحكم",
        sessionId: "معرّف الجلسة",
        supportPrompt: "تحتاج مساعدة؟ تواصل مع فريق الدعم عبر",
        supportEmail: "support@cvlogs.com"
      },
      paymentFailed: {
        title: "تم إلغاء الدفع",
        subtitle: "لم يتم تفعيل اشتراكك",
        noChargeMessage: "لا تقلق! لم يتم خصم أي مبلغ من حسابك.",
        cancellationReason: "تم إلغاء عملية الدفع أو انقطاعها. قد يحدث ذلك لأسباب متعددة:",
        reasonWindowClosed: "تم إغلاق نافذة الدفع قبل الإكمال",
        reasonDeclined: "تم رفض وسيلة الدفع أو لا يوجد رصيد كافٍ",
        reasonNetwork: "انقطع الاتصال بالشبكة",
        reasonChangedMind: "غيّرت رأيك (لا بأس!)",
        tryAgain: "المحاولة مرة أخرى",
        goHome: "العودة للصفحة الرئيسية",
        support: "الدعم",
        upgradeTitle: "ما زلت مهتماً بالترقية؟",
        upgradeDescription: "خططنا المميزة تفتح لك مزايا قوية لتسريع رحلتك المهنية. اختر الخطة المناسبة لك.",
        viewPlans: "عرض جميع الخطط",
        sessionId: "معرّف الجلسة",
        needHelp: "تحتاج مساعدة بعملية الدفع؟",
        contactSupport: "تواصل مع الدعم: support@cvlogs.com",
        freePlanReminder: "لا يزال بإمكانك استخدام CVLogs مع",
        freePlanLink: "الباقة المجانية"
      },
      // Remote Jobs
      remoteJobs: {
        title: "وظائف عن بُعد",
        subtitle: "وظيفة عن بُعد من شركات حول العالم",
        loading: "جاري تحميل الوظائف عن بُعد...",
        searchPlaceholder: "ابحث عن الوظائف حسب المسمى أو الشركة أو الكلمات المفتاحية...",
        filters: "الفلاتر",
        search: "بحث",
        searching: "جاري البحث...",
        resetFilters: "إعادة تعيين الفلاتر",
        viewAllJobs: "عرض كل الوظائف",
        showing: "عرض",
        of: "من",
        jobs: "وظائف",
        noJobsFound: "لم يتم العثور على وظائف",
        tryAdjusting: "حاول تعديل معايير البحث",
        loadMore: "تحميل المزيد من الوظائف",
        remaining: "متبقي",
        loading: "جاري التحميل...",
        fullTime: "دوام كامل",
        type: "النوع",
        location: "الموقع",
        posted: "تم النشر",
        salary: "الراتب",
        requiredSkills: "المهارات المطلوبة",
        applyOnCompanySite: "قدم على موقع الشركة",
        generateCoverLetterAI: "إنشاء خطاب تغطية بالذكاء الاصطناعي",
        saveToLibrary: "حفظ في المكتبة",
        savedToLibrary: "محفوظ في المكتبة",
        noDescription: "لا يوجد وصف تفصيلي للوظيفة. انقر على \"قدم\" لعرض المزيد من التفاصيل على موقع الشركة.",
        category: "الفئة",
        allCategories: "كل الفئات",
        jobType: "نوع الوظيفة",
        allTypes: "كل الأنواع",
        location: "الموقع",
        locationPlaceholder: "مثال: الولايات المتحدة، أوروبا، في أي مكان",
        filters: "الفلاتر",
        search: "بحث",
        searching: "جاري البحث...",
        resetFilters: "إعادة تعيين الفلاتر",
        noJobs: "لم يتم العثور على وظائف",
        noJobsDesc: "حاول تعديل معايير البحث الخاصة بك",
        viewAllJobs: "عرض جميع الوظائف",
        showing: "عرض",
        of: "من",
        jobs: "وظائف",
        loadMore: "تحميل المزيد من الوظائف",
        remaining: "متبقي",
        loading: "جاري التحميل...",
        loadingJobs: "جاري تحميل الوظائف عن بُعد...",
        jobDescription: "وصف الوظيفة",
        requiredSkills: "المهارات المطلوبة",
        type: "النوع",
        posted: "تاريخ النشر",
        salary: "الراتب",
        applyOnCompanySite: "قدم على موقع الشركة",
        generateCoverLetterAI: "إنشاء خطاب تغطية بالذكاء الاصطناعي",
        saveToLibrary: "حفظ في المكتبة",
        savedToLibrary: "محفوظ في المكتبة",
        noDescriptionAvailable: "لا يوجد وصف وظيفي مفصل. انقر على 'قدم' لعرض المزيد من التفاصيل على موقع الشركة.",
        proMatch: "تطابق احترافي",
        analyzing: "جاري التحليل...",
        today: "اليوم",
        yesterday: "أمس",
        daysAgo: "منذ أيام",
        weeksAgo: "منذ أسابيع",
        createCV: "إنشاء سيرة ذاتية",
        selectResumeFirst: "الرجاء إنشاء أو اختيار سيرة ذاتية لإنشاء CV",
        createCVTooltip: "إنشاء سيرة ذاتية لهذه الوظيفة"
      },
      // Advanced Search
      advancedSearch: {
        title: "البحث المتقدم عن الوظائف",
        subtitle: "ابحث عن الوظائف مع فلاتر متقدمة وترتيب ذكي",
        searchPlaceholder: "ابحث عن الوظائف حسب المسمى أو الكلمات المفتاحية أو الموقع...",
        history: "السجل",
        search: "بحث",
        searching: "جاري البحث...",
        enterSearchTerm: "يرجى إدخال عبارة البحث",
        location: "الموقع",
        locationPlaceholder: "مثل: القاهرة، مصر",
        filters: "الفلاتر",
        search: "بحث",
        searching: "جاري البحث...",
        resetFilters: "إعادة تعيين الفلاتر",
        provider: "الموفر",
        searchTerm: "مصطلح البحث",
        resultsWanted: "النتائج",
        daysOld: "الحد الأقصى للأيام",
        country: "البلد",
        datePosted: "تاريخ النشر",
        pages: "عدد الصفحات",
        remoteJobsOnly: "وظائف عن بُعد فقط",
        fetchDescription: "جلب أوصاف الوظائف",
        history: "السجل",
        searchHistory: "سجل البحث",
        noHistory: "لا يوجد سجل بحث",
        loadedFromHistory: "تم تحميل البحث من السجل",
        refresh: "تحديث",
        noSearchToRefresh: "لا يوجد بحث للتحديث",
        unnamedSearch: "بحث غير مسمى",
        results: "نتائج",
        showing: "عرض",
        noJobs: "لم يتم العثور على وظائف",
        noJobsDesc: "حاول تعديل معايير البحث الخاصة بك",
        remote: "عن بُعد",
        applyNow: "قدم الآن",
        proMatch: "تطابق احترافي",
        fullMatch: "تطابق كامل",
        generateCoverLetter: "إنشاء خطاب تغطية",
        matchWith: "تطابق مع:",
        analyzing: "جاري التحليل...",
        viewMatchAnalysis: "عرض تحليل التطابق",
        saveToLibrary: "حفظ في المكتبة",
        savedToLibrary: "محفوظ في المكتبة",
        saveJobSuccess: "تم حفظ الوظيفة في مكتبتك!",
        saveJobError: "فشل حفظ الوظيفة",
        jobAlreadySaved: "هذه الوظيفة موجودة بالفعل في مكتبتك",
        searchSuccess: "تم العثور على {{count}} وظيفة",
        refreshSuccess: "تم تحديث البحث ووجد {{count}} وظيفة",
        loadError: "فشل تحميل البحث",
        searchError: "فشل البحث عن الوظائف",
        refreshError: "فشل تحديث البحث",
        deleteConfirm: "هل أنت متأكد أنك تريد حذف هذا البحث؟",
        searchDeleted: "تم حذف البحث",
        deleteError: "فشل حذف البحث",
        delete: "حذف",
        selectResume: "يرجى اختيار سيرة ذاتية أولاً",
        noJobDescription: "لا يوجد وصف وظيفي متاح للتحليل",
        fullMatchSuccess: "تم الانتهاء من تحليل التطابق الكامل!",
        fullMatchError: "فشل تحليل تطابق الوظيفة",
        fullMatchUpgrade: "تحليل التطابق الكامل متاح لمشتركي الباقات المدفوعة",
        createResumeFullMatch: "💡 أنشئ سيرة ذاتية لاستخدام تحليل التطابق الكامل",
        premiumFeatureTitle: "ميزة مميزة",
        premiumFeatureDescription: "البحث المتقدم عن الوظائف مع فلاتر قوية وترتيب بالذكاء الاصطناعي متاح لمشتركي الباقات الأساسية والمميزة.",
        unlockHeading: "افتح البحث المتقدم:",
        unlockItem1: "ابحث عبر أكثر من 50 دولة",
        unlockItem2: "فلاتر متقدمة (التاريخ، الموقع، عن بُعد)",
        unlockItem3: "ترتيب للوظائف بالذكاء الاصطناعي",
        unlockItem4: "احفظ عمليات البحث والسجل",
        upgradeNow: "قم بالترقية الآن",
        useBasicSearch: "استخدم البحث الأساسي",
        createCV: "إنشاء سيرة ذاتية"
      },
      // Personal Info Form
      personalInfo: {
        title: "المعلومات الشخصية",
        subtitle: "أخبرنا عن نفسك وكيفية التواصل معك",
        form: {
          name: "اسمك الكامل",
          namePlaceholder: "مثال: أحمد محمد",
          email: "البريد الإلكتروني",
          emailPlaceholder: "مثال: ahmed@example.com",
          phone: "رقم الهاتف",
          phonePlaceholder: "مثال: +20 123 456 7890",
          city: "المدينة",
          cityPlaceholder: "مثال: القاهرة",
          country: "الدولة",
          countryPlaceholder: "مثال: مصر",
          address: "العنوان",
          addressPlaceholder: "مثال: ١٢٣ شارع الرئيسي",
          nameHelper: "أحرف إنجليزية فقط",
          phoneHelper: "أحرف إنجليزية فقط",
          cityHelper: "أحرف إنجليزية فقط",
          countryHelper: "اختر دولتك",
          addressHelper: "أحرف إنجليزية فقط",
          summaryHelper: "يمكن الكتابة بالإنجليزية أو العربية",
          currentLinks: "الروابط الحالية",
          professionalLinks: "الروابط المهنية",
          links: "الروابط المهنية",
          linksDescription: "أضف روابط حساباتك على LinkedIn وGitHub ومعرض أعمالك أو ملفاتك المهنية الأخرى",
          linkedinUrl: "رابط LinkedIn",
          linkedinPlaceholder: "https://www.linkedin.com/in/your-profile",
          githubUrl: "رابط GitHub",
          githubPlaceholder: "https://github.com/your-username",
          portfolioUrl: "رابط المعرض/الموقع",
          portfolioPlaceholder: "https://your-portfolio.com",
          customLink: "رابط آخر (التسمية)",
          customLinkLabel: "التسمية (مثال: Behance، Twitter)",
          customLinkUrl: "الرابط",
          summary: "الملخص المهني",
          summaryPlaceholder: "ملخص مهني مختصر عن نفسك..."
        }
      },
      // Education Form
      education: {
        title: "التعليم",
        subtitle: "أضف خلفيتك التعليمية",
        add: "إضافة تعليم",
        addNew: "إضافة تعليم جديد",
        edit: "تعديل التعليم",
        form: {
          school: "الجامعة/المؤسسة",
          degree: "الدرجة العلمية",
          major: "التخصص",
          city: "المدينة",
          country: "الدولة",
          startDate: "تاريخ البدء",
          endDate: "تاريخ الانتهاء"
        },
        organizationName: "اسم الجامعة/المؤسسة",
        organizationPlaceholder: "مثال: جامعة القاهرة",
        degree: "الدرجة العلمية",
        degreePlaceholder: "مثال: بكالوريوس علوم",
        major: "التخصص/المجال الدراسي",
        majorPlaceholder: "مثال: علوم الحاسب",
        city: "المدينة",
        cityPlaceholder: "مثال: القاهرة",
        country: "الدولة",
        countryPlaceholder: "اختر الدولة",
        startDate: "تاريخ البدء",
        startDatePlaceholder: "اختر تاريخ البدء",
        endDate: "تاريخ الانتهاء",
        endDatePlaceholder: "اختر تاريخ الانتهاء",
        save: "حفظ",
        cancel: "إلغاء",
        delete: "حذف",
        noEntries: "لا توجد إدخالات تعليمية بعد",
        empty: "لم تتم إضافة تعليم بعد",
        emptySubtitle: "أضف إدخال تعليمك الأول للبدء",
        addFirst: "أضف تعليمك الأول"
      },
      // Experience Form
      experience: {
        title: "الخبرة العملية",
        subtitle: "أضف خبرتك العملية",
        add: "إضافة خبرة",
        organization: "اسم الشركة/المؤسسة",
        organizationPlaceholder: "مثال: شركة جوجل",
        jobTitle: "المسمى الوظيفي",
        jobTitlePlaceholder: "مثال: مهندس برمجيات",
        city: "المدينة",
        cityPlaceholder: "مثال: دبي",
        country: "الدولة",
        countryPlaceholder: "مثال: الإمارات",
        workType: "نوع العمل",
        fulltime: "دوام كامل",
        parttime: "دوام جزئي",
        intern: "تدريب",
        freelance: "عمل حر",
        remote: "عن بعد",
        other: "أخرى",
        startDate: "تاريخ البدء",
        endDate: "تاريخ الانتهاء",
        description: "وصف الوظيفة",
        descriptionPlaceholder: "اوصف مسؤولياتك وإنجازاتك...",
        present: "حتى الآن",
        save: "حفظ",
        cancel: "إلغاء",
        edit: "تعديل",
        delete: "حذف",
        noEntries: "لا توجد إدخالات خبرة بعد",
        empty: "لم تتم إضافة خبرة بعد",
        emptySubtitle: "أضف خبرتك العملية الأولى للبدء",
        addFirst: "أضف خبرتك الأولى"
      },
      // Skills Form
      skills: {
        title: "المهارات",
        subtitle: "أضف مهاراتك وكفاءاتك",
        add: "إضافة مهارة",
        addNew: "إضافة مهارة جديدة",
        edit: "تعديل المهارة",
        name: "اسم المهارة",
        namePlaceholder: "مثال: JavaScript، إدارة المشاريع",
        type: "نوع المهارة",
        technical: "تقنية",
        soft: "شخصية",
        other: "أخرى",
        save: "حفظ",
        cancel: "إلغاء",
        delete: "حذف",
        noEntries: "لم تتم إضافة مهارات بعد",
        empty: "لم تتم إضافة مهارات بعد",
        emptySubtitle: "أضف مهارتك الأولى للبدء",
        addFirst: "أضف مهارتك الأولى"
      },
      // Languages Form
      languages: {
        title: "اللغات",
        subtitle: "أضف اللغات التي تتحدثها",
        add: "إضافة لغة",
        addNew: "إضافة لغة جديدة",
        edit: "تعديل اللغة",
        languageName: "اللغة",
        languagePlaceholder: "مثال: الإنجليزية، الإسبانية، العربية",
        proficiencyLevel: "مستوى الإتقان",
        levels: {
          native: "لغة أم",
          fluent: "طليق",
          advanced: "متقدم",
          intermediate: "متوسط",
          beginner: "مبتدئ"
        },
        save: "حفظ",
        cancel: "إلغاء",
        delete: "حذف",
        noEntries: "لم تتم إضافة لغات بعد",
        empty: "لم تتم إضافة لغات بعد",
        emptySubtitle: "أضف لغتك الأولى للبدء",
        addFirst: "أضف لغتك الأولى"
      },
      // Projects Form
      projects: {
        title: "المشاريع",
        subtitle: "اعرض أفضل أعمالك",
        add: "إضافة مشروع",
        addNew: "إضافة مشروع جديد",
        edit: "تعديل المشروع",
        projectTitle: "عنوان المشروع",
        titlePlaceholder: "مثال: منصة متجر إلكتروني",
        technologies: "التقنيات المستخدمة",
        technologiesPlaceholder: "مثال: تطوير الويب، إدارة قواعد البيانات",
        description: "الوصف",
        descriptionPlaceholder: "اوصف مشروعك، الغرض منه، ودورك...",
        links: "الروابط",
        linkName: "اسم الرابط",
        linkNamePlaceholder: "مثال: الموقع الإلكتروني",
        linkUrl: "رابط URL",
        linkUrlPlaceholder: "https://...",
        link: "رابط المشروع",
        linkPlaceholder: "https://...",
        save: "حفظ",
        cancel: "إلغاء",
        delete: "حذف",
        noEntries: "لم تتم إضافة مشاريع بعد",
        empty: "لم تتم إضافة مشاريع بعد",
        emptySubtitle: "أضف مشروعك الأول للبدء",
        addFirst: "أضف مشروعك الأول"
      },
      // Training Form
      training: {
        title: "التدريب والدورات",
        subtitle: "أضف دوراتك التدريبية وشهاداتك",
        add: "إضافة تدريب",
        addNew: "إضافة دورة تدريبية جديدة",
        edit: "تعديل الدورة التدريبية",
        courseTitle: "عنوان الدورة",
        titlePlaceholder: "مثال: تطوير الويب المتقدم",
        organization: "المنظمة التدريبية",
        organizationPlaceholder: "مثال: Udemy، Coursera",
        date: "تاريخ الإنجاز",
        datePlaceholder: "اختر تاريخ الإنجاز",
        certificateLink: "رابط الشهادة",
        linkPlaceholder: "https://...",
        save: "حفظ",
        cancel: "إلغاء",
        delete: "حذف",
        noEntries: "لم تتم إضافة تدريبات بعد",
        empty: "لم تتم إضافة تدريبات بعد",
        emptySubtitle: "أضف دورتك التدريبية الأولى للبدء",
        addFirst: "أضف دورتك التدريبية الأولى"
      },
      // Publications Form
      publications: {
        title: "المنشورات",
        subtitle: "قائمة أعمالك المنشورة",
        add: "إضافة منشور",
        addNew: "إضافة منشور جديد",
        edit: "تعديل المنشور",
        title_field: "عنوان المنشور",
        titlePlaceholder: "مثال: بحث في الذكاء الاصطناعي",
        publisher: "الناشر",
        publisherPlaceholder: "مثال: IEEE",
        publicationDate: "تاريخ النشر",
        publicationDatePlaceholder: "اختر تاريخ النشر",
        link: "الرابط",
        linkPlaceholder: "https://...",
        save: "حفظ",
        cancel: "إلغاء",
        delete: "حذف",
        noEntries: "لم تتم إضافة منشورات بعد",
        empty: "لم تتم إضافة منشورات بعد",
        emptySubtitle: "أضف منشورك الأول للبدء",
        addFirst: "أضف منشورك الأول"
      },
      // Licenses Form
      licenses: {
        title: "التراخيص والشهادات",
        subtitle: "أضف شهاداتك المهنية",
        add: "إضافة رخصة",
        addNew: "إضافة رخصة جديدة",
        edit: "تعديل الرخصة",
        form: {
          title: "عنوان الرخصة/الشهادة",
          titlePlaceholder: "مثال: AWS مهندس حلول معتمد",
          name: "اسم الرخصة/الشهادة",
          namePlaceholder: "مثال: AWS مهندس حلول معتمد",
          issuer: "المنظمة المصدرة",
          issuerPlaceholder: "مثال: Amazon Web Services",
          issueDate: "تاريخ الإصدار",
          issueDatePlaceholder: "اختر تاريخ الإصدار",
          expiryDate: "تاريخ الانتهاء",
          expiryDatePlaceholder: "اختر تاريخ الانتهاء",
          credentialId: "معرف الاعتماد",
          credentialIdPlaceholder: "مثال: ABC123XYZ",
          credentialUrl: "رابط الاعتماد",
          credentialUrlPlaceholder: "https://..."
        },
        save: "حفظ",
        cancel: "إلغاء",
        delete: "حذف",
        noEntries: "لم تتم إضافة تراخيص بعد",
        empty: "لم تتم إضافة تراخيص بعد",
        emptySubtitle: "أضف رخصتك أو شهادتك الأولى للبدء",
        addFirst: "أضف رخصتك الأولى"
      },
      // Awards Form
      awards: {
        title: "الجوائز والإنجازات",
        subtitle: "أبرز إنجازاتك",
        add: "إضافة جائزة",
        addNew: "إضافة جائزة جديدة",
        edit: "تعديل الجائزة",
        form: {
          title: "عنوان الجائزة",
          titlePlaceholder: "مثال: موظف العام",
          organization: "المنظمة المانحة",
          organizationPlaceholder: "مثال: شركة ABC",
          dateReceived: "تاريخ الاستلام",
          dateReceivedPlaceholder: "اختر تاريخ الاستلام",
          link: "رابط الجائزة",
          linkPlaceholder: "https://...",
          description: "الوصف",
          descriptionPlaceholder: "اوصف الإنجاز..."
        },
        save: "حفظ",
        cancel: "إلغاء",
        delete: "حذف",
        noEntries: "لم تتم إضافة جوائز بعد",
        empty: "لم تتم إضافة جوائز بعد",
        emptySubtitle: "أضف جائزتك الأولى للبدء",
        addFirst: "أضف جائزتك الأولى"
      },
      // Volunteer Form
      volunteer: {
        title: "الخبرة التطوعية",
        subtitle: "شارك عملك التطوعي",
        add: "إضافة خبرة تطوعية",
        addNew: "إضافة خبرة تطوعية جديدة",
        edit: "تعديل الخبرة التطوعية",
        organization: "المنظمة",
        organizationPlaceholder: "مثال: الصليب الأحمر",
        role: "الدور",
        rolePlaceholder: "مثال: منظم مجتمعي",
        link: "رابط المنظمة",
        linkPlaceholder: "https://...",
        startDate: "تاريخ البدء",
        startDatePlaceholder: "اختر تاريخ البدء",
        endDate: "تاريخ الانتهاء",
        endDatePlaceholder: "اختر تاريخ الانتهاء",
        description: "الوصف",
        descriptionPlaceholder: "اوصف عملك التطوعي...",
        save: "حفظ",
        cancel: "إلغاء",
        delete: "حذف",
        noEntries: "لم تتم إضافة خبرات تطوعية بعد",
        empty: "لم تتم إضافة خبرات تطوعية بعد",
        emptySubtitle: "أضف خبرتك التطوعية الأولى للبدء",
        addFirst: "أضف خبرتك التطوعية الأولى"
      },
      // Recommendations Form
      recommendations: {
        title: "التوصيات",
        subtitle: "أضف التوصيات المهنية",
        add: "إضافة توصية",
        addNew: "إضافة توصية جديدة",
        edit: "تعديل التوصية",
        name: "اسم الموصي",
        namePlaceholder: "مثال: أحمد علي",
        position: "المنصب",
        positionPlaceholder: "مثال: مدير أول",
        company: "الشركة",
        companyPlaceholder: "مثال: شركة ABC",
        relationship: "العلاقة",
        relationshipPlaceholder: "مثال: مدير مباشر",
        text: "نص التوصية",
        textPlaceholder: "أدخل التوصية...",
        save: "حفظ",
        cancel: "إلغاء",
        delete: "حذف",
        noEntries: "لم تتم إضافة توصيات بعد",
        empty: "لم تتم إضافة توصيات بعد",
        emptySubtitle: "أضف توصيتك الأولى للبدء",
        addFirst: "أضف توصيتك الأولى"
      },
      // Interests Form
      interests: {
        title: "الاهتمامات",
        subtitle: "شارك هواياتك واهتماماتك",
        add: "إضافة اهتمام",
        addNew: "إضافة اهتمام جديد",
        edit: "تعديل الاهتمام",
        name: "الاهتمام",
        interestName: "الاهتمام/الهواية",
        namePlaceholder: "مثال: التصوير، القراءة، المشي",
        save: "حفظ",
        cancel: "إلغاء",
        delete: "حذف",
        noEntries: "لم تتم إضافة اهتمامات بعد",
        empty: "لم تتم إضافة اهتمامات بعد",
        emptySubtitle: "أضف اهتمامك الأول للبدء",
        addFirst: "أضف اهتمامك الأول"
      },
      // Common Form Actions
      formActions: {
        save: "حفظ",
        cancel: "إلغاء",
        edit: "تعديل",
        delete: "حذف",
        add: "إضافة",
        update: "تحديث",
        close: "إغلاق",
        submit: "إرسال"
      },
      // Success Messages
      success: {
        saved: "تم الحفظ بنجاح",
        updated: "تم التحديث بنجاح",
        deleted: "تم الحذف بنجاح",
        added: "تمت الإضافة بنجاح",
        educationAdded: "تمت إضافة التعليم بنجاح",
        educationUpdated: "تم تحديث التعليم بنجاح",
        educationDeleted: "تم حذف التعليم بنجاح",
        experienceAdded: "تمت إضافة الخبرة بنجاح",
        experienceUpdated: "تم تحديث الخبرة بنجاح",
        experienceDeleted: "تم حذف الخبرة بنجاح",
        skillAdded: "تمت إضافة المهارة بنجاح",
        skillUpdated: "تم تحديث المهارة بنجاح",
        skillDeleted: "تم حذف المهارة بنجاح",
        languageAdded: "تمت إضافة اللغة بنجاح",
        languageUpdated: "تم تحديث اللغة بنجاح",
        languageDeleted: "تم حذف اللغة بنجاح"
      },
      // Confirmation Messages
      confirm: {
        delete: "هل أنت متأكد من حذف هذا الإدخال؟",
        deleteEducation: "هل أنت متأكد من حذف هذا الإدخال التعليمي؟",
        deleteExperience: "هل أنت متأكد من حذف هذا الإدخال الخبرة؟",
        deleteSkill: "هل أنت متأكد من حذف هذه المهارة؟"
      },
      // Usage
      usage: {
        resumes: "السير الذاتية",
        aiGenerations: "التحسينات بالذكاء الاصطناعي",
        coverLetters: "رسائل التغطية",
        chatMessages: "رسائل المحادثة (اليوم)",
        noDataAvailable: "بيانات الاستخدام غير متوفرة",
        loading: "جاري تحميل الخطط..."
      },
      // Chatbot
      chatbot: {
        title: "المساعد المهني الذكي",
        subtitle: "اسألني أي شيء عن مسيرتك المهنية",
        welcomeTitle: "مرحباً! كيف يمكنني مساعدتك اليوم؟",
        welcomeMessage: "أنا هنا لمساعدتك في سيرتك الذاتية، رسائل التغطية، البحث عن وظائف، والنصائح المهنية. اسألني أي شيء!",
        newChat: "محادثة جديدة",
        sessions: "سجل المحادثات",
        session: "محادثة",
        messages: "الرسائل",
        noSessions: "لا يوجد سجل محادثات بعد",
        inputPlaceholder: "اكتب رسالتك...",
        inputHint: "اضغط Enter للإرسال، Shift + Enter لسطر جديد",
        suggestion1: "كيف يمكنني تحسين سيرتي الذاتية؟",
        suggestion2: "ساعدني في كتابة رسالة تغطية",
        suggestion3: "ما المهارات التي يجب إضافتها؟",
        suggestion4: "نصائح لمقابلات العمل",
        errorLoadingSessions: "فشل في تحميل سجل المحادثات",
        errorLoadingHistory: "فشل في تحميل المحادثة",
        errorCreatingSession: "فشل في إنشاء محادثة جديدة",
        errorDeletingSession: "فشل في حذف المحادثة",
        errorSendingMessage: "فشل في إرسال الرسالة",
        newSessionCreated: "تم إنشاء محادثة جديدة بنجاح",
        sessionDeleted: "تم حذف المحادثة بنجاح",
        featureResume: "مراجعة السيرة الذاتية",
        featureInterview: "التحضير للمقابلات",
        featureCoverLetter: "رسالة التغطية",
        featureCareer: "النصائح المهنية",
        tryAsking: "جرّب السؤال",
        prompt1: "راجع سيرتي الذاتية",
        prompt2: "ساعدني في التحضير لمقابلة عمل",
        prompt3: "اكتب لي رسالة تغطية",
        prompt4: "ما المهارات التي يجب أن أتعلمها؟"
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { ...resources, tr: {
  "translation": {
    "actions": {
      "add": "Eklemek",
      "edit": "Düzenlemek",
      "delete": "Silmek",
      "remove": "Kaldırmak",
      "cancel": "İptal etmek",
      "confirm": "Onaylamak",
      "save": "Kaydetmek",
      "saving": "Kaydediliyor..."
    },
    "validation": {
      "required": "{{alan}} gerekli",
      "nameRequired": "Ad gerekli",
      "emailRequired": "E-posta gerekli",
      "email": "Geçerli bir e",
      "invalidEmail": "Geçerli bir e",
      "url": "Lütfen geçerli bir URL girin",
      "invalidUrl": "Lütfen geçerli bir URL girin",
      "invalidUrlField": "{{field}} geçerli bir URL olmalıdır (ör. https://example.com)",
      "phone": "Lütfen geçerli bir telefon numarası girin",
      "minLength": "Minimum {min} karakter gerekli",
      "maxLength": "İzin verilen maksimum {max} karakter",
      "pleaseFixErrors": "Lütfen hataları düzeltin:",
      "correctFollowingErrors": "Lütfen aşağıdaki hataları düzeltin:",
      "andMoreErrors": "+{{count}} hata daha",
      "englishOnly": "Lütfen yalnızca İngilizce karakterler kullanın",
      "englishOnlyField": "{{field}} yalnızca İngilizce olmalıdır",
      "bilingualField": "{{field}} yalnızca İngilizce veya Arapça olmalıdır",
      "invalidCharacters": "Geçersiz karakterler içeriyor",
      "invalidCharsField": "{{field}} geçersiz karakterler içeriyor",
      "noFutureDate": "{{field}} gelecekte olamaz",
      "futureDateField": "{{field}} gelecekteki bir tarih olamaz",
      "dateRange": "Başlangıç ​​tarihi bitiş tarihinden önce olmalıdır",
      "startBeforeEnd": "Başlangıç ​​tarihi bitiş tarihinden önce olmalıdır",
      "linkRequired": "Hem bağlantı adı hem de URL gereklidir"
    },
    "nav": {
      "home": "Ev",
      "cvBuilder": "CV Oluşturucu",
      "jobSearch": "İş Arama",
      "remoteJobs": "Uzak İşler",
      "aiAssistant": "Yapay Zeka Asistanı",
      "services": "Hizmetler",
      "about": "Hakkında",
      "contact": "Temas etmek",
      "login": "Oturum aç",
      "register": "Başlayın",
      "logout": "Oturumu Kapat",
      "profile": "Profil",
      "myCV": "CV'lerim",
      "settings": "Ayarlar"
    },
    "dashboard": {
      "welcome": "tekrar hoşgeldiniz",
      "subtitle": "İşte bugün iş aramanızda neler oluyor",
      "errorLoading": "Kontrol paneli verileri yüklenemedi",
      "loading": "Kontrol paneli yükleniyor...",
      "loadingJobs": "İşler yükleniyor...",
      "totalResumes": "Toplam Özgeçmişler",
      "savedJobs": "Kayıtlı İşler",
      "applications": "Uygulamalar",
      "jobsApplied": "Gönderilen İş Başvuruları",
      "jobSearches": "İş Aramaları",
      "activityInsights": "Etkinliğiniz ve Analizleriniz",
      "activityInsightsDesc": "İlerlemenizi ve performansınızı takip edin",
      "momentumTitle": "Haftalık ivme",
      "momentumDesc": "Son aktivitenizin birleşik puanı",
      "focusTitle": "Sonraki en iyi eylemler",
      "focusDesc": "Aramanızı ilerletmek için hızlı kazançlar",
      "timelineTitle": "Son zaman çizelgesi",
      "timelineEmpty": "Henüz etkinlik yok. Analizleri burada görmek için araçları keşfetmeye başlayın",
      "resumeUpdated": "CV güncellendi",
      "jobSaved": "İş kaydedildi",
      "searchTriggered": "İş araması tamamlandı",
      "aiReady": "Yapay zeka geliştirmesi hazır",
      "createFirstInsight": "İçgörülerin kilidini açmak için ilk özgeçmişinizi oluşturun",
      "focusEnhance": "Yapay zeka ile en iyi CV'nizi geliştirin",
      "focusApply": "Hedeflenen iki başvuru gönderin",
      "focusSearch": "İş aramalarınızı yenileyin",
      "momentumHigh": "Harika hız! Seriyi canlı tutun.",
      "thisMonth": "Bu Ay",
      "newApplications": "yeni uygulamalar",
      "recentActivity": "Son Etkinlik",
      "last7Days": "Son 7 gün",
      "profileStrength": "Profil Gücü",
      "complete": "tamamlamak",
      "quickActions": "Hızlı Eylemler",
      "createResume": "Özgeçmiş Oluştur",
      "searchJobs": "İş Ara",
      "aiEnhancement": "Yapay Zeka Geliştirme",
      "remoteJobs": "Uzak İşler",
      "recentResumes": "Son Özgeçmişler",
      "savedJobsList": "Kayıtlı İşler",
      "remoteJobsTitle": "Uzak Fırsatları Keşfedin",
      "remoteJobsDesc": "Dünyanın her yerinden çalışın",
      "exploreRemote": "Tüm Uzak İşleri Keşfedin",
      "viewAll": "Tümünü Görüntüle",
      "noResumes": "Henüz özgeçmiş yok",
      "createFirst": "İlk özgeçmişinizi oluşturun",
      "noJobs": "Henüz kayıtlı iş yok",
      "noRemoteJobs": "Uzak iş yok",
      "startSearching": "İş aramaya başlayın",
      "applicationProgress": "Uygulama İlerlemesi",
      "totalSaved": "Toplam Kaydedilen",
      "applied": "Uygulandı",
      "interviewing": "Röportaj",
      "offers": "Teklifler",
      "successStories": "Başarı Hikayeleri",
      "joinThousands": "Binlerce kişiye katılın",
      "testimonial1": "Hayalimdeki işi 2 haftada buldum!",
      "users": "Kullanıcılar",
      "jobsLanded": "İşler",
      "rating": "Derecelendirme",
      "usersHired": "Kullanıcılar Bu Ay İşe Alındı",
      "avgTime": "Ortalama Mülakat Süresi",
      "days": "günler",
      "satisfactionRate": "Kullanıcı Memnuniyeti",
      "upgradeTitle": "Kariyer Potansiyelinizin Kilidini Açın",
      "upgradeDesc": "Pro'ya yükseltin ve sınırsız özgeçmiş, yapay zeka geliştirmeleri ve öncelikli destek alın",
      "upgradeCTA": "Pro'ya yükseltin",
      "upgradePro": "Pro'ya yükseltin",
      "unlockFeatures": "Tüm premium özelliklerin kilidini açın",
      "unlimitedCV": "Sınırsız CV",
      "advancedAI": "Gelişmiş Yapay Zeka",
      "prioritySupport": "Öncelikli Destek",
      "upgradeNow": "Şimdi Yükselt",
      "proFeature1": "Sınırsız CV oluşturma",
      "proFeature2": "Gelişmiş yapay zeka optimizasyonu",
      "proFeature3": "Öncelikli destek 7/24",
      "proFeature4": "Premium şablonlar",
      "selectCV": "Yapay Zeka Geliştirme için bir CV seçin",
      "selectCVDesc": "Yapay zeka ile hangi CV'yi geliştirmek istediğinizi seçin",
      "noResumesForAI": "Özgeçmiş yok",
      "createCVFirst": "Yapay zeka geliştirmesini kullanmak için önce bir CV oluşturun",
      "aiMatching": "Yapay Zeka İş Eşleştirme",
      "aiMatchingDesc": "Akıllı öneriler",
      "tryNow": "Şimdi Deneyin",
      "explore": "Keşfetmek",
      "aiCoverLetter": "AI Kapak Mektupları",
      "aiCoverLetterDesc": "Saniyeler içinde profesyonel",
      "generate": "Oluştur",
      "trackProgress": "İlerlemenizi takip edin",
      "active": "Aktif",
      "trackJourney": "İş başvurusu yolculuğunuzu takip edin",
      "loadingRemoteJobs": "Uzak işler yükleniyor...",
      "moreRemoteOpportunities": "daha uzak fırsatlar",
      "moreSavedJobs": "daha fazla kayıtlı iş",
      "jobSearch": "İş Arama",
      "advancedSearch": "Gelişmiş Arama",
      "searchWorldwide": "Dünya çapında iş arayın",
      "advancedSearchDesc": "Gelişmiş seçeneklerle filtreleyin"
    },
    "welcomeBanner": {
      "heading": "Kariyer Çalışma Alanınız",
      "description": "Yapay zeka ile özgeçmişinizi oluşturun, eşleşen işleri bulun ve size özel ön mektuplar oluşturun.",
      "createResume": "İlk Özgeçmişinizi Oluşturun",
      "searchJobs": "İş Ara",
      "exploreAI": "Yapay Zeka Özelliklerini Keşfedin"
    },
    "onboarding": {
      "title": "Başlarken Kontrol Listesi",
      "stepsRemaining_one": "{{count}} adım kaldı",
      "stepsRemaining_other": "{{count}} adım kaldı",
      "allDone": "Hepsi bitti! 🎉",
      "step_resume": "İlk özgeçmişinizi oluşturun",
      "step_resume_desc": "Akıllı oluşturucumuzu kullanarak ATS'ye hazır bir CV oluşturun",
      "step_resume_cta": "Özgeçmiş Oluştur",
      "step_search": "İş ara",
      "step_search_desc": "İlgili işleri bulun ve beğendiklerinizi kaydedin",
      "step_search_cta": "İş Ara",
      "step_match": "Yapay zeka iş eşleşmesi çalıştırın",
      "step_match_desc": "Yapay zekanın CV'nizi iş tanımıyla karşılaştırmasına izin verin",
      "step_match_cta": "Şimdi Eşleştir",
      "step_cover": "Bir kapak mektubu oluşturun",
      "step_cover_desc": "Tek tıklamayla özel bir kapak mektubu oluşturun",
      "step_cover_cta": "Oluştur"
    },
    "auth": {
      "welcomeBack": "Tekrar hoşgeldiniz",
      "signInDesc": "Harika CV'ler oluşturmaya devam etmek için hesabınızda oturum açın",
      "emailAddress": "E-posta Adresi",
      "password": "Şifre",
      "enterEmail": "E-postanızı girin",
      "enterPassword": "Şifrenizi girin",
      "forgotPassword": "Şifrenizi mi unuttunuz?",
      "signIn": "Oturum aç",
      "signingIn": "Oturum Açılıyor...",
      "orContinueWith": "Veya ile devam edin",
      "signInGoogle": "Google ile oturum açın",
      "connectingGoogle": "Google'a bağlanılıyor...",
      "noAccount": "Hesabınız yok mu?",
      "signUpNow": "Şimdi kaydolun",
      "getStarted": "Başlayın",
      "createAccount": "Hesabınızı oluşturun ve profesyonel CV'ler oluşturmaya başlayın",
      "firstName": "İlk adı",
      "firstNamePlaceholder": "İlk adı",
      "lastName": "Soy isim",
      "lastNamePlaceholder": "Soy isim",
      "fullName": "Ad Soyad",
      "enterFullName": "Tam adınızı girin",
      "confirmPassword": "Şifreyi Onayla",
      "confirmPasswordPlaceholder": "Şifrenizi onaylayın",
      "createAccountBtn": "Hesap oluşturmak",
      "creatingAccount": "Hesap Oluşturuluyor...",
      "signUpGoogle": "Google'a kaydolun",
      "hasAccount": "Zaten bir hesabınız var mı?",
      "signInNow": "Şimdi oturum açın",
      "email": "E-posta",
      "name": "İsim",
      "forgotPasswordTitle": "Parolanızı mı unuttunuz?",
      "resetInstructions": "E-posta adresinizi girin, size şifrenizi sıfırlamanız için bir bağlantı göndereceğiz.",
      "emailPlaceholder": "E-postanızı girin",
      "sending": "Gönderiliyor...",
      "sendResetLink": "Sıfırlama Bağlantısını Gönder",
      "backToLogin": "Girişe Geri Dön",
      "resetEmailSent": "Şifre sıfırlama e-postası gönderildi! Gelen kutunuzu kontrol edin.",
      "resetEmailSentTitle": "E-postanızı Kontrol Edin",
      "resetEmailSentDesc": "E-posta adresinize şifre sıfırlama bağlantısı gönderdik. Lütfen gelen kutunuzu kontrol edin ve şifrenizi sıfırlamak için talimatları izleyin.",
      "didntReceiveEmail": "E-postayı almadınız mı?",
      "resendEmail": "E-postayı yeniden gönder",
      "verifyEmailTitle": "E-postanızı Doğrulayın",
      "verifyEmailDesc": "E-posta adresinize bir doğrulama bağlantısı gönderdik. Lütfen gelen kutunuzu kontrol edin ve hesabınızı doğrulamak için bağlantıya tıklayın.",
      "verificationEmailSent": "Doğrulama e-postası gönderildi! Gelen kutunuzu kontrol edin.",
      "emailVerified": "E-posta başarıyla doğrulandı!",
      "emailVerifiedTitle": "E-posta Doğrulandı!",
      "emailVerifiedDesc": "E-postanız doğrulandı. Sizi kontrol paneline yönlendiriyoruz...",
      "emailNotVerifiedYet": "E-posta henüz doğrulanmadı. Lütfen gelen kutunuzu kontrol edin.",
      "iVerifiedMyEmail": "E-postamı doğruladım",
      "resendVerificationEmail": "Doğrulama e-postasını yeniden gönder"
    },
    "hero": {
      "title": "Profesyonel Özgeçmişinizi Oluşturun",
      "subtitle": "Profesyonelce hazırlanmış bir özgeçmiş ve ön yazıyla kalabalığın arasından sıyrılın",
      "getStarted": "Şimdi Başlayın",
      "aiPowered": "Yapay Zeka Destekli CV Oluşturucu",
      "landDreamJob": "Hayalinizdeki İşi Bulun",
      "landDreamJobDesc": "İşe alım uzmanları ve işe alım yöneticileri tarafından fark edilmenizi sağlayacak profesyonel bir CV oluşturun. Yapay zeka teknolojimiz özgeçmişinizi maksimum etki ve ATS uyumluluğu için optimize eder.",
      "startBuilding": "Şimdi İnşa Etmeye Başlayın",
      "seeStories": "Başarı Hikayelerini Görün",
      "instantUpgrade": "Anında CV Yükseltme",
      "transformCV": "Mevcut CV'nizi Dönüştürün",
      "transformCVDesc": "Mevcut özgeçmişinizi yükleyin ve yapay zekamızın onu profesyonel biçimlendirme, anahtar kelime optimizasyonu ve sektöre özel iyileştirmelerle anında geliştirmesini izleyin.",
      "uploadUpgrade": "Yükleme ve Yükseltme",
      "previewTransforms": "Dönüşümleri Önizle",
      "smartCreation": "Akıllı CV Oluşturma",
      "buildPerfect": "Mükemmel Özgeçmişinizi Oluşturun",
      "buildPerfectDesc": "Akıllı anketimiz, benzersiz değer teklifinizi işverenlere sergileyen ilgi çekici bir CV oluşturmanızda size yol gösterir.",
      "startSmart": "Smart Builder'ı başlatın",
      "tryDemo": "İnteraktif Demoyu Deneyin"
    },
    "features": {
      "title": "Neden CV Oluşturucumuzu Seçmelisiniz?",
      "subtitle": "Profesyonel bir özgeçmiş oluşturmak için ihtiyacınız olan her şey",
      "templates": "Profesyonel Şablonlar",
      "customize": "Özelleştirilmesi Kolay",
      "download": "Anında İndirme",
      "templatesDesc": "İşe alım uzmanları tarafından tasarlanan düzinelerce ATS dostu şablon arasından seçim yapın",
      "customizeDesc": "Sezgisel sürükle ve bırak düzenleyicimizle özgeçmişinizi kolayca özelleştirin",
      "downloadDesc": "Özgeçmişinizi anında PDF veya DOCX formatında indirin",
      "advancedBuilder": "Gelişmiş CV Oluşturucu",
      "advancedBuilderDesc": "Modern şablonlar ve tam kişiselleştirmeyle profesyonel CV'ler oluşturun",
      "aiChat": "Yapay Zeka Destekli Sohbet",
      "aiChatDesc": "Yapay zeka asistanımızdan kişiselleştirilmiş CV iyileştirme ipuçları alın",
      "multiExport": "Çoklu Dışa Aktarma Formatları",
      "multiExportDesc": "CV'nizi PDF, Word veya HTML formatında kaydedin",
      "autoEnhancement": "Otomatik Geliştirme",
      "autoEnhancementDesc": "Otomatik içerik geliştirme ve SEO optimizasyonu",
      "millionsUsers": "Milyonlarca Kullanıcı",
      "millionsUsersDesc": "Hizmetlerimize güvenen milyonlarca profesyonele katılın",
      "securityPrivacy": "Güvenlik ve Gizlilik",
      "securityPrivacyDesc": "Verileriniz en son güvenlik ve şifrelemeyle korunuyor",
      "optimization": "Akıllı Optimizasyon",
      "optimizationDesc": "Daha iyi görünürlük için yapay zeka destekli içerik önerileri ve anahtar kelime optimizasyonu",
      "atsOptimized": "ATS Dostu",
      "atsOptimizedDesc": "Şablonlarımız büyük şirketlerin kullandığı Başvuru Takip Sistemleri için optimize edilmiştir",
      "bilingualSupport": "İki Dilli Destek",
      "bilingualSupportDesc": "Uygun RTL desteği ve kültürel uyarlamalarla hem İngilizce hem de Arapça CV'ler oluşturun",
      "aiSuggestions": "Yapay Zeka Destekli Öneriler",
      "aiSuggestionsDesc": "CV'nizin öne çıkmasını sağlayacak içerik, biçimlendirme ve anahtar kelimelere ilişkin akıllı öneriler alın"
    },
    "journey": {
      "title": "Nasıl Çalışır?",
      "subtitle": "Mükemmel CV'nizi oluşturmak için basit 4 adımlı sürecimizi takip edin",
      "step1": "Yönteminizi Seçin",
      "step1Desc": "Mevcut CV'nizi yükleyin veya adım adım oluşturucumuzla sıfırdan başlayın",
      "step2": "Akıllı Form veya Yapay Zeka Çıkarma",
      "step2Desc": "Akıllı soruları yanıtlayın veya yapay zekanın mevcut özgeçmişinizden veri çıkarmasına izin verin",
      "step3": "Özelleştir ve Önizle",
      "step3Desc": "İçeriği düzenleyin, şablonları seçin ve profesyonel CV'nizi önizleyin",
      "step4": "İndirin ve Paylaşın",
      "step4Desc": "Birden fazla formatta dışa aktarın ve hayalinizdeki işlere başvurmaya başlayın",
      "getStarted": "Başlayın",
      "tryAI": "AI Chat'i deneyin",
      "previewDemo": "Demoyu Önizle",
      "exportOptions": "Dışa Aktarma Seçenekleri"
    },
    "cvBuilder": {
      "noResumes": "Henüz özgeçmiş yok",
      "getStarted": "İlk profesyonel özgeçmişinizi oluşturarak başlayın",
      "createNew": "Yeni Özgeçmiş Oluştur",
      "createFirst": "İlk Özgeçmişinizi Oluşturun",
      "loading": "Özgeçmişleriniz yükleniyor...",
      "untitled": "Başlıksız Özgeçmiş",
      "edit": "Düzenlemek",
      "createModalTitle": "Yeni Özgeçmiş Oluştur",
      "createModalDesc": "CV belgeniz için bir ad seçin (ör. \"Ön Uç Geliştirici Özgeçmişi 2024\")",
      "resumeName": "CV Dokümanı Adı",
      "resumeNamePlaceholder": "ör. Yazılım Geliştirici Özgeçmişi",
      "resumeNameHint": "Bu, kişisel adınız değil, belgenizin başlığıdır. Bir sonraki adımda kişisel bilgilerinizi ekleyeceksiniz.",
      "createButton": "Özgeçmiş Oluştur",
      "creating": "Oluşturuluyor...",
      "cancel": "İptal etmek"
    },
    "steps": {
      "personalInfo": "Kişisel Bilgiler",
      "education": "Eğitim",
      "experience": "İş Deneyimi",
      "projects": "Projeler",
      "courses": "Eğitim ve Sertifikalar",
      "skills": "Yetenekler",
      "review": "İncele ve İndir"
    },
    "errors": {
      "fillAllFields": "Lütfen tüm alanları doldurun",
      "passwordsDontMatch": "Şifreler eşleşmiyor",
      "passwordTooShort": "Şifre en az 6 karakter olmalıdır",
      "loginFailed": "Giriş başarısız oldu. Lütfen tekrar deneyin.",
      "registrationFailed": "Kayıt başarısız oldu. Lütfen tekrar deneyin.",
      "resetFailed": "Sıfırlama e-postası gönderilemedi",
      "userNotFound": "Bu e-postaya ait hesap bulunamadı",
      "wrongPassword": "Geçersiz şifre",
      "invalidEmail": "Geçersiz e-posta adresi",
      "tooManyRequests": "Çok fazla başarısız deneme. Lütfen daha sonra tekrar deneyin.",
      "userDisabled": "Bu hesap devre dışı bırakıldı",
      "emailInUse": "Bu e-postaya sahip bir hesap zaten mevcut",
      "weakPassword": "Şifre çok zayıf",
      "popupBlocked": "Pop-up engellendi. Lütfen açılır pencerelere izin verin ve tekrar deneyin.",
      "popupClosed": "Oturum açma işlemi iptal edildi",
      "accountExistsDifferent": "Bu e-postayla farklı bir oturum açma yöntemi kullanan bir hesap zaten mevcut",
      "googleSignInFailed": "Google'da oturum açma başarısız oldu. Lütfen tekrar deneyin.",
      "embeddedBrowser": "Google ile Oturum Açma bu tarayıcıda desteklenmiyor. Lütfen Safari veya Chrome'da açın.",
      "networkError": "Ağ hatası. Lütfen bağlantınızı kontrol edin.",
      "emailNotVerified": "Lütfen giriş yapmadan önce e-posta adresinizi doğrulayın.",
      "emailSendFailed": "Doğrulama e-postası gönderilemedi.",
      "checkVerificationFailed": "Doğrulama durumu kontrol edilemedi.",
      "fillRequired": "Lütfen gerekli tüm alanları doldurun",
      "addFailed": "Giriş eklenemedi",
      "updateFailed": "Giriş güncellenemedi",
      "deleteFailed": "Giriş silinemedi",
      "saveFailed": "Değişiklikler kaydedilemedi"
    },
    "embeddedBrowser": {
      "title": "Tarayıcıda Açma Gerekli",
      "description": "Güvenlik nedeniyle Google ile Oturum Açma bu tarayıcıda kullanılamıyor. Lütfen bu sayfayı Safari veya Chrome'da açın.",
      "howTo": "Nasıl devam edilir:",
      "step1": "1. Sağ üstteki menü simgesine (⋯) dokunun",
      "step2": "2. 'Safari'de Aç'ı veya 'Tarayıcıda Aç'ı seçin",
      "step3": "3. Veya aşağıdaki bağlantıyı kopyalayıp tarayıcınıza yapıştırın",
      "copyLink": "Bağlantıyı Kopyala",
      "copied": "Kopyalandı!",
      "tryOpenBrowser": "Tarayıcıda Açmayı Deneyin",
      "useEmail": "Bunun yerine E-posta ile devam edin",
      "googleNotAvailable": "Google ile Oturum Açma (Tarayıcıda Aç)",
      "detectedIn": "Saptanmış:",
      "inAppBrowser": "uygulama içi tarayıcı"
    },
    "success": {
      "saved": "Başarıyla kaydedildi",
      "updated": "Başarıyla güncellendi",
      "deleted": "Başarıyla silindi",
      "added": "Başarıyla eklendi",
      "educationAdded": "Eğitim başarıyla eklendi",
      "educationUpdated": "Eğitim başarıyla güncellendi",
      "educationDeleted": "Eğitim başarıyla silindi",
      "experienceAdded": "Deneyim başarıyla eklendi",
      "experienceUpdated": "Deneyim başarıyla güncellendi",
      "experienceDeleted": "Deneyim başarıyla silindi",
      "skillAdded": "Beceri başarıyla eklendi",
      "skillUpdated": "Beceri başarıyla güncellendi",
      "skillDeleted": "Beceri başarıyla silindi"
    },
    "common": {
      "loading": "Yükleniyor...",
      "error": "Hata",
      "success": "Başarı",
      "save": "Kaydetmek",
      "cancel": "İptal etmek",
      "delete": "Silmek",
      "add": "Eklemek",
      "required": "Gerekli",
      "optional": "İsteğe bağlı",
      "close": "Kapalı",
      "open": "Açık",
      "yes": "Evet",
      "no": "HAYIR",
      "createCV": "Özgeçmiş oluştur"
    },
    "pricing": {
      "title": "Planınızı Seçin",
      "subtitle": "Ücretsiz başlayın, daha fazla özelliğe ihtiyaç duyduğunuzda yükseltin",
      "free": "Özgür",
      "pro": "Profesyonel",
      "month": "/ay",
      "features": "Özellikler",
      "getStarted": "Başlayın",
      "upgrade": "Şimdi Yükselt",
      "popular": "En Popüler"
    },
    "home": {
      "metaTitle": "CvLogs - Profesyonel CV Oluşturucu",
      "metaDescription": "Yapay zeka destekli önerilerle etkileyici profesyonel CV'ler oluşturun",
      "heroTitle": "Dakikalar İçinde Mükemmel CV'nizi Oluşturun",
      "heroSubtitle": "Yapay zeka destekli öneriler ve güzel şablonlarla profesyonel, ATS dostu özgeçmişler oluşturun",
      "heroFeatures": "Dünya çapında 10.000'den fazla profesyonelin güvendiği",
      "continueBuilding": "İnşaata Devam Et",
      "startBuilding": "CV'nizi Oluşturmaya Başlayın",
      "benefits": {
        "title": "Neden CvLogs'u Seçmelisiniz?",
        "professionalTemplates": "Uzmanlar tarafından tasarlanan profesyonel şablonlar",
        "realtimePreview": "Oluştururken gerçek zamanlı önizleme",
        "industrySpecific": "Sektöre özel öneriler",
        "mobileResponsive": "Mobil uyumlu tasarım",
        "secureStorage": "Güvenli bulut depolama",
        "multipleFormats": "Birden fazla formatta dışa aktarın"
      },
      "testimonials": {
        "title": "Kullanıcılarımız Ne Diyor?",
        "user1": "CvLog'lar hayalimdeki işi bulmama yardımcı oldu! Yapay zeka önerileri inanılmaz derecede yardımcı oldu.",
        "user2": "Şablonlar profesyoneldir ve arayüzün kullanımı çok kolaydır.",
        "user3": "Sadece 15 dakikada muhteşem bir CV oluşturdum. Şiddetle tavsiye edilir!"
      },
      "testimonial": {
        "text": "CvLog'lar hayalimdeki işi bulmama yardımcı oldu! Yapay zeka önerileri dikkat çekiciydi ve şablonlar inanılmaz derecede profesyonel görünüyor.",
        "author": "Sarah Johnson",
        "role": "Google'da Yazılım Mühendisi"
      },
      "cta": {
        "title": "Mükemmel CV'nizi Oluşturmaya Hazır mısınız?",
        "subtitle": "Hayallerindeki işe başarıyla ulaşan binlerce profesyonele katılın",
        "button": "Şimdi İnşa Etmeye Başlayın",
        "free": "Başlamak tamamen ücretsizdir"
      }
    },
    "resumeChat": {
      "title": "AI Devam Asistanı | CVLog'lar",
      "description": "Kişiselleştirilmiş CV tavsiyesi alın ve yapay zeka yardımıyla özgeçmişinizi oluşturun",
      "heading": "Yapay Zeka Devam Ettirme Asistanı",
      "subtitle": "Bana özgeçmişinle ilgili her şeyi sor veya adım adım oluşturmana yardım etmeme izin ver",
      "placeholder": "Mesajınızı buraya yazın...",
      "send": "Göndermek",
      "quickQuestions": "Hızlı Sorular",
      "exampleQuestions": {
        "improve": "Özgeçmişimi nasıl geliştirebilirim?",
        "format": "En iyi özgeçmiş formatı nedir?",
        "keywords": "Hangi anahtar kelimeleri eklemeliyim?",
        "experience": "Deneyimimi nasıl tanımlarım?"
      },
      "aiTyping": "AI yazıyor...",
      "welcome": "Hoş geldin! Ben sizin AI özgeçmiş asistanınızım. Bugün sana nasıl yardımcı olabilirim?",
      "buildCV": "Yapay zeka ile CV oluşturun",
      "startBuilding": "İnşa Etmeye Başla"
    },
    "form": {
      "title": "CV'nizi Oluşturun",
      "editTitle": "CV'nizi Düzenleyin",
      "description": "Profesyonel CV'nizi oluşturmak için aşağıdaki adımları izleyin",
      "metaDescription": "Modern form oluşturucumuzla profesyonel CV'nizi adım adım oluşturun",
      "progress": "İlerlemek",
      "progressDescription": "CV tamamlama ilerlemenizi takip edin",
      "step": "Adım",
      "of": "ile ilgili",
      "complete": "Tamamlamak",
      "next": "Sonraki",
      "previous": "Öncesi",
      "skip": "Atlamak",
      "save": "Kaydetmek",
      "saveCV": "CV'yi kaydet",
      "updateCV": "CV'yi güncelle",
      "saving": "Kaydediliyor...",
      "updating": "Güncelleniyor...",
      "finish": "Sona ermek",
      "required": "Gerekli alan",
      "optional": "İsteğe bağlı",
      "languageChanged": "Dil şu şekilde değiştirildi:",
      "readyToSave": "Kaydetmeye hazır!",
      "loading": {
        "title": "CV Verilerinin Yüklenmesi",
        "message": "CV'niz düzenlenmek üzere yüklenirken lütfen bekleyin..."
      },
      "preparing": {
        "title": "Form Hazırlanıyor",
        "message": "CV oluşturucuyu hazırlarken lütfen bekleyin..."
      },
      "steps": {
        "personalInfo": {
          "title": "Kişisel Bilgiler",
          "subtitle": "Temel bilgilerinizle başlayalım",
          "description": "Bu bilgi CV'nizin üst kısmında görünecektir"
        },
        "education": {
          "title": "Eğitim",
          "subtitle": "Eğitim geçmişinizi ekleyin",
          "description": "Derecelerinizi, sertifikalarınızı ve akademik başarılarınızı ekleyin"
        },
        "track": {
          "title": "Kariyer Takibi",
          "subtitle": "Profesyonel alanınızı seçin",
          "description": "Çalıştığınız sektörü veya alanı seçin"
        },
        "experience": {
          "title": "İş Deneyimi",
          "subtitle": "Bize iş geçmişinizden bahsedin",
          "description": "Mesleki deneyiminizi ters kronolojik sırayla listeleyin"
        },
        "projects": {
          "title": "Projeler",
          "subtitle": "Önemli projelerinizi sergileyin",
          "description": "Kişisel, akademik veya profesyonel projeleri dahil edin"
        },
        "certifications": {
          "title": "Sertifikalar",
          "subtitle": "Profesyonel sertifikalarınızı ekleyin",
          "description": "Sertifikaları, lisansları ve profesyonel kimlik bilgilerini ekleyin"
        },
        "skills": {
          "title": "Beceriler ve Diller",
          "subtitle": "Hangi konuda iyisin?",
          "description": "Teknik becerilerinizi, sosyal becerilerinizi ve dillerinizi listeleyin"
        },
        "review": {
          "title": "İncele ve Bitir",
          "subtitle": "Bilgilerinizi gözden geçirin",
          "description": "CV'nizi oluşturmadan önce her şeyin iyi göründüğünü kontrol edin"
        }
      },
      "tracks": {
        "technology": "Teknoloji",
        "healthcare": "Sağlık hizmeti",
        "finance": "Finans",
        "education": "Eğitim",
        "marketing": "Pazarlama",
        "sales": "Satış",
        "design": "Tasarım",
        "engineering": "Mühendislik",
        "management": "Yönetmek",
        "consulting": "Danışmanlık",
        "legal": "Yasal",
        "media": "Medya",
        "research": "Araştırma",
        "operations": "Operasyonlar",
        "hr": "İnsan kaynakları",
        "other": "Diğer"
      },
      "track": {
        "customTrack": "Alanınızı belirtin",
        "customTrackPlaceholder": "Profesyonel alanınıza girin"
      },
      "personalInfo": {
        "fullName": {
          "label": "Ad Soyad",
          "placeholder": "Tam adınızı girin"
        },
        "email": {
          "label": "E-posta Adresi",
          "placeholder": "e-postanız@example.com"
        },
        "phone": {
          "label": "Telefon Numarası",
          "placeholder": "+1 (555) 123-4567"
        },
        "location": {
          "label": "Konum",
          "placeholder": "Şehir, Ülke"
        },
        "website": {
          "label": "Web sitesi/Portföy",
          "placeholder": "https://yourwebsite.com"
        },
        "linkedin": {
          "label": "LinkedIn Profili",
          "placeholder": "https://linkedin.com/in/yourprofile"
        },
        "github": {
          "label": "GitHub Profili",
          "placeholder": "https://github.com/yourusername"
        },
        "summary": {
          "label": "Profesyonel Özet",
          "placeholder": "Mesleki geçmişiniz ve kariyer hedeflerinizin kısa bir özeti..."
        }
      },
      "education": {
        "entry": "Eğitim",
        "degree": "Derece",
        "degreePlaceholder": "Bilgisayar Bilimleri Bilim Lisansı",
        "institution": "Kurum",
        "institutionPlaceholder": "Teknoloji Üniversitesi",
        "field": "Çalışma Alanı",
        "fieldPlaceholder": "Bilgisayar Bilimi",
        "graduationDate": "Mezuniyet Tarihi",
        "location": "Konum",
        "locationPlaceholder": "Şehir, Ülke",
        "addEducation": "Eğitim Ekle"
      },
      "experience": {
        "entry": "Deneyim",
        "position": "Konum",
        "positionPlaceholder": "Yazılım Mühendisi",
        "company": "Şirket",
        "companyPlaceholder": "Teknoloji Şirketi A.Ş.",
        "startDate": "Başlangıç ​​Tarihi",
        "endDate": "Bitiş Tarihi",
        "currentJob": "Şu anda burada çalışıyorum",
        "location": "Konum",
        "locationPlaceholder": "Şehir, Ülke",
        "description": "İş tanımı",
        "descriptionPlaceholder": "Temel sorumluluklarınızı ve başarılarınızı açıklayın...",
        "addExperience": "Deneyim Ekle"
      },
      "projects": {
        "entry": "Proje",
        "name": "Proje Adı",
        "namePlaceholder": "E-ticaret Web Sitesi",
        "url": "Proje URL'si",
        "urlPlaceholder": "https://github.com/user/project",
        "startDate": "Başlangıç ​​Tarihi",
        "endDate": "Bitiş Tarihi",
        "technologies": "Teknolojiler",
        "technologiesPlaceholder": "React, Node.js, MongoDB",
        "technologiesHint": "Teknolojileri virgülle ayırın",
        "description": "Tanım",
        "descriptionPlaceholder": "Projenin kısa açıklaması...",
        "addProject": "Proje Ekle"
      },
      "certifications": {
        "entry": "Sertifikasyon",
        "name": "Sertifika Adı",
        "namePlaceholder": "AWS Sertifikalı Çözüm Mimarı",
        "issuer": "Düzenleyen Kuruluş",
        "issuerPlaceholder": "Amazon Web Hizmetleri",
        "date": "Alındığı Tarih",
        "credentialId": "Kimlik bilgisi kimliği",
        "credentialIdPlaceholder": "ABC123XYZ",
        "url": "Doğrulama URL'si",
        "urlPlaceholder": "https://verify.certification.com",
        "addCertification": "Sertifika Ekle"
      },
      "volunteer": {
        "title": "Gönüllü Deneyimi",
        "entry": "Gönüllü Deneyimi",
        "role": "Rol",
        "organization": "Organizasyon",
        "cause": "Neden",
        "startDate": "Başlangıç ​​Tarihi",
        "endDate": "Bitiş Tarihi"
      },
      "awards": {
        "title": "Ödüller ve Tanınma",
        "entry": "Ödül",
        "name": "Ödül Başlığı",
        "organization": "Düzenleyen Kuruluş",
        "date": "Alındığı Tarih"
      },
      "skills": {
        "technical": "Teknik Beceriler",
        "technicalPlaceholder": "Teknik bir beceri ekleyin ve Enter'a basın",
        "languages": "Diller",
        "languagesPlaceholder": "Bir dil ekleyin (ör. İngilizce - Anadil) ve Enter'a basın",
        "soft": "Yumuşak Beceriler",
        "softPlaceholder": "Yumuşak bir beceri ekleyin ve Enter'a basın",
        "pressEnter": "Eklemek için Enter'a basın"
      },
      "review": {
        "congratulations": "Tebrikler!",
        "completedMessage": "CV'nizi tamamladınız. Artık önizleyebilir ve indirebilirsiniz.",
        "previewCV": "CV'yi önizle",
        "editMore": "Daha Fazlasını Düzenle",
        "sections": "Tamamlanan Bölümler",
        "stats": "CV İstatistikleri",
        "education": "Eğitim Girişleri",
        "experience": "İş Deneyimi",
        "projects": "Projeler",
        "certifications": "Sertifikalar"
      },
      "actions": {
        "add": "Eklemek",
        "edit": "Düzenlemek",
        "delete": "Silmek",
        "remove": "Kaldırmak",
        "cancel": "İptal etmek",
        "confirm": "Onaylamak"
      },
      "validation": {
        "required": "{{alan}} gerekli",
        "nameRequired": "Ad gerekli",
        "emailRequired": "E-posta gerekli",
        "email": "Geçerli bir e",
        "invalidEmail": "Geçerli bir e",
        "url": "Lütfen geçerli bir URL girin",
        "invalidUrl": "Lütfen geçerli bir URL girin",
        "invalidUrlField": "{{field}} geçerli bir URL olmalıdır (ör. https://example.com)",
        "phone": "Lütfen geçerli bir telefon numarası girin",
        "minLength": "Minimum {min} karakter gerekli",
        "maxLength": "İzin verilen maksimum {max} karakter",
        "pleaseFixErrors": "Lütfen hataları düzeltin:",
        "correctFollowingErrors": "Lütfen aşağıdaki hataları düzeltin:",
        "andMoreErrors": "+{{count}} hata daha",
        "englishOnly": "Lütfen yalnızca İngilizce karakterler kullanın",
        "englishOnlyField": "{{field}} yalnızca İngilizce olmalıdır",
        "bilingualField": "{{field}} yalnızca İngilizce veya Arapça olmalıdır",
        "invalidCharacters": "Geçersiz karakterler içeriyor",
        "invalidCharsField": "{{field}} geçersiz karakterler içeriyor",
        "noFutureDate": "{{field}} gelecekte olamaz",
        "futureDateField": "{{field}} gelecekteki bir tarih olamaz",
        "dateRange": "Başlangıç ​​tarihi bitiş tarihinden önce olmalıdır",
        "startBeforeEnd": "Başlangıç ​​tarihi bitiş tarihinden önce olmalıdır",
        "linkRequired": "Hem bağlantı adı hem de URL gereklidir"
      }
    },
    "cvGeneration": {
      "title": "Yapay Zeka CV Oluşturma",
      "subtitle": "Yapay zeka ile profesyonel CV'nizi oluşturun",
      "targetJob": "Hedef İş (İsteğe bağlı)",
      "jobTitle": "İş unvanı",
      "jobTitlePlaceholder": "ör. Kıdemli Ön Uç Geliştiricisi",
      "jobDescription": "İş tanımı",
      "jobDescriptionPlaceholder": "İş tanımını buraya yapıştırın...",
      "generateAICV": "Yeni AI CV Oluştur",
      "generating": "Oluşturuluyor...",
      "templateStyle": "Şablon Stili",
      "atsFriendly": "ATS Dostu",
      "atsFriendlyDesc": "Siyah beyaz, basit format",
      "modernColorful": "Modern Renkli",
      "modernColorfulDesc": "Renklerle canlı tasarım",
      "exportOptions": "Dışa Aktarma Seçenekleri",
      "downloadAsPDF": "PDF olarak indir",
      "downloadAsWord": "Word olarak indir",
      "viewHistory": "Geçmişi Görüntüle",
      "noCVGenerated": "Henüz CV Oluşturulmadı",
      "saveDraft": "Taslağı Kaydet ve Eski CV'yi Görüntüle",
      "noCVGeneratedDesc": "Önizlemeyi burada görmek için ilk yapay zeka destekli CV'nizi oluşturun",
      "generateFirstCV": "İlk CV'nizi Oluşturun",
      "loadingCV": "CV'niz yükleniyor...",
      "cvLoaded": "CV başarıyla yüklendi",
      "enhancingSection": "Bölüm geliştiriliyor...",
      "enhancementSuccess": "CV bölümü başarıyla geliştirildi!",
      "enhancementError": "CV bölümü geliştirilemedi",
      "enhancementNotAvailable": "Geliştirme işleyicisi kullanılamıyor",
      "aiEnhancement": "Yapay Zeka Geliştirme",
      "enhanceSection": "Bölümü Geliştir",
      "enhanceButtonTooltip": "Bu bölümü AI ile geliştirmek için tıklayın",
      "enhancementPrompt": "Geliştirme İstemi",
      "enhancementPromptPlaceholder": "Bu bölümü nasıl geliştirmek istediğinizi açıklayın...",
      "enhancementPromptHint": "Yapay zekanın bu bölümü nasıl geliştirmesi gerektiğine ilişkin özel talimatlar sağlayın",
      "quickSuggestions": "Hızlı öneriler:",
      "cancel": "İptal etmek",
      "enhancing": "Geliştiriliyor...",
      "enhancementModalTitle": "Yapay zeka ile geliştirin",
      "backToBuilder": "İnşaatçıya Geri Dön",
      "enterPromptError": "Lütfen bir istem girin veya bir öneri seçin",
      "aiContentHistory": "Geliştirilmiş Bölüm Geçmişi",
      "aiContent": "Yapay Zeka İçeriği",
      "history": "Tarih",
      "aiContentHistoryDesc": "Yapay zeka ile geliştirilmiş tüm bölümleri istemleri ve zaman damgalarıyla birlikte görüntüleyin",
      "loadingAIContent": "Yapay zeka içeriği yükleniyor...",
      "noAIContent": "Yapay zekayla geliştirilmiş içerik bulunamadı",
      "enhancedContent": "Geliştirilmiş İçerik",
      "enhancedItems": "Geliştirilmiş Öğeler",
      "technicalSkills": "Teknik Beceriler",
      "softSkills": "Yumuşak Beceriler",
      "professionalSummary": "Profesyonel Özet",
      "experienceEnhancements": "Deneyim Geliştirmeleri",
      "projectEnhancements": "Proje Geliştirmeleri",
      "skillsEnhancement": "Beceri Geliştirme",
      "volunteerEnhancements": "Gönüllü Geliştirmeleri",
      "awardsEnhancements": "Ödül Geliştirmeleri",
      "experience": "Deneyim",
      "project": "Proje",
      "volunteer": "Gönüllü",
      "award": "Ödül",
      "item": "Öğe",
      "copy": "Kopyala",
      "copied": "Panoya kopyalandı",
      "copyFailed": "Kopyalanamadı",
      "aiContentError": "AI içeriği yüklenemedi",
      "close": "Kapalı",
      "enhancementSectionMenu": "Yapay Zeka Geliştirme",
      "enhancementSectionMenuDesc": "Yapay zeka ile geliştirilecek bir bölüm seçin",
      "selectSectionToEnhance": "Yapay zeka ile geliştirilecek bir bölüm seçin",
      "professionalSummarySection": "Profesyonel Özet",
      "professionalSummaryDesc": "Profesyonel özetinizi geliştirin",
      "skillsSection": "Yetenekler",
      "skillsDesc": "Becerilerinizi geliştirin bölümü",
      "workExperienceSection": "İş Deneyimi",
      "workExperienceDesc": "Bireysel deneyimleri geliştirin",
      "projectsSection": "Projeler",
      "projectsDesc": "Bireysel projeleri geliştirin",
      "volunteerWorkSection": "Gönüllü Çalışma",
      "volunteerWorkDesc": "Gönüllü deneyimlerini geliştirin",
      "awardsSection": "Ödüller",
      "awardsDesc": "Ödüllerinizi geliştirin",
      "noDataAvailable": "Veri yok",
      "itemsCount": "öğeler",
      "oneItem": "öğe",
      "howEnhancementWorks": "Yapay Zeka Geliştirme Nasıl Çalışır?",
      "enhancementWorkflowDesc": "Yukarıdan bir bölüm seçin, onu nasıl geliştirmek istediğinize ilişkin talimatları sağlayın; yapay zekamız gelişmiş bir sürüm oluşturacaktır. Değişiklikleri uygulamadan önce inceleyebilir ve karşılaştırabilirsiniz.",
      "enhanceCVSections": "CV Bölümlerini Geliştirin",
      "enhancementAvailable": "Yapay Zeka Geliştirme Mevcut!",
      "enhancementHintDesc": "CV'nizi AI ile geliştirmek için iki yol kullanın:",
      "enhancementQuickWay": "Hızlı: \"CV Bölümlerini Geliştir\" düğmesini tıklayın ←",
      "enhancementDirectWay": "Doğrudan: Aşağıdaki herhangi bir bölümün üzerine gelin ve ✨ düğmesine tıklayın",
      "viewThisTemplate": "Bu Şablonu Görüntüle",
      "makeItProfessional": "Daha profesyonel hale getirin",
      "addMoreImpact": "Daha fazla etki ekleyin",
      "focusOnAchievements": "Başarılara odaklanın",
      "makeItConcise": "Kısa ve öz yapın",
      "highlightTechnicalSkills": "Teknik becerileri vurgulayın",
      "emphasizeLeadership": "Liderliği vurgulayın",
      "improveClarity": "Netliği artırın",
      "addQuantifiableResults": "Ölçülebilir sonuçlar ekleyin",
      "description": "Yapay zeka ile profesyonel CV'nizi oluşturun",
      "generatedCV": "Oluşturulan CV",
      "enhance": "Genişletmek",
      "template": "Şablon",
      "regenerate": "Yenile",
      "exportPDF": "PDF'yi dışa aktar",
      "export": "İhracat",
      "regenerateCV": "CV'yi yeniden oluştur",
      "targetJobOptional": "Belirli bir işi hedefleyin (isteğe bağlı)",
      "generateNow": "Oluştur",
      "leaveBlankGeneral": "Genel CV için boş bırakın",
      "aiEnhancedCV": "Yapay Zeka ile Geliştirilmiş CV",
      "quickActions": "Hızlı Eylemler",
      "saving": "Kaydediliyor...",
      "reviewChanges": "Değişiklikleri inceleyin",
      "original": "Orijinal",
      "aiEnhanced": "Geliştirilmiş Yapay Zeka",
      "editing": "Düzenleme",
      "copyOriginal": "Orijinal içeriği kopyala",
      "copyEnhanced": "Geliştirilmiş içeriği kopyala",
      "editManually": "Manuel olarak düzenle",
      "stopEditing": "Düzenlemeyi durdur",
      "editEnhanced": "Geliştirilmiş içeriği düzenleyin...",
      "noOriginalContent": "Orijinal içerik yok",
      "noEnhancedContent": "Geliştirilmiş içerik yok",
      "processing": "İşleme...",
      "editBeforeApplying": "Başvurmadan önce düzenleyin",
      "acceptOrKeepOriginal": "AI sürümünü kabul edin veya orijinali koruyun",
      "decline": "Reddetmek",
      "accept": "Kabul etmek",
      "acceptAndApply": "Kabul Et ve Uygula",
      "changesApplied": "Değişiklikler başarıyla uygulandı!",
      "changesDeclined": "Değişiklikler reddedildi",
      "originalCopied": "Orijinal panoya kopyalandı!",
      "enhancedCopied": "Geliştirilmiş panoya kopyalandı!",
      "templateAndExport": "Şablon ve Dışa Aktarma",
      "currentTemplate": "Mevcut Şablon",
      "change": "Değiştirmek",
      "downloadPDF": "PDF'yi indir",
      "downloadWord": "Word'ü İndir",
      "chooseTemplate": "CV Şablonunuzu Seçin",
      "templatePreviewDesc": "CV'niz için mükemmel şablonu önizleyin ve seçin",
      "generateWithTargetJob": "Hedef İşle Oluştur",
      "generatingPreview": "Önizleme oluşturuluyor...",
      "generatingPDF": "PDF oluşturuluyor...",
      "templateNotFound": "CV şablonu bulunamadı. Lütfen şablonun yüklenmesini bekleyin.",
      "pdfDownloaded": "PDF başarıyla indirildi!",
      "exportFailed": "PDF dışa aktarılamadı",
      "refreshPreview": "Önizlemeyi Yenile",
      "refreshing": "Yenileniyor...",
      "retryPreview": "Önizlemeyi Yeniden Dene",
      "generationHistory": "Nesil Geçmişi",
      "generationHistoryDesc": "Önceki CV nesillerini görüntüleyin ve geri yükleyin",
      "noGenerationHistory": "Henüz nesil geçmişi yok",
      "generationLabel": "Nesil",
      "current": "Akım",
      "skillsGenerated": "Oluşturulan Beceriler",
      "totalGenerations": "Toplam Nesiller",
      "sectionsAvailable": "iyileştirme için mevcut bölümler"
    },
    "cvSelector": {
      "title": "CV'nizi seçin",
      "noCVs": "CV bulunamadı. Önce bir tane yaratın!",
      "selectInstruction": "İş eşleştirme için kullanmak istediğiniz CV'yi seçin",
      "lastUpdated": "Son güncelleme",
      "continue": "Seçilen CV ile devam et"
    },
    "templateSelector": {
      "title": "CV Şablonunuzu Seçin",
      "description": "Bir şablon seçin ve indirmeden önce önizleyin. ATS şablonları başvuru sahibi takip sistemleri için optimize edilmiştir.",
      "atsFriendly": "ATS Dostu Şablonlar",
      "recommended": "Tavsiye edilen",
      "viewTemplate": "Bu Şablonu Görüntüle",
      "coloredDesign": "Renkli ve Tasarım Şablonları",
      "colorfulHeader": "Renkli başlık tasarımı",
      "selected": "Seçildi",
      "generating": "Oluşturuluyor...",
      "downloadPDF": "PDF'yi indir"
    },
    "jobSearch": {
      "title": "İş Arama",
      "subtitle": "LinkedIn, Indeed ve daha fazlasında iş arayın",
      "searchFilters": "Arama Filtreleri",
      "viewHistory": "Geçmişi Görüntüle",
      "searchHistory": "Arama Geçmişi",
      "noHistory": "Arama geçmişi yok",
      "provider": "sağlayıcı",
      "searchTerm": "Arama Dönemi",
      "searchTermPlaceholder": "İş ilanlarını unvana, şirkete göre arayın...",
      "location": "Konum",
      "locationPlaceholder": "ör. Kahire, Mısır",
      "country": "Ülke",
      "resultsWanted": "Sonuçlar",
      "daysOld": "Maksimum Gün Eski",
      "hoursOld": "Saat Eski (maks.)",
      "fetchDescription": "İş Tanımlarını Getir",
      "search": "Aramak",
      "searching": "İş aranıyor...",
      "enterSearchTerm": "Lütfen bir arama terimi girin",
      "searchSuccess": "{count} iş bulundu",
      "noResults": "Hiçbir iş bulunamadı",
      "searchError": "İş arama başarısız oldu",
      "results": "sonuçlar",
      "refresh": "Yenile",
      "refreshSuccess": "Yenilenen arama {count} iş buldu",
      "refreshError": "Arama yenilenemedi",
      "noSearchToRefresh": "Yenilenecek arama yok",
      "loadedFromHistory": "Geçmişten arama yüklendi",
      "loadError": "Arama yüklenemedi",
      "noJobs": "Hiçbir iş bulunamadı",
      "noJobsDesc": "Arama kriterlerinizi ayarlamayı deneyin",
      "remote": "Uzak",
      "viewJob": "İşi Görüntüle",
      "showDescription": "Açıklamayı Göster",
      "hideDescription": "Açıklamayı Gizle",
      "unnamedSearch": "Adsız Arama",
      "history": "Tarih",
      "applyNow": "Şimdi Başvurun",
      "proMatch": "Profesyonel Maç",
      "generateCoverLetter": "Kapak Mektubu Oluştur",
      "matchWith": "Şununla eşleştir:",
      "analyzing": "Analiz ediliyor...",
      "viewMatchAnalysis": "Maç Analizini Görüntüle",
      "saveToLibrary": "Kitaplığa kaydet",
      "savedToLibrary": "Kitaplığa kaydedildi",
      "createCV": "Özgeçmiş oluştur"
    },
    "settings": {
      "title": "Ayarlar",
      "account": "Hesap",
      "profile": "Profil",
      "preferences": "Tercihler",
      "language": "Dil",
      "theme": "Tema",
      "notifications": "Bildirimler",
      "privacy": "Mahremiyet",
      "security": "Güvenlik",
      "billing": "Faturalandırma",
      "subscription": "Abonelik",
      "helpSupport": "Yardım ve Destek"
    },
    "subscription": {
      "title": "Abonelik Planı",
      "currentPlan": "Mevcut Plan",
      "free": "Özgür",
      "pro": "Profesyonel",
      "upgrade": "Güncelleme",
      "downgrade": "Sürüm düşürme",
      "cancel": "İptal etmek",
      "manage": "Aboneliği Yönet",
      "features": "Özellikler",
      "billing": "Faturalandırma",
      "nextBillingDate": "Sonraki Fatura Tarihi",
      "paymentMethod": "Ödeme yöntemi"
    },
    "overview": {
      "title": "Genel Bakış",
      "welcomeBack": "tekrar hoşgeldiniz",
      "summary": "Özet",
      "recentActivity": "Son Etkinlik",
      "stats": "İstatistikler",
      "quickLinks": "Hızlı Bağlantılar"
    },
    "editResume": {
      "sections": "Bölümler",
      "progress": "İlerlemek",
      "previous": "Öncesi",
      "next": "Sonraki",
      "finish": "Bitir ve Oluştur",
      "saveDraft": "Taslağı Kaydet",
      "saveMagic": "CVLogs Magic'i Kaydet ve Gör",
      "saveMagicDesc": "CVLogs zekasının bunu uyarlamasına izin verin",
      "saveAndGenerate": "Kaydet ve Oluştur",
      "previewCV": "CV'yi önizle"
    },
    "tabs": {
      "personal": "Kişisel Bilgiler",
      "education": "Eğitim",
      "experience": "Deneyim",
      "skills": "Yetenekler",
      "languages": "Diller",
      "projects": "Projeler",
      "licenses": "Lisanslar",
      "training": "Eğitim",
      "awards": "Ödüller",
      "volunteer": "Gönüllü",
      "recommendations": "Öneriler",
      "publications": "Yayınlar",
      "interests": "İlgi alanları"
    },
    "jobLibrary": {
      "title": "İş Kitaplığım",
      "myJobLibrary": "İş Kitaplığım",
      "subtitle": "Kaydedilen iş fırsatlarınızı takip edin ve yönetin",
      "loading": "İş Kitaplığınız Yükleniyor...",
      "totalSaved": "Toplam Kaydedilen",
      "avgMatch": "Ortalama Eşleşme",
      "searchPlaceholder": "İşleri unvana, şirkete veya konuma göre arayın...",
      "filters": "Filtreler",
      "refresh": "Yenile",
      "applicationStatus": "Başvuru Durumu",
      "allStatuses": "Tüm Durumlar",
      "jobSource": "İş Kaynağı",
      "allSources": "Tüm Kaynaklar",
      "jobSearchLinkedIn": "İş Arama (LinkedIn/Gerçekten)",
      "remoteJobsSource": "Uzak İşler",
      "advancedSearchSource": "Gelişmiş Arama",
      "reset": "Sıfırla",
      "applyFilters": "Filtreleri Uygula",
      "noSavedJobs": "Kaydedilmiş İş Yok",
      "noSavedJobsDesc": "İşleri İş Arama veya Uzak İşler sayfalarından kaydetmeye başlayın",
      "noJobsMatch": "Filtrelerinizle eşleşen iş yok. Aramanızı ayarlamayı deneyin.",
      "searchJobsBtn": "İş Ara",
      "showing": "Gösterilen",
      "matchWith": "Şununla eşleştir:",
      "fullTime": "Tam zamanlı",
      "remote": "Uzak",
      "jobSearch": "İş Arama",
      "advanced": "Gelişmiş",
      "saved": "Kaydedildi",
      "viewDetails": "Ayrıntıları Görüntüle",
      "removeFromLibrary": "Kitaplıktan kaldır",
      "personalNotes": "Kişisel Notlar",
      "addNotes": "Not Ekle",
      "editNotes": "Notları Düzenle",
      "saveNotes": "Notları Kaydet",
      "cancel": "İptal etmek",
      "jobDescription": "İş tanımı",
      "type": "Tip",
      "location": "Konum",
      "posted": "Gönderildi",
      "requiredSkills": "Gerekli Beceriler",
      "notApplied": "Uygulanmadı",
      "applied": "Uygulandı",
      "interviewing": "Röportaj",
      "rejected": "Reddedilmiş",
      "accepted": "Kabul edildi",
      "filterStatus": "Duruma göre filtrele",
      "filterSource": "Kaynağa Göre Filtrele",
      "showFilters": "Filtreleri Göster",
      "hideFilters": "Filtreleri Gizle",
      "noJobs": "İş İlanı Bulunamadı",
      "noJobsDesc": "Henüz herhangi bir iş kaydetmediniz. İş arayarak başlayın!",
      "startSearching": "İş Aramayı Başlat",
      "generateCoverLetter": "Kapak Mektubu Oluştur",
      "analyzeMatch": "Maçı Analiz Et",
      "proMatch": "Profesyonel Maç",
      "updateStatus": "Durumu Güncelle",
      "notesPlaceholder": "Bu işle ilgili notlarınızı ekleyin...",
      "deleteJob": "İşi Sil",
      "confirmDelete": "Silmeyi Onayla",
      "deleteMessage": "Bu işi kitaplığınızdan kaldırmak istediğinizden emin misiniz?",
      "matchScore": "Maç Skoru",
      "calculating": "Hesaplanıyor...",
      "analyzing": "Analiz ediliyor...",
      "selectCV": "Maç puanını hesaplamak için bir CV seçin",
      "loadingJobs": "İş Kitaplığınız Yükleniyor...",
      "createCV": "Özgeçmiş oluştur"
    },
    "jobSearchPage": {
      "title": "İş Arama",
      "subtitle": "Bir sonraki fırsatınızı bulun",
      "searchPlaceholder": "İş unvanı, anahtar kelimeler veya şirket",
      "locationPlaceholder": "Şehir, eyalet veya posta kodu",
      "searchButton": "İş Ara",
      "filters": "Filtreler",
      "jobType": "İş Türü",
      "experienceLevel": "Deneyim Seviyesi",
      "salary": "Maaş Aralığı",
      "datePosted": "Yayınlanma Tarihi",
      "remote": "Uzak",
      "fullTime": "Tam zamanlı",
      "partTime": "Yarı Zamanlı",
      "contract": "Sözleşme",
      "internship": "Staj",
      "entryLevel": "Giriş Seviyesi",
      "midLevel": "Orta Seviye",
      "seniorLevel": "Kıdemli Seviye",
      "anyTime": "Herhangi Bir Zaman",
      "past24h": "Son 24 Saat",
      "pastWeek": "Geçen Hafta",
      "pastMonth": "Geçen Ay",
      "applyFilters": "Filtreleri Uygula",
      "clearFilters": "Filtreleri Temizle",
      "resultsFound": "bulunan işler",
      "noResults": "İş İlanı Bulunamadı",
      "noResultsDesc": "Arama kriterlerinizi ayarlamayı deneyin",
      "saveJob": "İşi Kaydet",
      "viewJob": "İşi Görüntüle",
      "applyNow": "Şimdi Başvurun",
      "loading": "İşler yükleniyor...",
      "searching": "Arama..."
    },
    "coverLetterModal": {
      "generateTitle": "Kapak Mektubu Oluştur",
      "viewTitle": "Ön yazı",
      "selectResume": "Devam Et'i seçin",
      "chooseResume": "Bir özgeçmiş seçin...",
      "companyName": "Firma Adı",
      "companyPlaceholder": "ör. Google, Microsoft, Amazon",
      "jobTitle": "İş unvanı",
      "jobTitlePlaceholder": "ör. Kıdemli Yazılım Mühendisi, Ürün Yöneticisi",
      "jobDescription": "İş tanımı",
      "jobDescPlaceholder": "İş tanımının tamamını buraya yapıştırın...",
      "customInstructions": "Özel Talimatlar (İstem)",
      "optional": "İsteğe bağlı",
      "customPlaceholder": "örneğin, Liderlik deneyimimi vurgulayın, Python becerilerimi vurgulayın, Resmi bir ton kullanın",
      "generateBtn": "AI ile Kapak Mektubu Oluşturun",
      "generatingBtn": "Kapak Mektubu oluşturuluyor...",
      "exportPDF": "PDF'yi dışa aktar",
      "preparing": "Hazırlanıyor...",
      "edit": "Düzenlemek",
      "save": "Kaydetmek",
      "regenerate": "Yenile",
      "required": "Lütfen bir özgeçmiş seçin",
      "requiredCompany": "Lütfen şirket adını girin",
      "requiredTitle": "Lütfen iş unvanını girin",
      "requiredDesc": "Lütfen iş tanımını girin",
      "successGenerate": "Kapak mektubu oluşturuldu",
      "successRegenerate": "Kapak mektubu başarıyla yeniden oluşturuldu",
      "successUpdate": "Kapak mektubu başarıyla güncellendi",
      "errorGenerate": "Kapak mektubu oluşturulamadı",
      "errorRegenerate": "Kapak mektubu yeniden oluşturulamadı",
      "errorUpdate": "Kapak mektubu güncellenemedi",
      "errorResumes": "Özgeçmişler yüklenemedi"
    },
    "coverLetter": {
      "title": "Kapak Mektubu Oluşturucu",
      "history": "Kapak Mektubu Geçmişi",
      "historyTitle": "Kapak Mektubu Geçmişi",
      "historySubtitle": "Oluşturulan kapak mektuplarınızı görüntüleyin ve yönetin",
      "generate": "Kapak Mektubu Oluştur",
      "generating": "Oluşturuluyor...",
      "selectJob": "Bir İş Seçin",
      "selectCV": "CV'nizi seçin",
      "jobTitle": "İş unvanı",
      "company": "Şirket",
      "jobDescription": "İş tanımı",
      "customInstructions": "Özel Talimatlar (İsteğe Bağlı)",
      "customPlaceholder": "Vurgulamak istediğiniz belirli noktaları ekleyin...",
      "generateButton": "Kapak Mektubu Oluştur",
      "download": "İndirmek",
      "copy": "Panoya kopyala",
      "edit": "Düzenlemek",
      "delete": "Silmek",
      "preview": "Önizleme",
      "createdOn": "Oluşturulma tarihi",
      "noHistory": "Henüz Ön Yazı Yok",
      "noHistoryDesc": "Burada görmek için ilk kapak mektubunuzu oluşturun",
      "createCustom": "Özel Kapak Mektubu Oluşturun",
      "letter": "Mektup",
      "letters": "Edebiyat",
      "searchPlaceholder": "Şirkete veya iş unvanına göre arayın...",
      "allResumes": "Tüm Özgeçmişler",
      "untitledResume": "Başlıksız Özgeçmiş",
      "noLettersFound": "Kapak mektubu bulunamadı",
      "generateFirst": "İlk ön mektubunuzu iş arama veya kütüphane sayfalarından oluşturun",
      "adjustFilters": "Filtrelerinizi ayarlamayı deneyin",
      "goToSearchBtn": "İş Aramaya Git",
      "edited": "Düzenlendi",
      "view": "Görüş",
      "editLetter": "Kapak Mektubunu Düzenle",
      "deleteTitle": "Kapak Mektubu Sil",
      "deleteMessage": "Bu ön mektubu silmek istediğinizden emin misiniz? Bu eylem geri alınamaz.",
      "deleteConfirm": "Bu ön mektubu silmek istediğinizden emin misiniz?",
      "cancel": "İptal etmek",
      "startGenerating": "Kapak Mektubu Oluştur",
      "copied": "Panoya kopyalandı!",
      "downloadSuccess": "Kapak mektubu başarıyla indirildi!",
      "loading": "Kapak mektupları yükleniyor..."
    },
    "jobMatch": {
      "title": "Yapay Zeka İş Eşleştirici",
      "subtitle": "Özgeçmişinizin herhangi bir iş tanımına ne kadar iyi uyduğunu analiz edin",
      "analyzeProfile": "Profilimi Analiz Et",
      "selectCV": "CV'nizi seçin",
      "selectResume": "Özgeçmişinizi Seçin",
      "selectJob": "Analiz Edilecek Bir İş Seçin",
      "untitledResume": "Başlıksız Özgeçmiş",
      "jobDescription": "İş tanımı",
      "jobDescriptionPlaceholder": "İş tanımının tamamını buraya yapıştırın...",
      "analyzeMatch": "AI ile Eşleştirmeyi Analiz Edin",
      "matchScore": "Maç Skoru",
      "strengths": "Güçlü yönler",
      "weaknesses": "İyileştirme Alanları",
      "gaps": "Beceri Boşlukları",
      "recommendations": "Yapay Zeka Önerileri",
      "analyzing": "Maç Analiz Ediliyor...",
      "noMatch": "Eşleşme Verisi Yok",
      "noMatchDesc": "Eşleşme analizini görmek için bir CV ve iş seçin",
      "readyToAnalyze": "Analize Hazır",
      "readyToAnalyzeDesc": "Pozisyona ne kadar iyi uyduğunuza dair yapay zeka destekli öngörüler almak için özgeçmişinizi seçin ve bir iş tanımı yapıştırın.",
      "strongMatch": "Güçlü Eşleşme",
      "goodMatch": "İyi Eşleşme",
      "fairMatch": "Adil Maç",
      "weakMatch": "Zayıf Eşleşme",
      "excellentMatch": "Mükemmel Maç",
      "poorMatch": "Kötü Maç",
      "viewDetails": "Ayrıntıları Görüntüle",
      "improveMatch": "Nasıl Geliştirilir",
      "keySkills": "Anahtar Beceriler",
      "missingSkills": "Eksik Beceriler",
      "matchedSkills": "Eşleşen Beceriler",
      "missingRequiredSkills": "Gerekli Becerilerin Eksikliği",
      "additionalSkills": "Ek Beceriler",
      "experience": "Deneyim",
      "experienceMatch": "Deneyim Maçı",
      "education": "Eğitim",
      "educationMatch": "Eğitim Maçı",
      "overall": "Genel Maç",
      "skillsAnalysis": "Beceri Analizi",
      "aiSummary": "Yapay Zeka Özeti",
      "analyzedIn": "Analiz edildi",
      "analysisTime": "{{time}}ms içinde analiz edildi",
      "jobTitle": "İş unvanı",
      "noResumes": "Özgeçmiş bulunamadı. Önce bir tane yaratın!",
      "resumesLoadError": "Özgeçmişler yüklenemedi",
      "pleaseSelectResume": "Lütfen bir özgeçmiş seçin",
      "pleaseEnterJobDescription": "Lütfen bir iş tanımı girin",
      "analysisComplete": "Analiz tamamlandı!",
      "analysisFailed": "İş eşleşmesi analiz edilemedi",
      "premiumFeature": "Premium Özellik",
      "premiumDesc": "Gelişmiş analiz ve öneriler sunan AI Job Matcher, Basic, Premium ve Pro üyelere açıktır.",
      "unlockFeatures": "AI Job Matcher'ın kilidini açın:",
      "feature1": "Yapay zeka destekli iş eşleştirme analizi",
      "feature2": "Ayrıntılı beceri eşleşmesi dökümü",
      "feature3": "Kişiselleştirilmiş öneriler",
      "feature4": "Güçlü ve zayıf yön analizi",
      "feature5": "Deneyim ve eğitim puanlaması",
      "upgradeNow": "Şimdi Yükselt",
      "backToDashboard": "Kontrol Paneline Geri Dön",
      "loading": "Maç analizi yükleniyor...",
      "totalYears": "toplam yıl",
      "highestDegree": "en yüksek derece"
    },
    "paymentSuccess": {
      "fetchError": "Abonelik ayrıntıları getirilemiyor",
      "loadingMessage": "Aboneliğiniz işleniyor...",
      "title": "Ödeme Başarılı!",
      "subtitle": "Yükseltilmiş deneyiminize hoş geldiniz",
      "errorDescription": "Ödemeniz başarılı oldu ancak abonelik ayrıntılarınızı yüklerken sorun yaşıyoruz. Endişelenmeyin, aboneliğiniz aktif!",
      "subscriptionDetails": "Abonelik Ayrıntılarınız",
      "planLabel": "Planı",
      "trialTitle": "Deneme Süresi Aktif",
      "trialRemaining": "Ücretsiz deneme sürenizde {{days}} gününüz kaldı",
      "nextBillingDate": "Sonraki fatura tarihi",
      "cvGenerations": "CV Nesilleri",
      "perMonth": "aylık",
      "jobApplications": "İş Başvuruları",
      "confirmationTitle": "🎉 Onay e-postası gönderildi!",
      "confirmationSubtitle": "Makbuz ve abonelik ayrıntıları için gelen kutunuzu kontrol edin.",
      "thankYouTitle": "Ödemeniz için teşekkür ederiz!",
      "thankYouSubtitle": "Aboneliğiniz başarıyla etkinleştirildi.",
      "continueButton": "Kontrol Paneline Devam Et",
      "sessionId": "Oturum Kimliği",
      "supportPrompt": "Yardıma mı ihtiyacınız var? Destek ekibimizle şu adresten iletişime geçin:",
      "supportEmail": "destek@cvlogs.com"
    },
    "paymentFailed": {
      "title": "Ödeme İptal Edildi",
      "subtitle": "Aboneliğiniz etkinleştirilmedi",
      "noChargeMessage": "Merak etme! Hesabınıza herhangi bir ödeme yapılmadı.",
      "cancellationReason": "Ödemeniz iptal edildi veya kesintiye uğradı. Bu çeşitli nedenlerle gerçekleşebilir:",
      "reasonWindowClosed": "Ödeme penceresi tamamlanmadan kapatıldı",
      "reasonDeclined": "Ödeme yöntemi reddedildi veya bakiye yetersiz",
      "reasonNetwork": "Ağ bağlantısı kesildi",
      "reasonChangedMind": "Fikrini değiştirdin (sorun değil!)",
      "tryAgain": "Tekrar deneyin",
      "goHome": "Eve Git",
      "support": "Destek",
      "upgradeTitle": "Hala yükseltmeyle ilgileniyor musunuz?",
      "upgradeDescription": "Premium planlarımız kariyer yolculuğunuzu hızlandıracak güçlü özelliklerin kilidini açar. İhtiyaçlarınıza uygun bir plan seçin.",
      "viewPlans": "Tüm planları görüntüle",
      "sessionId": "Oturum Kimliği",
      "needHelp": "Ödeme konusunda yardıma mı ihtiyacınız var?",
      "contactSupport": "Destek Ekibiyle İletişime Geçin: support@cvlogs.com",
      "freePlanReminder": "CVLog'ları hâlâ bizim ürünlerimizle kullanabilirsiniz.",
      "freePlanLink": "ücretsiz plan"
    },
    "remoteJobs": {
      "title": "Uzak İşler",
      "subtitle": "dünya çapındaki şirketlerden uzak pozisyonlar",
      "loading": "Yükleniyor...",
      "searchPlaceholder": "İşleri başlığa, şirkete veya anahtar kelimelere göre arayın...",
      "filters": "Filtreler",
      "search": "Aramak",
      "searching": "Arama...",
      "resetFilters": "Filtreleri Sıfırla",
      "viewAllJobs": "Tüm İşleri Görüntüle",
      "showing": "Gösterilen",
      "of": "ile ilgili",
      "jobs": "işler",
      "noJobsFound": "İş İlanı Bulunamadı",
      "tryAdjusting": "Arama kriterlerinizi ayarlamayı deneyin",
      "loadMore": "Daha Fazla İş Yükle",
      "remaining": "geriye kalan",
      "fullTime": "Tam zamanlı",
      "type": "Tip",
      "location": "Konum",
      "posted": "Gönderildi",
      "salary": "Maaş",
      "requiredSkills": "Gerekli Beceriler",
      "applyOnCompanySite": "Şirket Sitesinden Başvurun",
      "generateCoverLetterAI": "AI ile Kapak Mektubu Oluşturun",
      "saveToLibrary": "Kitaplığa Kaydet",
      "savedToLibrary": "Kitaplığa kaydedildi",
      "noDescription": "Detaylı iş tanımı mevcut değil. Şirketin web sitesinde daha fazla ayrıntı görüntülemek için \"Uygula\"yı tıklayın.",
      "category": "Kategori",
      "allCategories": "Tüm Kategoriler",
      "jobType": "İş Türü",
      "allTypes": "Tüm Türler",
      "locationPlaceholder": "ör. ABD, Avrupa, Dünya çapında",
      "noJobs": "İş İlanı Bulunamadı",
      "noJobsDesc": "Arama kriterlerinizi ayarlamayı deneyin",
      "loadingJobs": "Uzak İşler Yükleniyor...",
      "jobDescription": "İş tanımı",
      "noDescriptionAvailable": "Detaylı iş tanımı mevcut değil. Şirketin web sitesinde daha fazla ayrıntı görüntülemek için 'Uygula'yı tıklayın.",
      "proMatch": "Profesyonel Maç",
      "analyzing": "Analiz ediliyor...",
      "today": "Bugün",
      "yesterday": "Dün",
      "daysAgo": "günler önce",
      "weeksAgo": "haftalar önce",
      "createCV": "Özgeçmiş oluştur",
      "selectResumeFirst": "CV oluşturmak için lütfen bir özgeçmiş oluşturun veya seçin",
      "createCVTooltip": "Bu iş için CV oluştur"
    },
    "advancedSearch": {
      "title": "Gelişmiş İş Arama",
      "subtitle": "Gelişmiş filtrelere ve sıralamaya sahip işleri arayın",
      "searchPlaceholder": "İşleri başlığa, anahtar kelimelere, konuma göre arayın...",
      "history": "Tarih",
      "search": "Aramak",
      "searching": "Arama...",
      "enterSearchTerm": "Lütfen bir arama sorgusu girin",
      "location": "Konum",
      "locationPlaceholder": "ör. Kahire, Mısır",
      "filters": "Filtreler",
      "resetFilters": "Filtreleri Sıfırla",
      "provider": "sağlayıcı",
      "searchTerm": "Arama Dönemi",
      "resultsWanted": "Sonuçlar",
      "daysOld": "Maksimum Gün Eski",
      "country": "Ülke",
      "datePosted": "Yayınlanma Tarihi",
      "pages": "Sayfalar",
      "remoteJobsOnly": "Yalnızca Uzak İşler",
      "fetchDescription": "İş Tanımlarını Getir",
      "searchHistory": "Arama Geçmişi",
      "noHistory": "Arama geçmişi yok",
      "loadedFromHistory": "Geçmişten arama yüklendi",
      "refresh": "Yenile",
      "noSearchToRefresh": "Yenilenecek arama yok",
      "unnamedSearch": "Adsız Arama",
      "results": "sonuçlar",
      "showing": "Gösterilen",
      "noJobs": "Hiçbir iş bulunamadı",
      "noJobsDesc": "Arama kriterlerinizi ayarlamayı deneyin",
      "remote": "Uzak",
      "applyNow": "Şimdi Başvurun",
      "proMatch": "Profesyonel Maç",
      "fullMatch": "Tam Eşleşme",
      "generateCoverLetter": "Kapak Mektubu Oluştur",
      "matchWith": "Şununla eşleştir:",
      "analyzing": "Analiz ediliyor...",
      "viewMatchAnalysis": "Maç Analizini Görüntüle",
      "saveToLibrary": "Kitaplığa kaydet",
      "savedToLibrary": "Kitaplığa kaydedildi",
      "saveJobSuccess": "İş kitaplığınıza kaydedildi!",
      "saveJobError": "İş kaydedilemedi",
      "jobAlreadySaved": "Bu iş zaten kütüphanenizde",
      "searchSuccess": "{{count}} iş bulundu",
      "refreshSuccess": "Yenilenen arama {{count}} iş buldu",
      "loadError": "Arama yüklenemedi",
      "searchError": "İş arama başarısız oldu",
      "refreshError": "Arama yenilenemedi",
      "deleteConfirm": "Bu aramayı silmek istediğinizden emin misiniz?",
      "searchDeleted": "Arama silindi",
      "deleteError": "Arama silinemedi",
      "delete": "Silmek",
      "selectResume": "Lütfen önce bir özgeçmiş seçin",
      "noJobDescription": "Analiz için iş tanımı yok",
      "fullMatchSuccess": "Tam Maç analizi tamamlandı!",
      "fullMatchError": "İş eşleşmesi analiz edilemedi",
      "fullMatchUpgrade": "Tam Eşleştirme Temel, Premium ve Pro kullanıcılar için mevcuttur",
      "createResumeFullMatch": "💡 Tam Eşleşme özelliğini kullanmak için bir özgeçmiş oluşturun",
      "premiumFeatureTitle": "Premium Özellik",
      "premiumFeatureDescription": "Güçlü filtrelere ve yapay zeka destekli sıralamaya sahip Gelişmiş İş Arama, Basic ve Premium üyeler için mevcuttur.",
      "unlockHeading": "Gelişmiş Aramanın Kilidini Açın:",
      "unlockItem1": "50'den fazla ülkede arama yapın",
      "unlockItem2": "Gelişmiş filtreler (tarih, konum, uzak)",
      "unlockItem3": "Yapay zeka destekli iş sıralaması",
      "unlockItem4": "Aramaları ve geçmişi kaydedin",
      "upgradeNow": "Şimdi Yükselt",
      "useBasicSearch": "Temel Aramayı Kullan",
      "createCV": "Özgeçmiş oluştur"
    },
    "personalInfo": {
      "title": "Kişisel Bilgiler",
      "subtitle": "Bize kendinizden ve sizinle nasıl iletişime geçeceğimizden bahsedin",
      "form": {
        "name": "Tam Adınız",
        "namePlaceholder": "örneğin John Doe",
        "nameHelper": "Yalnızca İngilizce karakterler",
        "email": "E-posta",
        "emailPlaceholder": "ör. john.doe@example.com",
        "phone": "Telefon",
        "phonePlaceholder": "ör. +1 234 567 890",
        "phoneHelper": "Yalnızca İngilizce karakterler",
        "city": "Şehir",
        "cityPlaceholder": "örneğin New York",
        "cityHelper": "Yalnızca İngilizce karakterler",
        "country": "Ülke",
        "countryPlaceholder": "örneğin Amerika Birleşik Devletleri",
        "countryHelper": "Ülkenizi seçin",
        "address": "Adres",
        "addressPlaceholder": "ör. 123 Ana Cadde",
        "addressHelper": "Yalnızca İngilizce karakterler",
        "professionalLinks": "Profesyonel Bağlantılar",
        "links": "Profesyonel Bağlantılar",
        "linksDescription": "LinkedIn, GitHub, portföy veya diğer profesyonel profillerinize bağlantılar ekleyin",
        "linkedinUrl": "LinkedIn URL'si",
        "linkedinPlaceholder": "https://www.linkedin.com/in/your-profile",
        "githubUrl": "GitHub URL'si",
        "githubPlaceholder": "https://github.com/your-username",
        "portfolioUrl": "Portföy/Web Sitesi URL'si",
        "portfolioPlaceholder": "https://your-portfolio.com",
        "customLink": "Diğer Bağlantı (Etiket)",
        "customLinkLabel": "Etiket (ör. Behance, Twitter)",
        "customLinkUrl": "URL'si",
        "currentLinks": "Güncel Bağlantılar",
        "summary": "Profesyonel Özet",
        "summaryPlaceholder": "Kendiniz hakkında kısa bir mesleki özet...",
        "summaryHelper": "İngilizce veya Arapça yazılabilir"
      }
    },
    "education": {
      "title": "Eğitim",
      "subtitle": "Eğitim geçmişinizi ekleyin",
      "add": "Eğitim Ekle",
      "addNew": "Yeni Eğitim Ekle",
      "edit": "Eğitimi Düzenle",
      "form": {
        "school": "Okul/Üniversite",
        "degree": "Derece",
        "major": "Çalışma Alanı",
        "city": "Şehir",
        "country": "Ülke",
        "startDate": "Başlangıç ​​Tarihi",
        "endDate": "Bitiş Tarihi"
      },
      "organizationName": "Kurum/Üniversite Adı",
      "organizationPlaceholder": "ör. Harvard Üniversitesi",
      "degree": "Derece",
      "degreePlaceholder": "örneğin, Lisans Diploması",
      "major": "Çalışma Alanı/Anadal",
      "majorPlaceholder": "ör. Bilgisayar Bilimi",
      "city": "Şehir",
      "cityPlaceholder": "örneğin Cambridge",
      "country": "Ülke",
      "countryPlaceholder": "Ülke seçin",
      "startDate": "Başlangıç ​​Tarihi",
      "startDatePlaceholder": "Başlangıç ​​tarihini seçin",
      "endDate": "Bitiş Tarihi",
      "endDatePlaceholder": "Bitiş tarihini seçin",
      "save": "Kaydetmek",
      "cancel": "İptal etmek",
      "delete": "Silmek",
      "noEntries": "Henüz eğitim girişi yok",
      "empty": "Henüz eğitim girişi eklenmedi",
      "emptySubtitle": "Başlamak için ilk eğitim girişinizi ekleyin",
      "addFirst": "İlk Eğitiminizi Ekleyin"
    },
    "experience": {
      "title": "İş Deneyimi",
      "subtitle": "İş deneyiminizi ekleyin",
      "add": "Deneyim Ekle",
      "organization": "Şirket/Kuruluş Adı",
      "organizationPlaceholder": "örneğin Google Inc.",
      "jobTitle": "İş unvanı",
      "jobTitlePlaceholder": "ör. Yazılım Mühendisi",
      "city": "Şehir",
      "cityPlaceholder": "ör. Mountain View",
      "country": "Ülke",
      "countryPlaceholder": "örneğin Amerika Birleşik Devletleri",
      "workType": "İş Türü",
      "fulltime": "Tam zamanlı",
      "parttime": "Yarı zamanlı",
      "intern": "Staj",
      "freelance": "Serbest çalışan",
      "remote": "Uzak",
      "other": "Diğer",
      "startDate": "Başlangıç ​​Tarihi",
      "endDate": "Bitiş Tarihi",
      "description": "İş tanımı",
      "descriptionPlaceholder": "Sorumluluklarınızı ve başarılarınızı açıklayın...",
      "present": "Sunmak",
      "save": "Kaydetmek",
      "cancel": "İptal etmek",
      "edit": "Düzenlemek",
      "delete": "Silmek",
      "noEntries": "Henüz deneyim girişi yok",
      "empty": "Henüz deneyim eklenmedi",
      "emptySubtitle": "Başlamak için ilk iş deneyiminizi ekleyin",
      "addFirst": "İlk Deneyiminizi Ekleyin"
    },
    "skills": {
      "title": "Yetenekler",
      "subtitle": "Becerilerinizi ve yeterliliklerinizi ekleyin",
      "add": "Beceri Ekle",
      "addNew": "Yeni Beceri Ekle",
      "edit": "Beceriyi Düzenle",
      "name": "Beceri Adı",
      "namePlaceholder": "ör. JavaScript, Proje Yönetimi",
      "type": "Beceri Türü",
      "technical": "Teknik",
      "soft": "Yumuşak",
      "other": "Diğer",
      "save": "Kaydetmek",
      "cancel": "İptal etmek",
      "delete": "Silmek",
      "noEntries": "Henüz beceri eklenmedi",
      "empty": "Henüz beceri eklenmedi",
      "emptySubtitle": "Başlamak için ilk becerinizi ekleyin",
      "addFirst": "İlk Becerinizi Ekleyin"
    },
    "languages": {
      "title": "Diller",
      "subtitle": "Konuştuğunuz dilleri ekleyin",
      "add": "Dil Ekle",
      "addNew": "Yeni Dil Ekle",
      "edit": "Dili Düzenle",
      "languageName": "Dil",
      "languagePlaceholder": "ör. İngilizce, İspanyolca, Arapça",
      "proficiencyLevel": "Yeterlilik Düzeyi",
      "levels": {
        "native": "Yerli",
        "fluent": "Akıcı",
        "advanced": "Gelişmiş",
        "intermediate": "Orta seviye",
        "beginner": "Acemi"
      },
      "save": "Kaydetmek",
      "cancel": "İptal etmek",
      "delete": "Silmek",
      "noEntries": "Henüz dil eklenmedi",
      "empty": "Henüz dil eklenmedi",
      "emptySubtitle": "Başlamak için ilk dilinizi ekleyin",
      "addFirst": "İlk Dilinizi Ekleyin"
    },
    "projects": {
      "title": "Projeler",
      "subtitle": "En iyi çalışmanızı sergileyin",
      "add": "Proje Ekle",
      "addNew": "Yeni Proje Ekle",
      "edit": "Projeyi Düzenle",
      "projectTitle": "Proje Başlığı",
      "titlePlaceholder": "ör. Çevrimiçi Mağaza Platformu",
      "technologies": "Kullanılan Teknolojiler",
      "technologiesPlaceholder": "ör. Web Geliştirme, Veritabanı Yönetimi",
      "description": "Tanım",
      "descriptionPlaceholder": "Projenizi, amacını ve rolünüzü açıklayın...",
      "links": "Bağlantılar",
      "linkName": "Bağlantı Adı",
      "linkNamePlaceholder": "ör. Web sitesi",
      "linkUrl": "Bağlantı URL'si",
      "linkUrlPlaceholder": "https://...",
      "link": "Proje Bağlantısı",
      "linkPlaceholder": "https://...",
      "save": "Kaydetmek",
      "cancel": "İptal etmek",
      "delete": "Silmek",
      "noEntries": "Henüz proje eklenmedi",
      "empty": "Henüz proje eklenmedi",
      "emptySubtitle": "Başlamak için ilk projenizi ekleyin",
      "addFirst": "İlk Projenizi Ekleyin"
    },
    "training": {
      "title": "Eğitim ve Kurslar",
      "subtitle": "Eğitim kurslarınızı ve sertifikalarınızı ekleyin",
      "add": "Eğitim Ekle",
      "addNew": "Yeni Eğitim Kursu Ekle",
      "edit": "Eğitim Kursunu Düzenle",
      "courseTitle": "Ders Adı",
      "titlePlaceholder": "ör. Gelişmiş Web Geliştirme",
      "organization": "Eğitim Organizasyonu",
      "organizationPlaceholder": "ör. Udemy, Coursera",
      "date": "Tamamlanma Tarihi",
      "datePlaceholder": "Tamamlanma tarihini seçin",
      "certificateLink": "Sertifika Bağlantısı",
      "linkPlaceholder": "https://...",
      "save": "Kaydetmek",
      "cancel": "İptal etmek",
      "delete": "Silmek",
      "noEntries": "Henüz eğitim eklenmedi",
      "empty": "Henüz eğitim eklenmedi",
      "emptySubtitle": "Başlamak için ilk eğitim kursunuzu ekleyin",
      "addFirst": "İlk Eğitim Kursunuzu Ekleyin"
    },
    "publications": {
      "title": "Yayınlar",
      "subtitle": "Yayınlanan çalışmalarınızı listeleyin",
      "add": "Yayın Ekle",
      "addNew": "Yeni Yayın Ekle",
      "edit": "Yayını Düzenle",
      "title_field": "Yayın Başlığı",
      "titlePlaceholder": "ör. Yapay Zeka Üzerine Araştırma Makalesi",
      "publisher": "Yayıncı",
      "publisherPlaceholder": "örneğin IEEE",
      "publicationDate": "Yayın Tarihi",
      "publicationDatePlaceholder": "Yayınlanma tarihini seçin",
      "link": "Bağlantı",
      "linkPlaceholder": "https://...",
      "save": "Kaydetmek",
      "cancel": "İptal etmek",
      "delete": "Silmek",
      "noEntries": "Henüz yayın eklenmedi",
      "empty": "Henüz yayın eklenmedi",
      "emptySubtitle": "Başlamak için ilk yayınınızı ekleyin",
      "addFirst": "İlk Yayınınızı Ekleyin"
    },
    "licenses": {
      "title": "Lisanslar ve Sertifikalar",
      "subtitle": "Profesyonel sertifikalarınızı ekleyin",
      "add": "Lisans Ekle",
      "addNew": "Yeni Lisans Ekle",
      "edit": "Lisansı Düzenle",
      "form": {
        "title": "Lisans/Sertifika Başlığı",
        "titlePlaceholder": "ör. AWS Sertifikalı Çözüm Mimarı",
        "name": "Lisans/Sertifika Adı",
        "namePlaceholder": "ör. AWS Sertifikalı Çözüm Mimarı",
        "issuer": "Düzenleyen Kuruluş",
        "issuerPlaceholder": "ör. Amazon Web Hizmetleri",
        "issueDate": "Düzenleme tarihi",
        "issueDatePlaceholder": "Veriliş tarihini seçin",
        "expiryDate": "Son kullanma tarihi",
        "expiryDatePlaceholder": "Son kullanma tarihini seçin",
        "credentialId": "Kimlik bilgisi kimliği",
        "credentialIdPlaceholder": "ör. ABC123XYZ",
        "credentialUrl": "Kimlik bilgisi URL'si",
        "credentialUrlPlaceholder": "https://..."
      },
      "save": "Kaydetmek",
      "cancel": "İptal etmek",
      "delete": "Silmek",
      "noEntries": "Henüz lisans eklenmedi",
      "empty": "Henüz lisans eklenmedi",
      "emptySubtitle": "Başlamak için ilk lisansınızı veya sertifikanızı ekleyin",
      "addFirst": "İlk Lisansınızı Ekleyin"
    },
    "awards": {
      "title": "Ödüller ve Başarılar",
      "subtitle": "Başarılarınızı vurgulayın",
      "add": "Ödül Ekle",
      "addNew": "Yeni Ödül Ekle",
      "edit": "Ödülü Düzenle",
      "form": {
        "title": "Ödül Başlığı",
        "titlePlaceholder": "örneğin Yılın Çalışanı",
        "organization": "Düzenleyen Kuruluş",
        "organizationPlaceholder": "ör. ABC Şirketi",
        "dateReceived": "Alındığı Tarih",
        "dateReceivedPlaceholder": "Alındığı tarihi seçin",
        "link": "Ödül URL'si",
        "linkPlaceholder": "https://...",
        "description": "Tanım",
        "descriptionPlaceholder": "Başarıyı anlatın..."
      },
      "save": "Kaydetmek",
      "cancel": "İptal etmek",
      "delete": "Silmek",
      "noEntries": "Henüz ödül eklenmedi",
      "empty": "Henüz ödül eklenmedi",
      "emptySubtitle": "Başlamak için ilk ödülünüzü ekleyin",
      "addFirst": "İlk Ödülünüzü Ekleyin"
    },
    "volunteer": {
      "title": "Gönüllü Deneyimi",
      "subtitle": "Gönüllü çalışmanızı paylaşın",
      "add": "Gönüllü Deneyimi Ekle",
      "addNew": "Yeni Gönüllü Deneyimi Ekle",
      "edit": "Gönüllü Deneyimini Düzenle",
      "organization": "Organizasyon",
      "organizationPlaceholder": "örneğin Kızılhaç",
      "role": "Rol",
      "rolePlaceholder": "örneğin Topluluk Organizatörü",
      "link": "Organizasyon Bağlantısı",
      "linkPlaceholder": "https://...",
      "startDate": "Başlangıç ​​Tarihi",
      "startDatePlaceholder": "Başlangıç ​​tarihini seçin",
      "endDate": "Bitiş Tarihi",
      "endDatePlaceholder": "Bitiş tarihini seçin",
      "description": "Tanım",
      "descriptionPlaceholder": "Gönüllü çalışmanızı anlatın...",
      "save": "Kaydetmek",
      "cancel": "İptal etmek",
      "delete": "Silmek",
      "noEntries": "Henüz gönüllü deneyimi eklenmedi",
      "empty": "Henüz gönüllü deneyimi eklenmedi",
      "emptySubtitle": "Başlamak için ilk gönüllü deneyiminizi ekleyin",
      "addFirst": "İlk Gönüllü Deneyiminizi Ekleyin"
    },
    "recommendations": {
      "title": "Öneriler",
      "subtitle": "Profesyonel öneriler ekleyin",
      "add": "Öneri Ekle",
      "addNew": "Yeni Öneri Ekle",
      "edit": "Öneriyi Düzenle",
      "name": "Tavsiye Eden Adı",
      "namePlaceholder": "örneğin John Smith",
      "position": "Konum",
      "positionPlaceholder": "örneğin Kıdemli Yönetici",
      "company": "Şirket",
      "companyPlaceholder": "ör. ABC Corp",
      "relationship": "İlişki",
      "relationshipPlaceholder": "ör. Doğrudan Yönetici",
      "text": "Tavsiye Metni",
      "textPlaceholder": "Tavsiyeyi girin...",
      "save": "Kaydetmek",
      "cancel": "İptal etmek",
      "delete": "Silmek",
      "noEntries": "Henüz öneri eklenmedi",
      "empty": "Henüz öneri eklenmedi",
      "emptySubtitle": "Başlamak için ilk önerinizi ekleyin",
      "addFirst": "İlk Tavsiyenizi Ekleyin"
    },
    "interests": {
      "title": "İlgi alanları",
      "subtitle": "Hobilerinizi ve ilgi alanlarınızı paylaşın",
      "add": "İlgi Alanı Ekle",
      "addNew": "Yeni İlgi Alanı Ekle",
      "edit": "İlgi Alanını Düzenle",
      "name": "Faiz",
      "interestName": "İlgi/Hobi",
      "namePlaceholder": "ör. Fotoğrafçılık, Okuma, Doğa Yürüyüşü",
      "save": "Kaydetmek",
      "cancel": "İptal etmek",
      "delete": "Silmek",
      "noEntries": "Henüz ilgi alanı eklenmedi",
      "empty": "Henüz ilgi alanı eklenmedi",
      "emptySubtitle": "Başlamak için ilk ilgi alanınızı ekleyin",
      "addFirst": "İlk İlgi Alanınızı Ekleyin"
    },
    "formActions": {
      "save": "Kaydetmek",
      "cancel": "İptal etmek",
      "edit": "Düzenlemek",
      "delete": "Silmek",
      "add": "Eklemek",
      "update": "Güncelleme",
      "close": "Kapalı",
      "submit": "Göndermek"
    },
    "confirm": {
      "delete": "Bu girişi silmek istediğinizden emin misiniz?",
      "deleteEducation": "Bu eğitim girişini silmek istediğinizden emin misiniz?",
      "deleteExperience": "Bu deneyim girişini silmek istediğinizden emin misiniz?",
      "deleteSkill": "Bu beceriyi silmek istediğinizden emin misiniz?"
    },
    "usage": {
      "resumes": "Özgeçmişler",
      "aiGenerations": "Yapay Zeka Geliştirmeleri",
      "coverLetters": "Kapak Mektupları",
      "chatMessages": "Sohbet Mesajları (Bugün)",
      "noDataAvailable": "Kullanım verileri mevcut değil",
      "loading": "Planlar yükleniyor..."
    },
    "chatbot": {
      "title": "Yapay Zeka Kariyer Asistanı",
      "subtitle": "Bana kariyerin hakkında her şeyi sor",
      "welcomeTitle": "Merhaba! Bugün sana nasıl yardımcı olabilirim?",
      "welcomeMessage": "Özgeçmişiniz, ön yazılarınız, iş arama ve kariyer tavsiyeleriniz konusunda size yardımcı olmak için buradayım. Bana bir şey sor!",
      "newChat": "Yeni Sohbet",
      "sessions": "Sohbet Geçmişi",
      "session": "Sohbet",
      "messages": "Mesajlar",
      "noSessions": "Henüz sohbet geçmişi yok",
      "inputPlaceholder": "Mesajınızı yazın...",
      "inputHint": "Göndermek için Enter'a, yeni satır için Shift + Enter'a basın",
      "suggestion1": "Özgeçmişimi nasıl geliştirebilirim?",
      "suggestion2": "Kapak mektubu yazmama yardım et",
      "suggestion3": "Hangi becerileri eklemeliyim?",
      "suggestion4": "İş görüşmeleri için ipuçları",
      "errorLoadingSessions": "Sohbet geçmişi yüklenemedi",
      "errorLoadingHistory": "Konuşma yüklenemedi",
      "errorCreatingSession": "Yeni sohbet oluşturulamadı",
      "errorDeletingSession": "Sohbet silinemedi",
      "errorSendingMessage": "Mesaj gönderilemedi",
      "newSessionCreated": "Yeni sohbet başarıyla oluşturuldu",
      "sessionDeleted": "Sohbet başarıyla silindi",
      "featureResume": "İncelemeyi Devam Ettir",
      "featureInterview": "Mülakat Hazırlığı",
      "featureCoverLetter": "Ön yazı",
      "featureCareer": "Kariyer Tavsiyesi",
      "tryAsking": "Sormayı dene",
      "prompt1": "Özgeçmişimi incele",
      "prompt2": "Röportaj için hazırlanmama yardım et",
      "prompt3": "Benim için bir kapak mektubu yaz",
      "prompt4": "Hangi becerileri öğrenmeliyim?"
    }
  }
} },
    fallbackLng: 'en',
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage']
    },

    interpolation: {
      escapeValue: false
    }
  });

export default i18n; 
